/*
Description: This is the file that will handle interacting with the Raintree EMR
Date: 5/30/2024
Revamped: 2026-05 — Full Flex architecture, no hardcoded widget names
Author: Jacob Michalski
*/

import {
  normalizeKey,
  panelCaptionName,
  tabDataNameToSectionName
} from './RaintreeHelpers.ts';
import {
  captureRaintreeNote,
  collectVisibleExamSpecs
} from './RaintreeCapture.ts';

export { getSchedule } from './RaintreeSchedule.ts';
export {
  getNoteKeyForCapture,
  noteMergeDepthForCapture,
  noteFinalizationSelectorForCapture
} from './RaintreeCapture.ts';

export function getPageInfo() {
  const labelElement = Array.from(
    document.querySelectorAll('label[for*="notetype"]')
  ).find(
    (el) => el.textContent?.trim() && el.textContent.trim() !== 'Note Type'
  );
  const noteType = labelElement?.textContent?.trim() || 'Unknown';
  return { URL: window.location.origin, 'Note Type': noteType };
}

export function getPatientInfo() {
  const patientInfoPanel = document.querySelector(
    '[data-widgetname="patinfo"]'
  );
  if (!patientInfoPanel) {
    console.error('Patient info panel not found');
    return null;
  }
  const nameLabel = Array.from(
    patientInfoPanel.querySelectorAll('label[for=""]')
  ).find((l) => l.textContent === 'Patient');
  const dobLabel = Array.from(
    patientInfoPanel.querySelectorAll('label[for=""]')
  ).find((l) => l.textContent === 'DOB');

  if (!nameLabel || !dobLabel) {
    console.error('Patient or DOB label not found');
    return null;
  }

  const patientNameEl = nameLabel
    .closest('.widget')
    ?.nextElementSibling?.querySelector('label');
  const patientDOBEl = dobLabel
    .closest('.widget')
    ?.nextElementSibling?.querySelector('label');

  if (!patientNameEl || !patientDOBEl) {
    console.error('Patient name or DOB element not found');
    return null;
  }

  const fullName = patientNameEl.innerHTML.trim();
  const [firstName, lastName] = fullName.split(' ');
  const emrID = `${lastName},${firstName}`.replace(/\s/g, '');

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: patientDOBEl.innerHTML.trim()
  };
}

// Note capture polls unattended: stay quiet (getPatientInfo logs an error when
// the panel is missing) and never throw into the poll.
export function getPatientInfoForCapture() {
  if (!document.querySelector('[data-widgetname="patinfo"]')) return null;
  try {
    return getPatientInfo();
  } catch (_) {
    return null;
  }
}

export function getPageInfoForCapture() {
  return getPageInfo();
}

// Capture reads the note through this integration's own label, panel-mapping and
// exam-spec logic, so it records the same paths this scan gives Comprehend.
const captureAdapter = {
  resolveLabel,
  hasRomInputs,
  buildRadioScaleField,
  buildPickerField,
  buildCheckboxListboxField,
  // Capture includes history and targets even when Comprehend may not write them.
  examFieldSpecs: (maintab: Element) =>
    collectVisibleExamSpecs(maintab, {
      panel: (panel, mainTab, subTab, result, caption) =>
        scanObjExamPanelFields(panel, mainTab, subTab, result, caption, true),
      flat: scanObjExamFlatFields,
      listboxes: scanObjExamTabListboxes
    }),
  examNestPath: objExamNestPath,
  narrativeKey: (caption: string, sectionKey: string) => {
    if (sectionKey === 'Subjective' && _matchesAnyPanelName(caption, SUBJECTIVE_PANEL_NAMES)) {
      return 'Subjective';
    }
    if (sectionKey === 'Assessment' && _matchesAnyPanelName(caption, ASSESSMENT_PANEL_NAMES)) {
      return 'Assessment';
    }
    return null;
  },
  catchAllKey: _catchAllKeyFor
};

export function getQuestionsForCapture() {
  return captureRaintreeNote(captureAdapter);
}

// The two integrations name some paths differently; a note captured under one
// is replaced, not mixed, if the user's integration flag flips mid-note.
export function getQuestionShapeForCapture() {
  return 'new-integration';
}

var questions = [];
var questionInteractions = {};
let getQuestionsInProgress = false;
let answeringInProgress = false;
let __answeringRefCount = 0;

// Cache the computed questions to avoid the expensive multi-tab rescan for rapid, back-to-back
// calls. Mirrors the WebPT2 injector: results are reused for GET_QUESTIONS_TTL_MS, and the TTL is
// refreshed while answering (see removeQuestionFromQueue) so we never rescan — and clobber
// in-progress fills — mid-answer.
let cachedQuestions: any[] | null = null;
let lastGetQuestionsAt = 0;
let cachedExamOpen = false; // was an exam form open when cachedQuestions was taken?
const GET_QUESTIONS_TTL_MS = 30000;

// Note type for the current scan. Used ONLY to decide which objective-exam measurement
// column is writable (see objExamWritableColumn) — panel naming is note-type independent.
type NoteTypeKind =
  | 'initialEval'
  | 'progressNote'
  | 'dailyNote'
  | 'discharge'
  | 'unknown';
let _noteTypeKind: NoteTypeKind = 'unknown';

/** Read the note type off the Raintree header. Anything unrecognised stays 'unknown', which
 *  is the safe outcome: no measurement column gets written. */
function detectNoteTypeKind(): NoteTypeKind {
  const raw =
    (
      Array.from(document.querySelectorAll('label[for*="notetype"]')).find(
        (el) => el.textContent?.trim() && el.textContent.trim() !== 'Note Type'
      ) as HTMLElement | null
    )?.textContent?.trim() ?? '';
  if (/initial\s*eval/i.test(raw)) return 'initialEval';
  // Re-Eval is grouped with Progress Note: both re-measure and write the Current column.
  if (/progress|re.?eval/i.test(raw)) return 'progressNote';
  if (/discharge/i.test(raw)) return 'discharge';
  if (/daily/i.test(raw)) return 'dailyNote';
  return 'unknown';
}

/** Panel captions that hold the free-text Subjective / Assessment content. Whichever of
 *  these a note happens to contain gets registered under the key "Subjective"/"Assessment"
 *  so the AI schema and the sidebar show a consistent name.
 *
 *  Deliberately NOT keyed by note type: Raintree templates vary per clinic and new note
 *  types keep appearing, so gating on "is this an Initial Eval" just means an unrecognised
 *  note type silently gets no mapping at all. Matching on the caption alone works for any
 *  note type that reuses one of these panels.
 *
 *  panelCaptionName() already strips trailing "(S)"/"(O)" markers, so these are the
 *  post-strip names (e.g. "Subjective Comments (S)" → "Subjective Comments").
 *
 *  Comparison goes through _normPanelName(), which strips a trailing instance number — so a
 *  single entry covers Raintree's numbered duplicates ("Othersub" matches Othersub1,
 *  Othersub2, …; "Clinical" matches Clinical1, Clinical2, …). No need to list each number.
 *
 *  Order matters only when a single note contains more than one of these panels — the
 *  first one scanned claims the key and any later match keeps its own caption (see
 *  buildPanelStructure), so nothing is overwritten. */
const SUBJECTIVE_PANEL_NAMES = [
  'Mechanism of Onset',
  'Subjective Comments',
  'Other Subjective Comments',
  // Raintree numbers duplicated panels — Othersub1, Othersub2, … all match this entry
  // because _normPanelName() strips the trailing instance number.
  'Othersub'
];

const ASSESSMENT_PANEL_NAMES = [
  'Clinical Impression',
  'Daily Assessment Comments',
  // Matches Clinical1, Clinical2, … (see note above).
  'Clinical'
];

/** Normalize a panel caption for comparison:
 *    lowercase → drop spaces/punctuation → strip a trailing instance number → strip one
 *    trailing "s".
 *  The instance number is what lets a single list entry cover Raintree's numbered panel
 *  duplicates ("Othersub1"/"Othersub2" → "othersub", "Clinical1"/"Clinical2" → "clinical").
 *  Dropping punctuation/spaces means "Other Sub" and "Othersub" are the same name, and the
 *  trailing-"s" strip keeps "Clinical Impressions" == "Clinical Impression".
 *  Matching stays whole-name (not prefix), so "Clinical Notes" does NOT match "Clinical". */
function _normPanelName(s: string): string {
  return s
    .trim()
    .toLowerCase()
    // Drop a trailing "(s)"/"(o)" SOAP marker FIRST. panelCaptionName() normally strips these
    // already, but if one survives, removing punctuation below would turn "(S)" into a bare
    // "s" and the trailing-"s" strip would then eat a real character instead.
    .replace(/\((?:s|o)\)\s*$/, '')
    .replace(/[^a-z0-9]/g, '')
    .replace(/\d+$/, '')
    .replace(/s$/, '');
}

function _panelNameMatches(actual: string, target: string): boolean {
  return _normPanelName(actual) === _normPanelName(target);
}

/** True when `name` is one of the listed Subjective/Assessment panel captions. */
function _matchesAnyPanelName(name: string, targets: string[]): boolean {
  return targets.some((t) => _panelNameMatches(name, t));
}

/** Inline an allowed-value list into a field's `_description` instead of sending it as a
 *  `_choices` enum. Used for the activity/modality pick-lists: the time-based exercise list
 *  is far past the 499-item enum cap, and keeping both tables on the same mechanism avoids
 *  one behaving differently from the other. Values are pipe-separated because many entries
 *  contain commas, slashes and parentheses. */
function _describeChoices(instruction: string, choices: string[]): string {
  // Equal-weighting clause: without it the model gravitates toward whichever option is the
  // more familiar/textbook term and overlooks an entry that literally names what was done
  // (e.g. picking a generic lifting entry when "Suitcase Carry" is right there in the list).
  const equalWeighting =
    'Treat every allowed value below as equally likely — none is more "standard", more common, ' +
    'or more clinically typical than any other, and position in the list carries no meaning. ' +
    'Read the whole list before deciding. If any value literally names what was described in ' +
    'the transcript, choose that one, even if a different value is the more familiar textbook ' +
    'term for the same movement (e.g. if the clinician described a suitcase carry, choose ' +
    '"Suitcase Carry" — do NOT substitute a general lifting or carrying entry).';
  return `${instruction} ${equalWeighting}\n\nAllowed values (${choices.length}), separated by " | ":\n${choices.join(' | ')}`;
}

// Structure keys (`${topLevelKey}::${structureKey}`) that a mapped SOAP panel has claimed —
// e.g. the "Mechanism of Onset" panel claiming "Subjective::Subjective". Later scan phases
// (the tab-level virtual-panel scan and buildFlatHtsStructure) also want to write a
// subsection named after the tab, which would merge their loose widgets straight into the
// mapped panel and bury/overwrite it. Reserving the key lets those phases redirect instead.
const _mappedSoapStructureKeys = new Set<string>();

/** Sibling subsection that unpaneled tab-level widgets fall back to when the tab-named key
 *  is already owned by a mapped SOAP panel. Kept as a sibling of (never nested inside) the
 *  mapped panel so the AI schema doesn't get a giant blob under "Subjective". */
function _catchAllKeyFor(topLevelKey: string): string {
  return `Additional ${topLevelKey} Fields`;
}

/** True when `label` is already registered under some other subsection of this question —
 *  used to keep the tab-level catch-all from re-listing fields a named panel already owns. */
function _isRegisteredElsewhere(
  structure: Record<string, any>,
  ownKey: string,
  label: string
): boolean {
  return Object.entries(structure).some(
    ([k, v]) =>
      k !== ownKey && v && typeof v === 'object' && !Array.isArray(v) && label in v
  );
}

// Background watcher: dismisses any "Abandon Changes?" dialog that the Raintree server
// sends asynchronously (via WebSocket) after a scan completes — those arrive after
// dismissAbandonChanges() has already returned, so they need a separate polling loop.
let _abandonWatchTimer: ReturnType<typeof setTimeout> | null = null;
function startAbandonWatcher(durationMs = 6000): void {
  if (_abandonWatchTimer !== null) return; // already running
  const deadline = Date.now() + durationMs;
  const tick = async () => {
    _abandonWatchTimer = null;
    await dismissAbandonChanges(0);
    if (Date.now() < deadline) {
      _abandonWatchTimer = setTimeout(tick, 300);
    }
  };
  _abandonWatchTimer = setTimeout(tick, 300);
}

// Background watcher for advisory "Notice" popups (single OK button) that Raintree can
// raise at any point while we're filling — typically on a table row edit. They block input
// until acknowledged, so the rest of the fill would go nowhere. Unlike the abandon watcher
// this runs for the whole answering pass rather than a fixed window, since we can't predict
// which row triggers one.
let _noticeWatchTimer: ReturnType<typeof setTimeout> | null = null;
let _noticeWatchActive = false;
function startNoticeWatcher(): void {
  if (_noticeWatchActive) return; // already running
  _noticeWatchActive = true;
  // Deliberately logged: without it, "no notice warnings in the console" is ambiguous
  // between the watcher never starting and it running but never matching the dialog.
  console.log('[Raintree] notice watcher: started');
  const tick = async () => {
    _noticeWatchTimer = null;
    if (!_noticeWatchActive) return;
    try {
      await dismissNoticeDialog(0);
    } catch (e) {
      console.warn('[Raintree] notice watcher:', e);
    }
    if (_noticeWatchActive) _noticeWatchTimer = setTimeout(tick, 250);
  };
  _noticeWatchTimer = setTimeout(tick, 250);
}
function stopNoticeWatcher(): void {
  _noticeWatchActive = false;
  if (_noticeWatchTimer !== null) {
    clearTimeout(_noticeWatchTimer);
    _noticeWatchTimer = null;
  }
}

// Picker batch: Input() just queues targets; answerFlexQuestion flushes them all at once
const _pickerBatch = new Map<string, any[]>();
const _pickerBatchFlushers = new Map<string, () => Promise<void>>();

// Memo append deduplication: keyed by element ID, cleared at the start of each fill.
// Prevents the same text being appended multiple times when the same DOM element is
// registered by multiple phases (Phase 1 panel + Phase 4 flat) and the answer hits both.
const _memoAppendBase = new Map<string, string>(); // elementId → baseline before this fill
const _memoAppendedVals = new Map<string, string[]>(); // elementId → vals already appended
// Objective Exam (objview) field specs: scanned during GET_DOM, used during fill.
// Key: rowName (e.g. "Shoulder Exam") → Map of flatLabel → ObjExamFieldSpec
interface ObjExamFieldSpec {
  nameAttr: string; // input[name] or element id
  type: 'input' | 'select' | 'memo' | 'listbox' | 'checkbox';
  choices?: string[];
  mainTabName: string;
  subTabName: string | null;
  panelCaption: string; // panel header text within the sub-tab
  movement: string | null; // movement name (e.g. "Flexion"), null for noright/panel-level fields
  column: string; // "Initial", "Goal", "Comments", "noright", or "listbox"
  listboxHeaders?: string[]; // column header names for listbox type
  skipPanelInPath?: boolean; // flat fields go directly under mainTabName (no panelCaption level)
}
const _objExamFieldSpecs = new Map<string, Map<string, ObjExamFieldSpec>>();
// Cache: rowName → { examKey, templateRow } so subsequent section calls reuse without re-opening
const _objExamTemplateCache = new Map<
  string,
  { examKey: string; templateRow: Record<string, any> }
>();

// Sub-tab registry: populated during GET_DOM Phase 2; used during fill to navigate nested tabs
// Key: "${topLevelKey}::${sectionKey}" → { widgetName, tabName }
const _subTabRegistry = new Map<
  string,
  { widgetName: string; tabName: string }
>();

// ─── Main Entry Point ─────────────────────────────────────────────────────────

export async function getQuestions() {
  // Serve cached results for rapid, back-to-back calls so we skip the expensive multi-tab rescan.
  const now = Date.now();
  // An open exam form is a different document than the note: a scan taken
  // with it open lists only the exam, one taken without it lists the SOAP
  // sections. Never serve one as the other — opening an exam right after a
  // note scan used to hand Comprehend the note's sections, so nothing in the
  // exam was ever filled.
  const examOpenNow = !!document.querySelector(
    '.screen.screen-active [data-widgetname="a_maintab"]'
  );
  if (
    cachedQuestions &&
    cachedExamOpen === examOpenNow &&
    now - lastGetQuestionsAt < GET_QUESTIONS_TTL_MS
  ) {
    return cachedQuestions;
  }

  // Don't re-scan while answering — tab switches would wipe fills in progress. Serve the last good
  // scan regardless of the TTL window; the TTL is refreshed as each answer finishes
  // (removeQuestionFromQueue), so we keep serving cache right after answering too.
  if (answeringInProgress || __overlayAnswerQueue.length > 0) {
    return cachedQuestions ?? questions;
  }

  // Prevent concurrent scans — multiple GET_DOM messages would produce partial/mismatched question arrays
  if (getQuestionsInProgress) {
    while (getQuestionsInProgress) {
      await new Promise((r) => setTimeout(r, 100));
    }
    return cachedQuestions ?? questions;
  }
  getQuestionsInProgress = true;
  try {
    questions = [];
    questionInteractions = {};
    _subTabRegistry.clear();
    _objExamFieldSpecs.clear();
    _objExamTemplateCache.clear();
    _mappedSoapStructureKeys.clear();
    // Refresh for this scan — gates which objective-exam column is writable.
    _noteTypeKind = detectNoteTypeKind();
    beginOverlay('Reading your note… Please wait.', { title: 'ComprehendPT' });

    const serviceTicketTab = document.querySelector(
      '[data-widgetname="servicetickettab"]'
    );
    if (!serviceTicketTab) {
      endOverlay();
      return [];
    }

    const tabContent = serviceTicketTab.querySelector('#tabcontent');
    if (!tabContent) {
      endOverlay();
      return [];
    }

    // Collect tab data-names upfront (strings, not element refs).
    // Element refs become stale after exam form navigation remounts the DOM,
    // so we re-query the live tabEl and tabContent inside each loop iteration.
    const ALLOWED_BASES = new Set([
      'subjective',
      'objective',
      'assess',
      'plan',
      'activities',
      'goals'
    ]);

    // Scan Objective last — opening/closing exam forms can remount DOM nodes,
    // which would corrupt tabContent refs for sections scanned afterward.
    const allTabNames = Array.from(
      serviceTicketTab.querySelectorAll('.tab-item[data-name]')
    )
      .map((t) => (t as HTMLElement).dataset.name || '')
      .filter((n) => ALLOWED_BASES.has(n.replace(/\d+$/, '')))
      .sort((a, b) => {
        const aObj = a.replace(/\d+$/, '') === 'objective' ? 1 : 0;
        const bObj = b.replace(/\d+$/, '') === 'objective' ? 1 : 0;
        return aObj - bObj;
      });

    // Remember original tab name to restore after scanning
    const originalTabName =
      (serviceTicketTab.querySelector('.tab-item.active') as HTMLElement | null)
        ?.dataset.name || '';

    // If an Objective Exam form is already open, scan only that form and skip SOAP tabs.
    // The exam form is a fullscreen overlay — navigating SOAP tabs would close it.
    const _openExamScreen = Array.from(document.querySelectorAll('.screen.screen-active'))
      .find((s) => s.querySelector('[data-widgetname="a_maintab"]')) ?? null;
    const _openMaintabEl = (_openExamScreen?.querySelector('[data-widgetname="a_maintab"]')) as Element | null;

    if (_openMaintabEl && _openExamScreen) {
      const examTitle =
        (_openExamScreen.querySelector('.screen-title') as HTMLElement)?.innerText?.trim() ||
        (_openExamScreen.querySelector('.screen-header h1, .screen-header h2, .screen-header .title') as HTMLElement)?.innerText?.trim() ||
        (_openExamScreen.querySelector('[data-widgetname="a_title"], [data-widgetname="a_header"]') as HTMLElement)?.innerText?.trim() ||
        (_openExamScreen as HTMLElement).getAttribute('aria-label')?.trim() ||
        'Objective Exam';

      const openFieldSpecMap = await scanObjExamFormFields(_openMaintabEl);
      _objExamFieldSpecs.set(examTitle, openFieldSpecMap);
      const openTemplateRow = buildObjExamTemplateRow(openFieldSpecMap);
      const capturedExamTitle = examTitle;
      const capturedOpenSpecMap = openFieldSpecMap;

      if (Object.keys(openTemplateRow).length > 0) {
        questionInteractions[capturedExamTitle] = {
          Type: 'ObjExam',
          Key: capturedExamTitle,
          Question: capturedExamTitle,
          Structure: openTemplateRow,
          Input: async (rowObj: Record<string, any>) => {
            const flatObj: Record<string, string> = {};
            const listboxFills: Array<{ spec: ObjExamFieldSpec; rowData: Record<string, string> }> = [];
            for (const [flatLabel, spec] of Array.from(capturedOpenSpecMap.entries())) {
              const path = objExamNestPath(spec);
              const val = getNestedValue(rowObj, path);
              if (val == null || val === '') continue;
              if (spec.type === 'listbox') {
                if (typeof val === 'object' && !Array.isArray(val)) {
                  const r = val as Record<string, string>;
                  if (Object.values(r).some((v) => v != null && v !== '')) listboxFills.push({ spec, rowData: r });
                } else if (Array.isArray(val)) {
                  for (const r of val) {
                    if (typeof r === 'object' && r !== null) {
                      const row = r as Record<string, string>;
                      if (Object.values(row).some((v) => v != null && v !== '')) listboxFills.push({ spec, rowData: row });
                    }
                  }
                }
              } else {
                flatObj[flatLabel] = String(val);
              }
            }
            await fillObjExamRow('', capturedExamTitle, flatObj, listboxFills, true);
          }
        };
      }

      endOverlay();

      // Serialize and return only the exam question
      const stripForFlexPP = (obj: any): void => {
        if (Array.isArray(obj)) { obj.forEach(stripForFlexPP); }
        else if (obj !== null && typeof obj === 'object') {
          delete obj['_existingValue'];
          Object.values(obj).forEach(stripForFlexPP);
        }
      };
      const examOut: any = {
        Key: capturedExamTitle,
        Question: capturedExamTitle,
        Type: 'Flex',
        Structure: { ...openTemplateRow, 'flex++': true, 'prune_early': true }
      };
      stripForFlexPP(examOut.Structure);
      getQuestionsInProgress = false;
      return Object.keys(openTemplateRow).length > 0 ? [examOut] : [];
    }

    for (const tabName of allTabNames) {
      const tabBase = tabName.replace(/\d+$/, '');
      if (!ALLOWED_BASES.has(tabBase)) continue;
      const topLevelKey = tabDataNameToSectionName(tabName);

      // Re-query live element refs each iteration — exam form navigation may remount the DOM.
      const liveTabEl = serviceTicketTab.querySelector(
        `.tab-item[data-name="${tabName}"]`
      ) as HTMLElement | null;
      if (!liveTabEl) continue;

      if (!liveTabEl.classList.contains('active')) {
        liveTabEl.click();
        await new Promise((r) => setTimeout(r, 600));
        await dismissAbandonChanges();
        // After dismissing any dialog, wait until this tab is actually active
        // (Raintree may need extra time to complete navigation after Save)
        for (let w = 0; w < 20; w++) {
          const freshCheck = serviceTicketTab.querySelector(
            `.tab-item[data-name="${tabName}"]`
          ) as HTMLElement | null;
          if (freshCheck?.classList.contains('active')) break;
          await new Promise((r) => setTimeout(r, 150));
        }
      }

      // Re-query liveTabEl after any navigation/remount that may have occurred
      const activeTabEl = serviceTicketTab.querySelector(
        `.tab-item[data-name="${tabName}"]`
      ) as HTMLElement | null;
      if (!activeTabEl) continue;

      // Derive tabContent from the SOAP tab control that owns this tab element.
      // serviceTicketTab.querySelector('#tabcontent') finds the INNER #tabcontent
      // (inside the Objective Exams sub-tab widget), not the outer SOAP section content.
      // Going up from activeTabEl to the enclosing .w-tab-control and taking its direct
      // .w-tab-control-content child gives us the correct outer content area.
      const soapTabCtrl = activeTabEl.closest(
        '.w-tab-control'
      ) as Element | null;
      if (!soapTabCtrl) continue;
      const tabContent = Array.from(soapTabCtrl.children).find((el) =>
        el.classList.contains('w-tab-control-content')
      ) as Element | null;
      if (!tabContent) continue;

      questionInteractions[topLevelKey] = {
        Type: 'Flex',
        Key: topLevelKey,
        Question: topLevelKey,
        tabName,
        Structure: {}
      };

      await buildAllPanels(tabContent, topLevelKey);
    }

    // Restore original tab (re-query live element after all navigation).
    // Objective scanning leaves exam forms that trigger "Abandon Changes" on tab switch —
    // wait up to 1.5 s for the dialog to appear before giving up.
    if (originalTabName) {
      const restoreEl = serviceTicketTab.querySelector(
        `.tab-item[data-name="${originalTabName}"]`
      ) as HTMLElement | null;
      if (restoreEl && !restoreEl.classList.contains('active')) {
        restoreEl.click();
        await dismissAbandonChanges(1500);
      }
    }

    endOverlay();

    // Recursively remove _existingValue so the backend pruner treats every
    // branch as all-null and can prune aggressively.  _choices must be kept
    // intact so gen_flexpp_leaf_prop can build proper OpenAI enum constraints —
    // without them the AI returns "Not Discussed" for every undiscussed field.
    const stripForFlexPP = (obj: any): void => {
      if (Array.isArray(obj)) {
        obj.forEach(stripForFlexPP);
      } else if (obj !== null && typeof obj === 'object') {
        delete obj['_existingValue'];
        Object.values(obj).forEach(stripForFlexPP);
      }
    };

    // Emit only non-empty Flex questions
    Object.keys(questionInteractions).forEach((key) => {
      const q = questionInteractions[key];

      // Objective Exam rows — each is its own separate question
      if (q.Type === 'ObjExam' && q.Structure && Object.keys(q.Structure).length > 0) {
        // Skip raw data-index aliases (e.g. "0AOAZA8WW5X") — only emit the human-readable exam key
        if (/^[0-9A-Z]{8,}$/.test(key)) return;
        const examOutQuestion: any = {
          Key: key,
          Question: key,
          Type: 'Flex',
          Structure: {
            ...q.Structure,
            'flex++': true,
            'prune_early': true
          }
        };
        stripForFlexPP(examOutQuestion.Structure);
        questions.push(examOutQuestion);
        return;
      }

      if (q.Type !== 'Flex' || Object.keys(q.Structure || {}).length === 0)
        return;

      const outQuestion: any = {
        Key: key,
        Question: key,
        Type: 'Flex',
        Structure: {}
      };

      for (const [subsectionKey, fields] of Object.entries(
        q.Structure as Record<string, Record<string, any>>
      )) {
        const fieldEntries = Object.entries(fields);

        // Subsections with no registered fields (e.g. "Questionnaires", "Functional Status",
        // "Total Time (minutes)", "Home Exercise Program") are panels whose widgets we never
        // resolved to an Input. Emitting them invites the AI to answer a section that cannot
        // be written, producing "Input not found … Available: Array(0)" at fill time — so
        // drop them from the schema entirely.
        if (fieldEntries.length === 0) continue;

        // Single-field subsection where the field key matches the subsection key
        // (e.g. "Goals" panel containing only a "Goals" listbox, or "Patient Goals" panel
        // containing only a "Patient Goals" memo) — flatten one level so the AI sees:
        //   "Goals": [...]  instead of  "Goals": { "Goals": [...] }
        if (
          fieldEntries.length === 1 &&
          normalizeKey(fieldEntries[0][0]) === normalizeKey(subsectionKey)
        ) {
          const def = fieldEntries[0][1];
          if (Array.isArray(def.Structure)) {
            outQuestion.Structure[subsectionKey] =
              (def.Structure as any[]) ?? [];
          } else if (def.Structure && typeof def.Structure === 'object') {
            // Plain-object structure (e.g. Objective evaluation rows) — pass through directly
            outQuestion.Structure[subsectionKey] = def.Structure;
          } else {
            outQuestion.Structure[subsectionKey] = {
              ...(def['Existing Value'] != null && {
                _existingValue: def['Existing Value']
              }),
              ...(def.Choices && { _choices: def.Choices }),
              ...(def.Description && { _description: def.Description })
            };
          }
          continue;
        }

        // Skip subsections whose key duplicates the top-level question key,
        // BUT only when they contain no row-list fields (Structure arrays).
        // Pure Phase 4 flat-HTS catch-alls (e.g. "Subjective" inside Subjective,
        // "Objective" inside Objective) duplicate content already in named panels
        // and should be dropped. Row-list subsections (e.g. "Goals" inside Goals)
        // are the sole representation of that listbox and must be kept.
        // A mapped SOAP panel ("Mechanism of Onset" registered as "Subjective") is also
        // exempt — it legitimately shares the question key and is the ONLY copy of that
        // panel's memo, so dropping it would blank out Subjective/Assessment entirely.
        if (
          normalizeKey(subsectionKey) === normalizeKey(key) &&
          !_mappedSoapStructureKeys.has(`${key}::${subsectionKey}`) &&
          !fieldEntries.some(([, def]) => Array.isArray((def as any).Structure))
        )
          continue;

        outQuestion.Structure[subsectionKey] = {};
        for (const [fieldKey, def] of Object.entries(fields)) {
          // Skip Raintree data-index aliases (e.g. "0AOAZA8WW5X") — they're
          // registered in questionInteractions for the fill path but the AI only
          // needs the human-readable exam title keys ("Knee Evaluation" etc.).
          if (/^[0-9A-Z]{8,}$/.test(fieldKey)) continue;
          if (Array.isArray(def.Structure)) {
            outQuestion.Structure[subsectionKey][fieldKey] =
              (def.Structure as any[]) ?? [];
            continue;
          }
          if (def.Structure && typeof def.Structure === 'object') {
            // Plain-object structure (e.g. Objective evaluation rows) — pass through directly
            outQuestion.Structure[subsectionKey][fieldKey] = def.Structure;
            continue;
          }
          outQuestion.Structure[subsectionKey][fieldKey] = {
            ...(def['Existing Value'] != null && {
              _existingValue: def['Existing Value']
            }),
            ...(def.Choices && { _choices: def.Choices }),
            ...(def.Description && { _description: def.Description })
          };
        }
      }

      outQuestion.Structure['flex++'] = true;
      if (key === 'Objective') {
        outQuestion.Structure['prune_early'] = true;
        // Strip existing values for Objective so the pruner treats all branches as
        // all-null and can prune aggressively — otherwise filled ROM values protect
        // huge sub-trees from pruning and inflate chunk count to 60+.
        stripForFlexPP(outQuestion.Structure);
        // Mark always-present panels so the pruner never drops them regardless of transcript.
        const _noPruneSections = ['Observations', 'Objective Findings Comments'];
        for (const s of _noPruneSections) {
          if (outQuestion.Structure[s] && typeof outQuestion.Structure[s] === 'object') {
            outQuestion.Structure[s]['_no_prune'] = true;
          }
        }
      }
      questions.push(outQuestion);
    });

    // Sort into standard SOAP order regardless of scan order (Objective is scanned
    // last to avoid DOM corruption but should appear second in the returned array).
    const SOAP_ORDER: Record<string, number> = {
      Subjective: 0,
      Objective: 1,
      Assessment: 2,
      Plan: 3,
      Activities: 4,
      Goals: 5
    };
    questions.sort(
      (a, b) => (SOAP_ORDER[a.Key] ?? 99) - (SOAP_ORDER[b.Key] ?? 99)
    );

    cachedQuestions = questions;
    cachedExamOpen = !!document.querySelector('.screen.screen-active [data-widgetname="a_maintab"]');
    lastGetQuestionsAt = Date.now();
    // Raintree may send "Abandon Changes?" via WebSocket after we've already returned —
    // keep polling in the background for a few seconds to catch those late arrivals.
    startAbandonWatcher(6000);
    return questions;
  } catch (err) {
    endOverlay();
    console.error('Error in getQuestions:', err);
    return [];
  } finally {
    getQuestionsInProgress = false;
  }
}

// ─── Panel → Structure Builder ────────────────────────────────────────────────

/**
 * Process all panels in a tab container, re-querying after each one that triggers
 * a DOM remount (e.g. picker screen navigation remounts panel elements).
 */
async function buildAllPanels(
  tabContent: Element,
  key: string,
  skipPhase2 = false
) {
  // Release this section's mapped SOAP key before rebuilding. The registry exists to stop a
  // SECOND panel in the same pass from stealing a key the first one claimed — it must not
  // survive into the next pass. answerFlexQuestion() rebuilds the structure before filling,
  // and a stale claim there made buildPanelStructure skip the rename, so the panel came back
  // as "Othersub1" and the AI's "Subjective" answer failed with "Subsection not found".
  _mappedSoapStructureKeys.delete(`${key}::Subjective`);
  _mappedSoapStructureKeys.delete(`${key}::Assessment`);

  const processed = new Set<string>();

  // Pre-discover nested tab widgets so Phase 1 can skip their panels.
  // These are [data-widgetname] elements directly inside this tab content that contain
  // a .w-tab-control — Phase 2 handles them by activating each sub-tab in turn.
  // (The outer servicetickettab is NOT inside tabContent so it won't be matched here.)
  //
  // IMPORTANT: the SOAP .w-tab-control-content holds ALL section tab panes in the DOM
  // simultaneously — inactive sections are just CSS-hidden.  Without the visibility
  // guard, _nestedWidgetNames would include widgets from OTHER (hidden) sections, and
  // Phase 2 would write their sub-tab content under the wrong key.  Only widgets that
  // are currently rendered and visible belong to the active section.
  //
  // We use checkVisibility() (Chrome 105+, always available in a Chrome extension)
  // because it checks ALL hiding methods (display:none, visibility:hidden,
  // content-visibility:hidden, opacity:0).  offsetWidth/Height only catches
  // display:none and misses Raintree's other hiding mechanisms.
  const _isVisible = (el: Element): boolean => {
    if (typeof (el as any).checkVisibility === 'function') {
      return (el as any).checkVisibility({ checkVisibilityCSS: true });
    }
    // Fallback for environments where checkVisibility isn't available.
    let node: Element | null = el;
    while (node && node !== tabContent) {
      if (getComputedStyle(node as HTMLElement).display === 'none')
        return false;
      if (getComputedStyle(node as HTMLElement).visibility === 'hidden')
        return false;
      node = node.parentElement;
    }
    return true;
  };
  const _nestedWidgetNames = Array.from(
    tabContent.querySelectorAll('[data-widgetname]')
  )
    .filter(
      (el) =>
        !el.closest('.w-panel') &&
        el.querySelector('.w-tab-control') !== null &&
        _isVisible(el)
    )
    .map((el) => el.getAttribute('data-widgetname'))
    .filter(Boolean) as string[];
  const _nestedWidgetSel = _nestedWidgetNames.length
    ? _nestedWidgetNames.map((w) => `[data-widgetname="${w}"]`).join(', ')
    : null;

  // Phase 1: .w-panel elements (standard panels with captions).
  // Exclude panels inside nested tab widgets — Phase 2 handles those when the sub-tab is
  // active so element refs are valid and fields are interactive.
  for (let guard = 0; guard < 30; guard++) {
    const panels = Array.from(tabContent.querySelectorAll('.w-panel')).filter(
      (panel) => !_nestedWidgetSel || !panel.closest(_nestedWidgetSel)
    );
    let processedAny = false;
    for (const panel of panels) {
      const name = panelCaptionName(panel);
      if (!name || processed.has(name)) continue;
      processed.add(name);
      if (/^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/.test(name) || hasRomInputs(panel)) {
        await buildRomDatePanelStructure(panel, key);
      } else {
        await buildPanelStructure(panel, key);
      }
      processedAny = true;
      if (!document.contains(panel)) break; // picker navigation remounted DOM
    }
    if (!processedAny) break;
  }

  // Phase 2: nested .w-tab-control widgets — skipped during fill rebuilds (stale element refs).
  // Auto-discovered so no hardcoding needed. Re-query after each tab scan (picker nav remounts DOM).
  if (!skipPhase2) {
    for (const widgetName of _nestedWidgetNames) {
      const widgetEl = () =>
        tabContent.querySelector(
          `[data-widgetname="${widgetName}"]`
        ) as HTMLElement | null;
      if (!widgetEl()) continue;

      // Collect tab names up front (before any DOM changes)
      const initialCtrl = widgetEl()!.querySelector(
        '.w-tab-control'
      ) as HTMLElement | null;
      if (!initialCtrl || initialCtrl.closest('.w-panel')) continue;

      const tabNames = (
        Array.from(
          initialCtrl.querySelectorAll('.w-tab-control-tabs .tab-item')
        ) as HTMLElement[]
      )
        .map(
          (t) =>
            t.getAttribute('aria-label')?.trim() ||
            t.querySelector('span')?.textContent?.trim() ||
            ''
        )
        .filter(Boolean);

      const originalActiveName =
        (initialCtrl.querySelector('.tab-item.active') as HTMLElement | null)
          ?.getAttribute('aria-label')
          ?.trim() || '';

      for (const tabName of tabNames) {
        if (processed.has(tabName)) continue;
        processed.add(tabName);

        // Re-query after potential DOM remount from previous tab's picker scan
        const freshCtrl = widgetEl()?.querySelector(
          '.w-tab-control'
        ) as HTMLElement | null;
        if (!freshCtrl) continue;

        const tabItem = (
          Array.from(
            freshCtrl.querySelectorAll('.w-tab-control-tabs .tab-item')
          ) as HTMLElement[]
        ).find(
          (t) =>
            (t.getAttribute('aria-label')?.trim() ||
              t.querySelector('span')?.textContent?.trim()) === tabName
        );

        // Activate tab if not already active
        if (tabItem && !tabItem.classList.contains('active')) {
          tabItem.click();
          await new Promise((r) => setTimeout(r, 400));
          await dismissAbandonChanges();
        }

        // Re-query again after tab click (Raintree may remount content)
        const freshCtrl2 = widgetEl()?.querySelector(
          '.w-tab-control'
        ) as HTMLElement | null;
        if (!freshCtrl2) continue;
        const tabContentEl = freshCtrl2.querySelector(
          '.w-tab-control-content'
        ) as HTMLElement | null;
        if (!tabContentEl) continue;

        // Snapshot structure keys before scanning this sub-tab so we can register new ones
        const structBefore = new Set(
          Object.keys(questionInteractions[key].Structure)
        );

        // Scan panels within this tab first.
        // Use a per-sub-tab set so identical panel names across different sub-tabs
        // (e.g. "ICD Codes" appearing in both "Diagnosis 1" and "Diagnosis 2") are
        // each scanned — the shared `processed` set must not block them.
        const tabPanelProcessed = new Set<string>();
        for (const panel of Array.from(
          tabContentEl.querySelectorAll('.w-panel')
        )) {
          const panelName = panelCaptionName(panel);
          if (!panelName || tabPanelProcessed.has(panelName)) continue;
          tabPanelProcessed.add(panelName);
          if (/^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/.test(panelName) || hasRomInputs(panel)) {
            await buildRomDatePanelStructure(panel, key);
          } else {
            await buildPanelStructure(panel, key);
          }
        }

        // Re-query tab content after panel scan (panels may contain picker listboxes)
        const freshCtrl3 = widgetEl()?.querySelector(
          '.w-tab-control'
        ) as HTMLElement | null;
        const freshTabContent = freshCtrl3?.querySelector(
          '.w-tab-control-content'
        ) as HTMLElement | null;
        if (!freshTabContent) continue;

        // Scan the tab content itself for fields not inside a panel
        await buildPanelStructure(freshTabContent, key, tabName);

        // Register all newly added structure sections in the sub-tab registry
        for (const sectionKey of Object.keys(
          questionInteractions[key].Structure
        )) {
          if (!structBefore.has(sectionKey)) {
            const rKey = `${key}::${sectionKey}`;
            _subTabRegistry.set(rKey, { widgetName, tabName });
          }
        }
      }

      // Restore original active tab — fall back to first tab when originalActiveName is empty
      // so we never leave Raintree stuck on an exam form (which triggers "Abandon Changes"
      // when the caller navigates away from Objective).
      const finalCtrl = widgetEl()?.querySelector(
        '.w-tab-control'
      ) as HTMLElement | null;
      if (finalCtrl) {
        const allTabItems = Array.from(
          finalCtrl.querySelectorAll('.w-tab-control-tabs .tab-item')
        ) as HTMLElement[];
        const restoreItem = originalActiveName
          ? allTabItems.find(
              (t) => t.getAttribute('aria-label')?.trim() === originalActiveName
            ) ?? allTabItems[0]
          : allTabItems[0];
        if (restoreItem && !restoreItem.classList.contains('active')) {
          restoreItem.click();
          await dismissAbandonChanges(1500);
        }
      }
    }
  } // end if (!skipPhase2)

  // Phase 3: top-level listboxes directly in the tab content — not inside any .w-panel or
  // nested tab widget (those are already handled by Phase 1 & 2).
  const nestedWidgetSel = _nestedWidgetSel;
  const structure = questionInteractions[key].Structure;
  for (const listbox of Array.from(tabContent.querySelectorAll('.w-listbox'))) {
    if (listbox.closest('.w-panel')) continue;
    if (nestedWidgetSel && listbox.closest(nestedWidgetSel)) continue;
    if (listbox.querySelector('.w-listbox-scroller.disabled')) continue;
    const label = resolveLabel(listbox, tabContent, key);
    if (processed.has(label)) continue;
    processed.add(label);
    if (!structure[label]) structure[label] = {};
    const fields = structure[label];
    if (!buildCheckboxListboxField(listbox, tabContent, label, fields)) {
      if (!(await buildRowListField(listbox, tabContent, label, fields))) {
        await buildAddRowListboxField(listbox, tabContent, label, fields);
      }
    }
  }

  // Phase 4: flat HTS widget scan — handles absolute-positioned widgets directly in the
  // tab content. Runs when there are no panels (pure flat layout) OR when flat non-tab-control
  // widgets exist alongside nested sub-tab widgets (e.g. assessment complexity fields
  // next to the diagnosis sub-tab widget). Skip if all direct widgets are tab controls.
  const hasPanels = !!tabContent.querySelector('.w-panel');
  const flatContent =
    tabContent.querySelector(':scope > .w-panel-content') || tabContent;
  const hasFlatDirectWidgets = Array.from(
    flatContent.querySelectorAll(':scope > [data-widgetname]')
  ).some((w) => {
    const wEl = w as HTMLElement;
    const top = parseInt(wEl.style.top || '0');
    return (
      top >= 0 &&
      !wEl.querySelector('.w-tab-control') &&
      !!wEl.querySelector(
        '.w-combobox-openview, .w-memo-content, input[type="checkbox"], input[type="radio"]'
      )
    );
  });
  if (!hasPanels || hasFlatDirectWidgets) {
    buildFlatHtsStructure(flatContent, key);
  }
}

/**
 * Handle flat HTS tab layout: widgets positioned absolutely, no .w-panel structure.
 * Groups radio buttons by the nearest preceding standalone label above them.
 * Checkboxes are captured individually.
 */
function buildFlatHtsStructure(content: Element, key: string) {
  const structure = questionInteractions[key].Structure;

  // A mapped SOAP panel may already own the tab-named key (e.g. "Mechanism of Onset"
  // registered as "Subjective"). Never merge these loose widgets into it — put them in a
  // sibling subsection instead so the mapped panel stays a small, clean field.
  const effectiveKey = _mappedSoapStructureKeys.has(`${key}::${key}`)
    ? _catchAllKeyFor(key)
    : key;

  if (!structure[effectiveKey]) structure[effectiveKey] = {};
  const fields = structure[effectiveKey];

  /** These are unpaneled leftovers — skip anything a named panel already registered. */
  const skipCatchAllField = (label: string): boolean =>
    _isRegisteredElsewhere(structure, effectiveKey, label);

  const getTop = (w: HTMLElement) => parseInt(w.style.top || '0');
  const getLeft = (w: HTMLElement) => parseInt(w.style.left || '0');

  const allWidgets = Array.from(
    content.querySelectorAll(':scope > [data-widgetname]')
  ) as HTMLElement[];

  // Standalone labels: w-label whose <label> for= doesn't point to an input in this content
  type Anchor = { text: string; top: number; left: number };
  const anchors: Anchor[] = [];
  for (const w of allWidgets) {
    const labelEl = w.querySelector('.w-label');
    if (!labelEl) continue;
    const forId = w.querySelector('label')?.getAttribute('for') || '';
    const pointsToInput =
      forId &&
      !!content.querySelector(
        `input#${CSS.escape(forId)}, select#${CSS.escape(forId)}`
      );
    if (!pointsToInput) {
      const text = labelEl.textContent?.trim() || '';
      if (text) anchors.push({ text, top: getTop(w), left: getLeft(w) });
    }
  }

  // Return text of the nearest anchor above (and closest horizontally) to position (top, left)
  function nearestAnchor(top: number, left: number): string {
    let best = '';
    let bestScore = Infinity;
    for (const a of anchors) {
      if (a.top > top) continue;
      const score = top - a.top + Math.abs(a.left - left);
      if (score < bestScore) {
        bestScore = score;
        best = a.text;
      }
    }
    return best.replace(/[:\s]+$/, '') || key;
  }

  // Helper: skip widgets that are nested tab controls (handled by Phase 2)
  const isNestedTabWidget = (w: HTMLElement) =>
    !!w.querySelector('.w-tab-control');

  // Memos — each is its own field
  for (const w of allWidgets) {
    if (getTop(w) < 0 || isNestedTabWidget(w)) continue;
    const memo = w.querySelector('.w-memo-content') as HTMLElement | null;
    if (!memo) continue;
    const label = resolveLabel(memo, content, key);
    if (!label || label === key || fields[label]) continue;
    if (skipCatchAllField(label)) continue;
    bubbleFocus(memo);
    fields[label] = {
      'Existing Value': (memo as HTMLElement).innerText?.trim() || null,
      Input: async (val: string, replyType: string) => {
        if (isEmptyOrNull(val)) return;
        const elId = (memo as HTMLElement).id || label;
        if (replyType === 'append') {
          const trimmed = val.trim();
          if (!_memoAppendBase.has(elId))
            _memoAppendBase.set(
              elId,
              (memo as HTMLElement).innerText?.trim() || ''
            );
          const existing = _memoAppendedVals.get(elId) ?? [];
          if (existing.includes(trimmed)) return;
          existing.push(trimmed);
          _memoAppendedVals.set(elId, existing);
          const base = _memoAppendBase.get(elId)!;
          await eventBasedFill(
            memo as HTMLElement,
            base ? `${base}\n${existing.join('\n')}` : existing.join('\n')
          );
        } else {
          await eventBasedFill(memo as HTMLElement, val);
        }
      }
    };
  }

  // Comboboxes — each is its own field
  for (const w of allWidgets) {
    if (getTop(w) < 0 || isNestedTabWidget(w)) continue;
    const el = w.querySelector(
      '.w-combobox-openview'
    ) as HTMLSelectElement | null;
    if (!el) continue;
    const label = resolveLabel(el, content, key);
    if (!label || label === key || fields[label]) continue;
    if (skipCatchAllField(label)) continue;
    const optionMap = new Map<string, string>();
    Array.from(el.options).forEach((o) => {
      const txt = o.textContent?.trim() || '';
      if (txt) optionMap.set(txt, o.value);
    });
    const choices = Array.from(optionMap.keys());
    const existingText =
      el.selectedIndex >= 0
        ? el.options[el.selectedIndex]?.textContent?.trim() || el.value || null
        : el.value || null;
    fields[label] = {
      'Existing Value': existingText,
      Choices: choices,
      Input: (val: string) => {
        if (isEmptyOrNull(val)) return;
        const internalValue = optionMap.get(val) ?? val;
        bubbleFocus(el);
        el.focus();
        const wasReadonly = el.hasAttribute('readonly');
        if (wasReadonly) el.removeAttribute('readonly');
        const nativeSetter = Object.getOwnPropertyDescriptor(
          window.HTMLSelectElement.prototype,
          'value'
        )?.set;
        if (nativeSetter) nativeSetter.call(el, internalValue);
        else el.value = internalValue;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        const wrapper = el.closest('.w-combobox') as HTMLElement | null;
        if (wrapper)
          wrapper.dispatchEvent(new Event('change', { bubbles: true }));
        if (wasReadonly) el.setAttribute('readonly', '');
        el.blur();
      }
    };
  }

  // Checkboxes — each is its own field
  for (const w of allWidgets) {
    const el = w.querySelector(
      'input[type="checkbox"]'
    ) as HTMLInputElement | null;
    if (!el) continue;
    if (getTop(w) < 0 || isNestedTabWidget(w)) continue;
    const label =
      el.getAttribute('aria-label')?.trim() ||
      Array.from(content.querySelectorAll(`label[for="${el.id}"]`))
        .map((l) => l.textContent?.replace(/ /g, ' ').trim() || '')
        .find((t) => t.length > 0) ||
      el.id;
    if (!label || fields[label]) continue;
    if (skipCatchAllField(label)) continue;
    fields[label] = {
      'Existing Value': el.checked ? 'true' : 'false',
      Description: 'true or false',
      Input: (val: string) => {
        const shouldCheck =
          val === 'true' || val === '1' || val?.toLowerCase() === 'yes';
        const liveEl =
          ((el.id
            ? document.getElementById(el.id)
            : null) as HTMLInputElement | null) ?? el;
        if (shouldCheck === liveEl.checked) return;
        const wasReadonly = liveEl.hasAttribute('readonly');
        if (wasReadonly) liveEl.removeAttribute('readonly');
        const lblEl = liveEl
          .closest('.w-checkbox')
          ?.querySelector('label.custom-control-label') as HTMLElement | null;
        if (lblEl) {
          lblEl.click();
        } else {
          liveEl.checked = shouldCheck;
          liveEl.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (wasReadonly) liveEl.setAttribute('readonly', '');
      }
    };
  }

  // Radio buttons — group by nearest anchor label
  const radioGroups = new Map<
    string,
    { input: HTMLInputElement; text: string }[]
  >();
  for (const w of allWidgets) {
    if (isNestedTabWidget(w)) continue;
    const el = w.querySelector(
      'input[type="radio"]'
    ) as HTMLInputElement | null;
    if (!el) continue;
    const groupLabel = nearestAnchor(getTop(w), getLeft(w));
    const text =
      content.querySelector(`label[for="${el.id}"]`)?.textContent?.trim() ||
      el.getAttribute('aria-label')?.trim() ||
      '';
    if (!text) continue;
    if (!radioGroups.has(groupLabel)) radioGroups.set(groupLabel, []);
    radioGroups.get(groupLabel)!.push({ input: el, text });
  }

  Array.from(radioGroups.entries()).forEach(([groupLabel, entries]) => {
    const choices = entries.map(
      (e: { input: HTMLInputElement; text: string }) => e.text
    );
    const selected = entries.find(
      (e: { input: HTMLInputElement; text: string }) => e.input.checked
    );
    fields[groupLabel] = {
      'Existing Value': selected?.text ?? null,
      Choices: choices,
      Input: (val: string) => {
        if (isEmptyOrNull(val)) return;
        const target = entries.find(
          (e: { input: HTMLInputElement; text: string }) => e.text === val
        );
        if (!target) return;
        const liveInput =
          ((target.input.id
            ? document.getElementById(target.input.id)
            : null) as HTMLInputElement | null) ?? target.input;
        const lblEl = liveInput
          .closest('.w-radio-button')
          ?.querySelector('label') as HTMLElement | null;
        const wasReadonly = liveInput.hasAttribute('readonly');
        if (wasReadonly) liveInput.removeAttribute('readonly');
        if (lblEl) {
          lblEl.click();
        } else {
          liveInput.checked = true;
          liveInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (wasReadonly) liveInput.setAttribute('readonly', '');
      }
    };
  });
}

/** ROM/MMT date-column panels (caption like "06-30-26") hold all Initial fields for a
 *  measurement panel. Instead of creating a date-keyed sub-section, distribute each field
 *  into the correct motion sub-panel using the name attribute pattern [PREFIX]_[col]_[motion]. */
async function buildRomDatePanelStructure(panel: Element, topLevelKey: string) {
  const content = panel.querySelector(':scope > .w-panel-content') || panel;
  const structure = questionInteractions[topLevelKey].Structure;
  // Lowercase keys so HABD/HAbd/habd all resolve correctly
  const ptAbbrLower: Record<string, string> = {
    'flex': 'Flexion', 'ext': 'Extension', 'abd': 'Abduction',
    'add': 'Adduction', 'er': 'External Rotation', 'ir': 'Internal Rotation',
    'ha': 'Horizontal Adduction', 'habd': 'Horizontal Abduction',
  };
  const colMap: Record<string, string> = {
    'init': 'Initial', 'goal': 'Goal', 'comm': 'Comments', 'last': 'Last', 'current': 'Current'
  };
  const romNameRe = /^[A-Za-z]{2,8}_([A-Za-z]+)_([A-Za-z]+)$/;
  const motionName = (raw: string) => ptAbbrLower[raw.toLowerCase()] ?? raw;

  // Text / numeric inputs
  content
    .querySelectorAll('.w-field-openview input[type="text"], .w-field-openview input[inputmode="numeric"]')
    .forEach((input) => {
      const el = input as HTMLInputElement;
      const name = el.getAttribute('name')?.trim() || '';
      const romMatch = name.match(romNameRe);
      if (!romMatch) return;
      const colLabel = colMap[romMatch[1].toLowerCase()];
      if (!colLabel) return;
      const motion = motionName(romMatch[2]);
      if (!structure[motion]) structure[motion] = {};
      if (structure[motion][colLabel]) return;
      structure[motion][colLabel] = {
        'Existing Value': el.value || null,
        Input: async (val: string) => {
          if (isEmptyOrNull(val)) return;
          nativeInputFill(el, val);
        }
      };
    });

  // Memo / RTF fields (e.g. Comments column)
  content.querySelectorAll('.w-memo-content').forEach((memoEl) => {
    const el = memoEl as HTMLElement;
    const name = el.getAttribute('name')?.trim() || '';
    const romMatch = name.match(romNameRe);
    if (!romMatch) return;
    const colLabel = colMap[romMatch[1].toLowerCase()];
    if (!colLabel) return;
    const motion = motionName(romMatch[2]);
    if (!structure[motion]) structure[motion] = {};
    if (structure[motion][colLabel]) return;
    structure[motion][colLabel] = {
      'Existing Value': el.innerText?.trim() || null,
      Input: async (val: string) => {
        if (isEmptyOrNull(val)) return;
        await eventBasedFill(el, val);
      }
    };
  });
}

/** Returns true if the panel contains ROM/MMT grid inputs (name pattern PREFIX_col_motion). */
function hasRomInputs(panel: Element): boolean {
  const content = panel.querySelector(':scope > .w-panel-content') || panel;
  return Array.from(
    content.querySelectorAll('.w-field-openview input[type="text"]')
  ).some((el) =>
    /^[A-Za-z]{2,8}_[A-Za-z]+_[A-Za-z]+$/.test(
      (el as HTMLInputElement).getAttribute('name') || ''
    )
  );
}

async function buildPanelStructure(
  panel: Element,
  topLevelKey: string,
  nameOverride?: string
) {
  const subsectionName = nameOverride || panelCaptionName(panel);
  if (!subsectionName) return;
  if (/add\s+cc/i.test(subsectionName)) return;

  // Subjective/Assessment panel mapping (see SUBJECTIVE_PANEL_NAMES / ASSESSMENT_PANEL_NAMES).
  // When this panel carries the free-text Subjective/Assessment content, register it under the
  // key "Subjective"/"Assessment" instead of the raw Raintree panel caption (e.g. "Mechanism of
  // Onset") — this is what the AI schema and the extension's own UI display as the field name.
  // `subsectionName` itself stays the raw caption so the Medicare-Cap check and other
  // panel-name-specific logic below still work correctly.
  //
  // Matching is on the caption alone, independent of note type. If a note contains more than
  // one of the listed panels, the first one scanned claims the key and later matches fall back
  // to their own caption rather than overwriting it.
  const _soapTarget =
    topLevelKey === 'Subjective' && _matchesAnyPanelName(subsectionName, SUBJECTIVE_PANEL_NAMES)
      ? 'Subjective'
      : topLevelKey === 'Assessment' && _matchesAnyPanelName(subsectionName, ASSESSMENT_PANEL_NAMES)
      ? 'Assessment'
      : null;
  const structureKey =
    _soapTarget && !_mappedSoapStructureKeys.has(`${topLevelKey}::${_soapTarget}`)
      ? _soapTarget
      : subsectionName;

  // A rename actually happened only when structureKey differs from the raw caption. Without
  // this flag, the tab-level virtual-panel scan (called with nameOverride = tabName, so
  // subsectionName is literally "Subjective"/"Assessment") would satisfy any
  // `structureKey === 'Subjective'` test for free and be mistaken for the mapped panel.
  const isMappedSoapPanel = structureKey !== subsectionName;

  // This call is the tab-level catch-all sweep for unpaneled widgets, not a real panel.
  const isTabLevelCatchAll = subsectionName === topLevelKey;

  const structure = questionInteractions[topLevelKey].Structure;

  const _reservedKey = `${topLevelKey}::${structureKey}`;
  if (isMappedSoapPanel) _mappedSoapStructureKeys.add(_reservedKey);

  // If a mapped SOAP panel already owns this key, don't merge into it — the loose widgets
  // would bury the mapped memo and duplicate fields the named panels already registered.
  const effectiveKey =
    !isMappedSoapPanel && _mappedSoapStructureKeys.has(_reservedKey)
      ? _catchAllKeyFor(topLevelKey)
      : structureKey;
  const isRedirectedCatchAll = effectiveKey !== structureKey;

  if (!structure[effectiveKey]) structure[effectiveKey] = {};
  const fields = structure[effectiveKey];

  /** Catch-all sweeps must not re-register a field a named panel already owns, and must not
   *  create a field named after the tab itself (the "Subjective inside Subjective" case —
   *  buildFlatHtsStructure already guards this with its own `label === key` check). */
  const skipCatchAllField = (label: string): boolean => {
    if (!isTabLevelCatchAll && !isRedirectedCatchAll) return false;
    if (label === topLevelKey || label === subsectionName) return true;
    return _isRegisteredElsewhere(structure, effectiveKey, label);
  };

  // For real panels use .w-panel-content; for virtual (nested tab) panels use the element itself
  // but exclude fields already inside a nested .w-panel (they're handled separately)
  // Use :scope > to only match a DIRECT .w-panel-content child (not nested panel contents).
  // When panel is a tab content element the direct child is not .w-panel-content, so we fall
  // back to the element itself and scan all direct fields (skipIfInPanel guards nested ones).
  const content = panel.querySelector(':scope > .w-panel-content') || panel;

  // ── Memo / rich-text fields ──────────────────────────────────────────────
  // When scanning virtual tab content, skip fields already inside a .w-panel (handled by phase 1)
  const skipIfInPanel = (el: Element) =>
    !!nameOverride && !!el.closest('.w-panel');

  // Mapped SOAP panel: its free-text memo is keyed "Subjective"/"Assessment" too, not by the
  // raw widget label. Raintree names these memos things like "Othersub1" / "Clinical1", which
  // would surface as Subjective → { Othersub1: … }; keying it to match the subsection lets the
  // output flattener collapse that to Subjective → value. Only the FIRST memo in the panel is
  // renamed (and it alone carries the inference description) — a panel with several memos keeps
  // the rest under their own labels rather than having them all collide on one key.
  let mappedMemoClaimed = false;

  content.querySelectorAll('.w-memo-content').forEach((memo) => {
    if (skipIfInPanel(memo)) return;
    const rawLabel = resolveLabel(memo, content, subsectionName);
    if (skipCatchAllField(rawLabel)) return;
    const isMappedMemo = isMappedSoapPanel && !mappedMemoClaimed;
    if (isMappedMemo) mappedMemoClaimed = true;
    const label = isMappedMemo ? structureKey : rawLabel;
    const el = memo as HTMLElement;
    bubbleFocus(el);

    // Subjective / Assessment memo panels: these are sometimes left blank because the
    // transcript never explicitly says "subjective" or "assessment" — the content is
    // implied rather than labeled. Point the AI at the correct panel for this note type
    // (see SUBJECTIVE_PANEL_NAMES / ASSESSMENT_PANEL_NAMES) and tell it to infer from context.
    // Only the single remapped memo gets this — isMappedMemo requires both that a rename
    // actually occurred (so the tab-level sweep, where subsectionName is itself "Subjective",
    // can't stamp it onto unrelated memos) and that this is the panel's first memo.
    let memoDescription: string | undefined;
    if (isMappedMemo) {
      if (topLevelKey === 'Subjective' && structureKey === 'Subjective') {
        memoDescription =
          'Summarize the patient\'s subjective report for this visit — symptoms, complaints, pain, ' +
          'or how they say they are feeling/functioning — based on the transcript. ' +
          'The clinician will often NOT explicitly say the word "subjective" — infer this from whatever ' +
          'the patient reported or the clinician described about the patient\'s condition, even if stated indirectly. ' +
          'Only leave blank if the transcript truly contains no patient-reported information.';
      } else if (topLevelKey === 'Assessment' && structureKey === 'Assessment') {
        memoDescription =
          'Summarize the clinical impression/assessment for this visit — the clinician\'s interpretation of the ' +
          'patient\'s progress, condition, or response to treatment — based on the transcript. ' +
          'The clinician will often NOT explicitly say the word "assessment" — infer this from the clinician\'s ' +
          'overall clinical judgment as described in the visit, even if stated indirectly. ' +
          'Only leave blank if the transcript truly contains no clinical assessment information. ' +
          'This single field also covers the Clinical Impression — apply any Clinical Impression instructions here. ' +
          'Write one cohesive narrative with no section headings or labels (e.g. "Clinical Impression:"), and do not include content that belongs to other fields.';
      }
    }

    if (/^current surgery(?! date)/i.test(label)) {
      memoDescription =
        'Only the current surgery relevant to the condition being treated this episode (e.g. the recent procedure the patient is in post-op rehab for). ' +
        'Do NOT list remote, prior, or unrelated surgical history. Leave blank if there is no current surgery.';
    }

    fields[label] = {
      'Existing Value': el.innerText.trim() || null,
      ...(memoDescription && { Description: memoDescription }),
      Input: async (val: string, replyType: string) => {
        if (isEmptyOrNull(val)) return;
        const elId = el.id || label;
        // Clinical Impression panel: if the field has the Medicare Cap template
        // (with @@ filled or not), always append to preserve the template text.
        if (subsectionName === 'Clinical Impression') {
          const currentContent = el.innerText?.trim() || '';
          if ( currentContent.includes('MEDICARE EXCEPTION STATEMENT')) replyType = 'append';
        }
        if (replyType === 'append') {
          const trimmed = val.trim();
          if (!_memoAppendBase.has(elId))
            _memoAppendBase.set(elId, el.innerText?.trim() || '');
          const existing = _memoAppendedVals.get(elId) ?? [];
          if (existing.includes(trimmed)) return;
          existing.push(trimmed);
          _memoAppendedVals.set(elId, existing);
          const base = _memoAppendBase.get(elId)!;
          await eventBasedFill(
            el,
            base ? `${base}\n${existing.join('\n')}` : existing.join('\n')
          );
        } else {
          await eventBasedFill(el, val);
        }
      }
    };
  });

  // ── Date / text inputs ───────────────────────────────────────────────────
  content
    .querySelectorAll(
      '.w-field-openview input[type="text"], .w-field-openview input[inputmode="numeric"]'
    )
    .forEach((input) => {
      if (skipIfInPanel(input)) return;
      const el = input as HTMLInputElement;
      const isLinkedField = !!el.closest('.w-field-linked');
      const elName = el.getAttribute('name') || '';
      const label = resolveLabel(el, content, subsectionName);
      if (skipCatchAllField(label)) return;
      // Detect by placeholder (primary) or by name pattern as fallback (placeholder may be absent in live DOM)
      const isDateField = !!el.placeholder?.match(/mm[\/\-]dd/i);
      const isTimeOut = /^out\d*$/i.test(elName);
      const isTimeIn = /^in\d+$/i.test(elName);
      const isTimeField =
        !!el.placeholder?.toLowerCase().startsWith('hh:mm') ||
        isTimeOut ||
        isTimeIn;
      let fieldDescription: string | undefined;
      if (isDateField && /^current surgery date/i.test(label)) {
        fieldDescription =
          'Date of the current surgery (the one in Current Surgery Details) in mm-dd-yy format (e.g. 06-01-26). ' +
          'Do NOT use dates of remote or unrelated surgeries. Leave blank if there is no current surgery or the date is not stated.';
      } else if (isDateField) {
        fieldDescription = 'Date in mm-dd-yy format (e.g. 06-01-26)';
      } else if (isTimeField) {
        fieldDescription = isTimeOut
          ? 'Time Out in hh:mma/p format (e.g. 10:00a or 1:30p). Must be later than Time In.'
          : 'Time In in hh:mma/p format (e.g. 8:00a or 10:00a). Must be earlier than Time Out.';
      } else if (isLinkedField) {
        fieldDescription =
          'Return the most specific ICD-10 code that corresponds to the patient underlying condition, symptoms, or functional limitations described in the transcript. ' +
          'You MUST only use codes from this approved list: ' +
          'M47.818, M47.896, M47.897, M47.898, M54.2, M54.59, M54.89, M53.3, M54.14, M54.15, M54.16, M54.17, M51.14, M51.15, M51.16, M51.17, M50.11, M50.121, M50.122, M50.123, M50.13, M54.11, M54.12, M54.13, M54.6, M48.01, M48.02, M48.03, M48.061, M48.062, M54.31, M54.32, M54.41, M54.42, R29.3, M40.03, M40.04, M40.05, M47.817, M47.26, M47.27, M47.28, M47.816, M51.26, M51.27, M51.36, M51.37, M50.21, M50.23, M50.31, M50.33, M26.69, M25.511, M25.512, M25.611, M25.612, M62.411, M62.412, M62.421, M62.422, M62.511, M62.512, M62.521, M62.522, M25.711, M25.712, M75.111, M75.112, M75.121, M75.122, M75.81, M75.82, M75.51, M75.52, M75.01, M75.02, M75.31, M75.32, M75.41, M75.42, M24.411, M24.412, M19.011, M19.012, M25.521, M25.522, M25.621, M25.622, M62.431, M62.432, M62.531, M62.532, M77.11, M77.12, M79.641, M79.642, M79.644, M79.645, M25.531, M25.532, M25.631, M25.632, M25.641, M25.642, M62.441, M62.442, M62.541, M62.542, G56.01, G56.02, G56.03, G56.11, G56.12, M25.551, M25.552, M25.651, M25.652, M25.751, M25.752, M70.61, M70.62, M70.71, M70.72, M76.01, M76.02, M76.11, M76.12, M76.21, M76.22, M76.31, M76.32, M16.11, M16.12, M16.0, M25.561, M25.562, M25.661, M25.662, M62.451, M62.452, M62.551, M62.552, M62.561, M62.562, M22.41, M22.42, M17.11, M17.12, M17.0, M25.571, M25.572, M79.671, M79.672, M79.674, M79.675, M25.671, M25.672, M25.674, M25.675, M62.461, M62.462, M62.471, M62.472, M62.571, M62.572, M76.61, M76.62, M72.2, M62.831, M62.838, M62.48, M62.49, M62.58, R60.0, R60.1, M79.604, M79.605, M79.661, M79.662, M79.651, M79.652, M79.601, M79.602, M79.621, M79.622, M79.631, M79.632, M62.81, H81.11, H81.12, H81.13, R42, R26.0, R26.1, R26.81, R26.89, R26.2, R27.8, G20.A1, G20.A2, G20.B1, G20.B2, G51.0, Q68.0, M43.6, M41.112, M41.113, M41.114, M41.115, M41.116, M41.117, M41.122, M41.123, M41.124, M41.125, M41.126, M41.127. ' +
          'Leave blank if there is insufficient information to determine a code.';
      } else if (/^init_frequency\d+$/i.test(elName)) {
        fieldDescription =
          'Number of visits per period (numeric only, e.g. 2). Do not include "x/week" or any text.';
      } else if (/^init_duration\d+$/i.test(elName)) {
        fieldDescription =
          'Number of weeks for this plan (numeric only, e.g. 8). Do not concatenate with the frequency number.';
      }
      fields[label] = {
        'Existing Value': el.value || null,
        Description: fieldDescription,
        Input: async (val: string) => {
          if (isEmptyOrNull(val)) return;
          // Linked fields (ICD codes) require a focus→fill→blur cycle so Raintree's
          // server validates and commits the value. Without focusin, the server
          // accepts the FieldSetContent message but returns an empty patch (no-op).
          if (isLinkedField) {
            bubbleFocus(el);
            el.focus();
            nativeInputFill(el, val);
            // Wait for Raintree's server-side ICD lookup to fire before committing.
            await new Promise((r) => setTimeout(r, 500));
            // Enter key tells Raintree to accept the first autocomplete match —
            // without this the value shows on screen but isn't committed to the record.
            el.dispatchEvent(
              new KeyboardEvent('keydown', {
                key: 'Enter',
                keyCode: 13,
                bubbles: true
              })
            );
            el.dispatchEvent(
              new KeyboardEvent('keypress', {
                key: 'Enter',
                keyCode: 13,
                bubbles: true
              })
            );
            el.dispatchEvent(
              new KeyboardEvent('keyup', {
                key: 'Enter',
                keyCode: 13,
                bubbles: true
              })
            );
            await new Promise((r) => setTimeout(r, 300));
            el.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
            el.blur();
            await new Promise((r) => setTimeout(r, 300));
            // Raintree sometimes navigates to a full-screen ICD10 Select picker after Enter.
            // Detect it and click back so we don't get stuck on that screen.
            const icd10Caption = document.querySelector(
              '.screen-header .screen-caption'
            );
            if (icd10Caption?.textContent?.trim() === 'ICD10 Select') {
              const backBtn = document.querySelector(
                '.backdown .backbtn, [id^="backbutton-"]'
              ) as HTMLElement | null;
              if (backBtn) {
                backBtn.click();
                await new Promise((r) => setTimeout(r, 500));
              }
            }
          } else {
            nativeInputFill(el, val);
          }
        }
      };
    });

  // ── Dropdown fields ──────────────────────────────────────────────────────
  content.querySelectorAll('.w-combobox-openview').forEach((sel) => {
    if (skipIfInPanel(sel)) return;
    const el = sel as HTMLSelectElement;
    const label = resolveLabel(el, content, subsectionName);
    if (skipCatchAllField(label)) return;
    const optionMap = new Map<string, string>(); // text → value attribute
    Array.from(el.options).forEach((o) => {
      const text = o.textContent?.trim() || '';
      if (text) optionMap.set(text, o.value);
    });
    const choices = Array.from(optionMap.keys());
    const existingText =
      el.selectedIndex >= 0
        ? el.options[el.selectedIndex]?.textContent?.trim() || el.value || null
        : el.value || null;
    fields[label] = {
      'Existing Value': existingText,
      Choices: choices,
      ...(/^work status$/i.test(label) && {
        Description:
          "Only populate if the visit is explicitly documented as a workers' compensation case. Otherwise leave blank."
      }),
      Input: (val: string) => {
        if (isEmptyOrNull(val)) return;
        // Match by display text first (LLM returns text), fall back to raw value
        const internalValue = optionMap.get(val) ?? val;
        bubbleFocus(el);
        el.focus();
        // Remove Raintree's readonly guard before changing value (same pattern as checkbox/radio)
        const wasReadonly = el.hasAttribute('readonly');
        if (wasReadonly) el.removeAttribute('readonly');
        const nativeSetter = Object.getOwnPropertyDescriptor(
          window.HTMLSelectElement.prototype,
          'value'
        )?.set;
        if (nativeSetter) {
          nativeSetter.call(el, internalValue);
        } else {
          el.value = internalValue;
        }
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        const wrapper = el.closest('.w-combobox') as HTMLElement | null;
        if (wrapper)
          wrapper.dispatchEvent(new Event('change', { bubbles: true }));
        if (wasReadonly) el.setAttribute('readonly', '');
        el.blur();
      }
    };
  });

  // ── Checkboxes ───────────────────────────────────────────────────────────
  content
    .querySelectorAll('.w-checkbox input[type="checkbox"]')
    .forEach((checkbox) => {
      if (skipIfInPanel(checkbox)) return;
      // Skip checkboxes in tab-header widgets (negative top, e.g. "Show Unit Summary")
      const widgetEl = checkbox.closest(
        '[data-widgetname]'
      ) as HTMLElement | null;
      if (widgetEl && parseInt(widgetEl.style.top || '0') < 0) return;
      const el = checkbox as HTMLInputElement;
      const label = resolveLabel(el, content, subsectionName);
      if (skipCatchAllField(label)) return;
      fields[label] = {
        'Existing Value': el.checked ? 'true' : 'false',
        Description: 'true or false',
        Input: (val: string) => {
          const shouldCheck =
            val === 'true' || val === '1' || val?.toLowerCase() === 'yes';
          const liveEl =
            ((el.id
              ? document.getElementById(el.id)
              : null) as HTMLInputElement | null) ?? el;
          if (shouldCheck === liveEl.checked) return;
          const wasReadonly = liveEl.hasAttribute('readonly');
          if (wasReadonly) liveEl.removeAttribute('readonly');
          const lblEl = liveEl
            .closest('.w-checkbox')
            ?.querySelector('label.custom-control-label') as HTMLElement | null;
          if (lblEl) {
            lblEl.click();
          } else {
            liveEl.checked = shouldCheck;
            liveEl.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (wasReadonly) liveEl.setAttribute('readonly', '');
        }
      };
    });

  // ── Radio group(s) ───────────────────────────────────────────────────────
  const radios = Array.from(
    content.querySelectorAll('.w-radio-button input[type="radio"]')
  ).filter((r) => !skipIfInPanel(r)) as HTMLInputElement[];
  if (radios.length > 0) {
    // Check for standalone labels (w-label whose <label for=""> doesn't point to an input here).
    // When present, use position-based grouping so each label gets its own field.
    const standaloneAnchors: { text: string; top: number; left: number }[] = [];
    Array.from(content.querySelectorAll('[data-widgetname]')).forEach((w) => {
      if (skipIfInPanel(w)) return;
      const labelEl = w.querySelector('.w-label');
      if (!labelEl) return;
      const forId =
        (w.querySelector('label') as HTMLLabelElement | null)?.getAttribute(
          'for'
        ) || '';
      const pointsToInput =
        forId &&
        !!content.querySelector(
          `input#${CSS.escape(forId)}, select#${CSS.escape(forId)}`
        );
      if (!pointsToInput) {
        const text = labelEl.textContent?.trim() || '';
        if (!text || /^\d/.test(text)) return; // skip empty or digit-starting labels (scale values, not section headers)
        const wEl = w as HTMLElement;
        standaloneAnchors.push({
          text,
          top: parseInt(wEl.style.top || '0'),
          left: parseInt(wEl.style.left || '0')
        });
      }
    });

    if (standaloneAnchors.length > 0) {
      // Group radios by nearest preceding standalone label using position scoring
      const radioGroupMap = new Map<
        string,
        { input: HTMLInputElement; text: string }[]
      >();
      radios.forEach((r) => {
        const rWidget = r.closest('[data-widgetname]') as HTMLElement | null;
        const rTop = rWidget ? parseInt(rWidget.style.top || '0') : 0;
        const rLeft = rWidget ? parseInt(rWidget.style.left || '0') : 0;
        let best = '';
        let bestScore = Infinity;
        for (const a of standaloneAnchors) {
          if (a.top > rTop) continue;
          const score = rTop - a.top + Math.abs(a.left - rLeft);
          if (score < bestScore) {
            bestScore = score;
            best = a.text;
          }
        }
        const groupLabel = best.replace(/[:\s]+$/, '') || subsectionName;
        const fromLabel = content.querySelector(`label[for="${r.id}"]`)?.textContent?.trim();
        const fromAria = r.getAttribute('aria-label')?.trim();
        const isDescriptiveAria = !!fromAria && /\d+\s*=/.test(fromAria);
        const rText = fromLabel || (isDescriptiveAria ? '' : fromAria) || '';
        if (!rText) return;
        if (!radioGroupMap.has(groupLabel)) radioGroupMap.set(groupLabel, []);
        radioGroupMap.get(groupLabel)!.push({ input: r, text: rText });
      });
      Array.from(radioGroupMap.entries()).forEach(([groupLabel, entries]) => {
        const choices = entries.map(
          (e: { input: HTMLInputElement; text: string }) => e.text
        );
        const selected = entries.find(
          (e: { input: HTMLInputElement; text: string }) => e.input.checked
        );
        fields[groupLabel] = {
          'Existing Value': selected?.text ?? null,
          Choices: choices,
          Input: (val: string) => {
            if (isEmptyOrNull(val)) return;
            const target = entries.find(
              (e: { input: HTMLInputElement; text: string }) =>
                e.text === val || matchesChoice(val, e.text)
            );
            if (!target) return;
            const liveRadio = (
              target.input.id ? document.getElementById(target.input.id) : null
            ) as HTMLInputElement | null;
            const liveLabel = target.input.id
              ? (document.querySelector(
                  `label[for="${target.input.id}"]`
                ) as HTMLElement | null)
              : null;
            if (liveLabel && liveRadio) {
              const wasReadonly = liveRadio.hasAttribute('readonly');
              if (wasReadonly) liveRadio.removeAttribute('readonly');
              liveLabel.click();
              if (wasReadonly) liveRadio.setAttribute('readonly', '');
            } else if (liveRadio) {
              liveRadio.removeAttribute('readonly');
              liveRadio.checked = true;
              liveRadio.dispatchEvent(new Event('change', { bubbles: true }));
              liveRadio.setAttribute('readonly', '');
            } else {
              console.warn(
                `[Raintree] Radio not found in live DOM: ${target.input.id}`
              );
            }
          }
        };
      });
    } else {
      buildRadioScaleField(radios, content, fields);
    }
  }

  // ── Picker widgets (multi-select grid with Quick find) ───────────────────
  content.querySelectorAll('.w-picker').forEach((picker) => {
    if (skipIfInPanel(picker)) return;
    buildPickerField(picker, content, subsectionName, fields);
  });

  // ── Listboxes with checkbox rows ─────────────────────────────────────────
  for (const listbox of Array.from(content.querySelectorAll('.w-listbox'))) {
    if (skipIfInPanel(listbox)) continue;
    if (listbox.querySelector('.w-listbox-scroller.disabled')) continue;

    // objview: right-click → full-page exam form (Objective Exam section)
    const closestWidget = listbox.closest('[data-widgetname]');
    if (
      closestWidget?.getAttribute('data-widgetname') === 'objview' ||
      (listbox as HTMLElement).id === 'objview'
    ) {
      await buildObjViewField(listbox, content, subsectionName, fields);
      continue;
    }

    // Try checkbox-row listbox first; then row-list (Goals/Chief Complaints style); then add-row
    if (!buildCheckboxListboxField(listbox, content, subsectionName, fields)) {
      if (
        !(await buildRowListField(listbox, content, subsectionName, fields))
      ) {
        await buildAddRowListboxField(listbox, content, subsectionName, fields);
      }
    }
  }
}

// ─── Field Builders ───────────────────────────────────────────────────────────

/**
 * Pair each scale radio with the numeric label directly above it (same row of labels,
 * nearest horizontal center). Raintree's label[for]/aria-label on these scales are often
 * shifted one column from what is displayed. Returns an empty map unless every radio maps
 * to a distinct label, so callers can fall back.
 */
function byLayoutPosition(
  radios: HTMLInputElement[],
  container: Element
): Map<HTMLInputElement, string> {
  const box = (w: HTMLElement) => ({
    top: parseInt(w.style.top || '0'),
    cx: parseInt(w.style.left || '0') + parseInt(w.style.width || '0') / 2
  });
  const labels = Array.from(container.querySelectorAll('[data-widgetname]'))
    .map((w) => ({
      w: w as HTMLElement,
      text: w.querySelector('.w-label')?.textContent?.trim() || ''
    }))
    .filter((l) => /^\d+$/.test(l.text))
    .map((l) => ({ ...l, ...box(l.w) }));
  const map = new Map<HTMLInputElement, string>();
  const used = new Set<string>();
  for (const r of radios) {
    const rw = r.closest('[data-widgetname]') as HTMLElement | null;
    if (!rw) return new Map();
    const rb = box(rw);
    const above = labels.filter(
      (l) => l.w.parentElement === rw.parentElement && l.top < rb.top
    );
    if (above.length === 0) return new Map();
    const rowTop = Math.max(...above.map((l) => l.top));
    const best = above
      .filter((l) => l.top === rowTop)
      .reduce((a, b) => (Math.abs(a.cx - rb.cx) <= Math.abs(b.cx - rb.cx) ? a : b));
    if (Math.abs(best.cx - rb.cx) > 18 || used.has(best.text)) return new Map();
    used.add(best.text);
    map.set(r, best.text);
  }
  return map;
}

function buildRadioScaleField(
  radios: HTMLInputElement[],
  container: Element,
  fields: Record<string, any>
) {
  // Collect label text for each radio from label[for=id] or aria-label.
  // Descriptive aria-labels like "10 = Worse Possible Pain" are skipped — the
  // numeric value is extracted separately via the index fallback.
  const radioValueMap = byLayoutPosition(radios, container);
  if (radioValueMap.size === 0) radios.forEach((r) => {
    const fromLabel = container
      .querySelector(`label[for="${r.id}"]`)
      ?.textContent?.trim();
    const fromAria = r.getAttribute('aria-label')?.trim();
    const isDescriptiveAria = !!fromAria && /\d+\s*=/.test(fromAria);
    const text = fromLabel || (isDescriptiveAria ? '' : fromAria) || '';
    if (text) radioValueMap.set(r, text);
  });
  // Fill gaps with index for any unmapped radios, avoiding collisions
  const takenValues = new Set(radioValueMap.values());
  radios.forEach((r, i) => {
    if (radioValueMap.has(r)) return;
    const indexValue = String(i);
    if (!takenValues.has(indexValue)) radioValueMap.set(r, indexValue);
    takenValues.add(indexValue);
  });

  const choices = Array.from(radioValueMap.values());
  // Numeric pain scale if all choices are numeric
  const allNumeric = choices.every((c) => /^\d+$/.test(c));
  const sorted = allNumeric ? choices.slice().sort((a, b) => +a - +b) : choices;
  const fieldLabel = allNumeric
    ? `Pain Scale (${sorted[0]}-${sorted[sorted.length - 1]})`
    : 'Value';
  const description = allNumeric
    ? `${sorted[0]} = No Pain, ${sorted[sorted.length - 1]} = Worst Possible Pain`
    : `Choose one of: ${sorted.join(', ')}`;
  const selected = radios.find((r) => r.checked);

  fields[fieldLabel] = {
    'Existing Value': selected ? radioValueMap.get(selected) || null : null,
    Choices: sorted,
    Description: description,
    Input: (val: string) => {
      if (isEmptyOrNull(val)) return;
      Array.from(radioValueMap.entries()).some(([radio, value]) => {
        if (value !== String(val).trim()) return false;
        // Re-resolve in the live DOM — Raintree may have re-rendered the panel since scan
        const liveRadio =
          ((radio.id
            ? document.getElementById(radio.id)
            : null) as HTMLInputElement | null) ?? radio;
        const lblEl = (liveRadio
          .closest('.w-radio-button')
          ?.querySelector('label.custom-control-label') ??
          document.querySelector(
            `label[for="${liveRadio.id}"]`
          )) as HTMLElement | null;
        const wasReadonly = liveRadio.hasAttribute('readonly');
        if (wasReadonly) liveRadio.removeAttribute('readonly');
        bubbleFocus(liveRadio);
        if (lblEl) lblEl.click();
        if (!liveRadio.checked) {
          liveRadio.checked = true;
          liveRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (wasReadonly) liveRadio.setAttribute('readonly', '');
        return true;
      });
    }
  };
}

function buildPickerField(
  picker: Element,
  container: Element,
  subsectionName: string,
  fields: Record<string, any>
) {
  const items = Array.from(picker.querySelectorAll('.w-picker-item'));
  if (items.length === 0) return;

  const all = items
    .map((item) => item.querySelector('.text')?.textContent?.trim() || '')
    .filter(Boolean);

  const existing = items
    .filter(
      (item) =>
        (item.querySelector('input[type="checkbox"]') as HTMLInputElement)
          ?.checked
    )
    .map((item) => item.querySelector('.text')?.textContent?.trim() || '')
    .filter(Boolean);

  const label = resolveLabel(picker, container, subsectionName);

  fields[label] = {
    'Existing Value': existing.length > 0 ? existing.join(', ') : null,
    Description: `IMPORTANT: You MUST respond using ONLY exact values from the list below — do NOT paraphrase or invent new values. Separate multiple selections with '$'. If nothing applies, reply with exactly: None\nAvailable choices: ${all.join(' | ')}`,
    Input: (val: string | string[]) => {
      if (isEmptyOrNull(val as string)) return;
      const targets = Array.isArray(val)
        ? val
        : (val as string)
            .split('$')
            .map((v) => v.trim())
            .filter(Boolean);
      items.forEach((item) => {
        const text = item.querySelector('.text')?.textContent?.trim() || '';
        const checkbox = item.querySelector(
          'input[type="checkbox"]'
        ) as HTMLInputElement | null;
        if (
          targets.some((t) => t.toLowerCase() === text.toLowerCase()) &&
          !checkbox?.checked
        ) {
          (item as HTMLElement).click();
        }
      });
    }
  };
}

/**
 * Returns true if `target` matches `choiceText`.
 * Supports @@ as a wildcard for template choices (e.g. "Pain @@ out of 10").
 */
function matchesChoice(target: string, choiceText: string): boolean {
  // Normalize: lowercase, strip apostrophes/smart quotes, collapse whitespace
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/['''\u2018\u2019`]/g, '')
      .replace(/["""\u201C\u201D]/g, '"')
      .replace(/\s+/g, ' ')
      .trim();
  const buildWildcardRegex = (template: string) => {
    const escaped = template
      .replace(/<@@>/g, '\x00') // angle-bracket-wrapped marker first
      .replace(/@@/g, '\x00') // bare marker
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      .replace(/\x00/g, '.+');
    return new RegExp('^' + escaped + '$', 'i');
  };
  if (target.toLowerCase() === choiceText.toLowerCase()) return true;
  if (normalize(target) === normalize(choiceText)) return true;
  if (choiceText.includes('@@')) {
    if (buildWildcardRegex(choiceText).test(target)) return true;
    if (buildWildcardRegex(normalize(choiceText)).test(normalize(target)))
      return true;
  }
  return false;
}

function buildCheckboxListboxField(
  listbox: Element,
  container: Element,
  subsectionName: string,
  fields: Record<string, any>
): boolean {
  const rows = Array.from(listbox.querySelectorAll('tbody tr'));
  // Ignore extended-listbox row-selector checkboxes (.w-listbox-extended-checkbox);
  // only count checkboxes that are actual data fields.
  const hasCheckboxRows = rows.some((r) => {
    const chk = r.querySelector('input[type="checkbox"]');
    return chk && !chk.closest('.w-listbox-extended-checkbox');
  });
  if (!hasCheckboxRows) return false;

  const existing: string[] = [];
  const all: string[] = [];

  rows.forEach((row) => {
    const checkbox = row.querySelector(
      'input[type="checkbox"]'
    ) as HTMLInputElement | null;
    const cells = row.querySelectorAll('td');
    // Extended listboxes: td[0] is checkbox cell, td[1] is text
    const textCell = checkbox ? cells[1] : cells[0];
    const text = textCell?.textContent?.trim() || '';
    if (!text) return;
    all.push(text);
    if (checkbox?.checked) existing.push(text);
  });

  if (all.length === 0) return false;

  // Use the label resolving logic: find a label pointing to the listbox id,
  // or fall back to subsection name (panel caption)
  const label = resolveLabel(listbox, container, subsectionName);

  fields[label] = {
    'Existing Value': existing.length > 0 ? existing.join(', ') : null,
    Description: `Select all applicable from: ${all.join(', ')}. Provide '$' separated values.`,
    Input: (val: string | string[]) => {
      const targets = Array.isArray(val)
        ? val
        : val
            .split('$')
            .map((v) => v.trim())
            .filter(Boolean);
      rows.forEach((row) => {
        const checkbox = row.querySelector(
          'input[type="checkbox"]'
        ) as HTMLInputElement | null;
        const cells = row.querySelectorAll('td');
        const textCell = checkbox ? cells[1] : cells[0];
        const text = textCell?.textContent?.trim() || '';
        if (
          targets.some((t) => t.toLowerCase() === text.toLowerCase()) &&
          !checkbox?.checked
        ) {
          (row as HTMLElement).click();
        }
      });
    }
  };
  return true;
}

async function buildAddRowListboxField(
  listbox: Element,
  container: Element,
  subsectionName: string,
  fields: Record<string, any>
) {
  const addBtn = listbox.querySelector(
    '.w-listbox-toolbar-button.mdi-plus-box'
  ) as HTMLElement | null;
  if (!addBtn) return; // not an add-row listbox

  const label = resolveLabel(listbox, container, subsectionName);

  // ── During GET_DOM scan only: open picker to read available choices, then close ──
  // Skip during fill (answeringInProgress=true) to avoid double-opening the picker.
  let pickerChoices: string[] = [];
  if (!answeringInProgress) {
    const pickersBefore = new Set(
      Array.from(document.querySelectorAll('.w-picker'))
    );
    const pckInnerBeforeA = document.querySelector(
      '[data-widgetname="pckselector"] .w-picker'
    );
    const pckWasOpenBeforeA =
      pckInnerBeforeA?.classList.contains('openview') ?? false;
    addBtn.click();
    let waited = 0;
    let picker: Element | null = null;
    while (waited < 2000) {
      for (const p of Array.from(document.querySelectorAll('.w-picker'))) {
        if (
          !pickersBefore.has(p) &&
          p.querySelectorAll('.w-picker-item').length > 0
        ) {
          picker = p;
          break;
        }
      }
      if (picker) break;
      // pckselector: same .w-picker gains openview class when screen becomes active
      const pckInnerNowA = document.querySelector(
        '[data-widgetname="pckselector"] .w-picker'
      ) as Element | null;
      if (
        pckInnerNowA &&
        !pckWasOpenBeforeA &&
        pckInnerNowA.classList.contains('openview') &&
        pckInnerNowA.querySelectorAll('.w-picker-item').length > 0
      ) {
        picker = pckInnerNowA;
        break;
      }
      await new Promise((r) => setTimeout(r, 100));
      waited += 100;
    }
    if (picker) {
      pickerChoices = Array.from(
        picker.querySelectorAll('.w-picker-item .text')
      )
        .map((el) => el.textContent?.trim() || '')
        .filter(Boolean);
      const backBtn = (document.querySelector('.backdown .backbtn') ??
        document.querySelector('[id^="backbutton-"]') ??
        document.querySelector('.backbtn')) as HTMLElement | null;
      if (backBtn) {
        backBtn.click();
      } else {
        document.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Escape',
            bubbles: true,
            cancelable: true
          })
        );
      }
      await new Promise((r) => setTimeout(r, 500));
    } else {
      console.warn(
        `[Raintree] buildAddRowListboxField: picker did not open for "${label}"`
      );
      const pckInnerStrayA = document.querySelector(
        '[data-widgetname="pckselector"] .w-picker'
      ) as HTMLElement | null;
      if (
        pckInnerStrayA &&
        !pckWasOpenBeforeA &&
        pckInnerStrayA.classList.contains('openview')
      ) {
        const strayBackA = (document.querySelector('.backdown .backbtn') ??
          document.querySelector('[id^="backbutton-"]') ??
          document.querySelector('.backbtn')) as HTMLElement | null;
        strayBackA?.click();
        await new Promise((r) => setTimeout(r, 300));
      }
    }
  }

  const existingRows = Array.from(listbox.querySelectorAll('tbody tr'))
    .map((row) =>
      Array.from(row.querySelectorAll('td'))
        .map((td) => td.textContent?.trim() || '')
        .filter(Boolean)
        .join(' | ')
    )
    .filter(Boolean);

  const batchKey = `${subsectionName}.${label}`;
  // Capture the listbox ID now so the flush can find it after DOM remounts
  const listboxId = listbox.getAttribute('id') || '';

  // Register the flush function for this field (overwrites on each rebuild with fresh element refs)
  _pickerBatchFlushers.set(batchKey, async () => {
    const targets = _pickerBatch.get(batchKey) ?? [];
    _pickerBatch.delete(batchKey);
    if (targets.length === 0) return;

    // Re-query the listbox — first try by ID, then fall back to panel scan
    let freshListbox: Element | null = listboxId
      ? document.getElementById(listboxId)
      : null;
    if (!freshListbox) {
      // Listbox not in DOM — its sub-tab may not be active. Activate it by aria-label or span text.
      const tabItem = (
        Array.from(
          document.querySelectorAll('.w-tab-control-tabs .tab-item')
        ) as HTMLElement[]
      ).find(
        (t) =>
          (t.getAttribute('aria-label')?.trim() ||
            t.querySelector('span')?.textContent?.trim()) === subsectionName
      );
      if (tabItem && !tabItem.classList.contains('active')) {
        tabItem.click();
        await new Promise((r) => setTimeout(r, 400));
      }
      freshListbox = listboxId ? document.getElementById(listboxId) : null;
    }
    if (!freshListbox) {
      // Last resort: search panels
      for (const panel of Array.from(document.querySelectorAll('.w-panel'))) {
        if (panelCaptionName(panel) !== subsectionName) continue;
        freshListbox = panel.querySelector('.w-listbox');
        break;
      }
    }
    let freshBtn = freshListbox?.querySelector(
      '.w-listbox-toolbar-button.mdi-plus-box'
    ) as HTMLElement | null;
    if (!freshBtn) {
      console.warn(
        `[Raintree] pickerFlush: addBtn not found for "${batchKey}"`
      );
      return;
    }

    // Open picker
    const pickersBefore = new Set(
      Array.from(document.querySelectorAll('.w-picker'))
    );
    const pckInnerBeforeF = document.querySelector(
      '[data-widgetname="pckselector"] .w-picker'
    );
    const pckWasOpenBeforeF =
      pckInnerBeforeF?.classList.contains('openview') ?? false;
    freshBtn.click();
    let waited = 0;
    let picker: Element | null = null;
    while (waited < 2000) {
      for (const p of Array.from(document.querySelectorAll('.w-picker'))) {
        if (
          !pickersBefore.has(p) &&
          p.querySelectorAll('.w-picker-item').length > 0
        ) {
          picker = p;
          break;
        }
      }
      if (picker) break;
      // pckselector: same .w-picker gains openview class when screen becomes active
      const pckInnerNowF = document.querySelector(
        '[data-widgetname="pckselector"] .w-picker'
      ) as Element | null;
      if (
        pckInnerNowF &&
        !pckWasOpenBeforeF &&
        pckInnerNowF.classList.contains('openview') &&
        pckInnerNowF.querySelectorAll('.w-picker-item').length > 0
      ) {
        picker = pckInnerNowF;
        break;
      }
      await new Promise((r) => setTimeout(r, 100));
      waited += 100;
    }
    if (!picker) {
      console.warn(
        `[Raintree] pickerFlush: picker did not open for "${batchKey}"`
      );
      const pckInnerStrayF = document.querySelector(
        '[data-widgetname="pckselector"] .w-picker'
      ) as HTMLElement | null;
      if (
        pckInnerStrayF &&
        !pckWasOpenBeforeF &&
        pckInnerStrayF.classList.contains('openview')
      ) {
        const strayBackF = (document.querySelector('.backdown .backbtn') ??
          document.querySelector('[id^="backbutton-"]') ??
          document.querySelector('.backbtn')) as HTMLElement | null;
        strayBackF?.click();
        await new Promise((r) => setTimeout(r, 300));
      }
      return;
    }

    // Select all queued targets in one pass
    const items = Array.from(picker.querySelectorAll('.w-picker-item'));
    let matched = 0;
    for (const item of items) {
      const text = item.querySelector('.text')?.textContent?.trim() || '';
      const checkbox = item.querySelector(
        'input[type="checkbox"]'
      ) as HTMLInputElement | null;
      if (targets.some((t) => matchesChoice(t, text)) && !checkbox?.checked) {
        checkbox?.click();
        matched++;
        await new Promise((r) => setTimeout(r, 80));
      }
    }

    // Save commits the selections; back discards them
    const saveBtn = document.querySelector(
      '.w-picker-toolbar .mdi-content-save:not(.disabled)'
    ) as HTMLElement | null;
    if (saveBtn) {
      saveBtn.click();
    } else {
      const backBtn = (document.querySelector('.backdown .backbtn') ??
        document.querySelector('[id^="backbutton-"]') ??
        document.querySelector('.backbtn') ??
        document.querySelector('.backdown')) as HTMLElement | null;
      backBtn?.click();
    }
    // Wait for picker to close
    let closeWaited = 0;
    while (closeWaited < 2000) {
      await new Promise((r) => setTimeout(r, 100));
      closeWaited += 100;
      const pckCheck = document.querySelector(
        '[data-widgetname="pckselector"] .w-picker'
      );
      if (!pckCheck?.classList.contains('openview')) break;
    }
    await new Promise((r) => setTimeout(r, 300));
  });

  fields[label] = {
    'Existing Value': existingRows.length > 0 ? existingRows.join(', ') : null,
    Description:
      pickerChoices.length > 0
        ? `IMPORTANT: You MUST respond using ONLY exact values from the list below — do NOT paraphrase or invent new values. ` +
          `Only select items that were ACTUALLY performed or discussed in the session. ` +
          `Do NOT select an item merely because it sounds similar — if no choice precisely matches what was done, omit it rather than picking incorrectly. ` +
          `Values containing "@@ " are templates — use them EXACTLY as shown, keeping "@@ " as-is (do NOT replace @@ with actual text). ` +
          `Separate multiple selections with '$'. If nothing applies, reply with exactly: None\nAvailable choices: ${pickerChoices.join(' , ')}`
        : `Add rows by providing comma-separated values.`,
    Input: (val: string | string[]) => {
      if (isEmptyOrNull(val)) return;
      const newTargets = Array.isArray(val)
        ? val
        : String(val)
            .split('$')
            .map((v) => v.trim())
            .filter(Boolean);
      if (!_pickerBatch.has(batchKey)) _pickerBatch.set(batchKey, []);
      _pickerBatch.get(batchKey)!.push(...newTargets);
    }
  };
}

/**
 * Handles row-list listboxes (Goals, Chief Complaints) that have an Edit (mdi-pen) toolbar button.
 * Each row is a form with multiple columns; new rows are added via a picker.
 * Returns true if this listbox was handled as a row-list.
 */
/**
 * Handles row-list listboxes (Goals, Chief Complaints) that have an Edit (mdi-pen) toolbar button.
 * Structure mirrors WebPT1 goals: Existing Value is an array of row objects, each with named column
 * fields. The AI responds with an array of the same shape; each element is processed row by row.
 * Returns true if this listbox was handled as a row-list.
 */
async function buildRowListField(
  listbox: Element,
  container: Element,
  subsectionName: string,
  fields: Record<string, any>
): Promise<boolean> {
  if (!listbox.querySelector('.w-listbox-toolbar-button.mdi-pen')) return false;

  const addBtn = listbox.querySelector(
    '.w-listbox-toolbar-button.mdi-plus-box'
  ) as HTMLElement | null;

  const label = resolveLabel(listbox, container, subsectionName);

  // Column headers — skip checkbox and toolbar header cells
  const headers = Array.from(listbox.querySelectorAll('thead th'))
    .filter(
      (th) =>
        !th.classList.contains('w-listbox-extended-checkbox') &&
        !th.classList.contains('w-listbox-toolbar-header')
    )
    .map((th) => th.textContent?.trim() || '')
    .filter(Boolean);

  // Existing rows as array of { columnName: value } objects — same shape as the AI answer
  const existingRowData: Record<string, string>[] = Array.from(
    listbox.querySelectorAll('tbody tr')
  )
    .map((row) => {
      const cells = Array.from(row.querySelectorAll('td')).filter(
        (td) =>
          !td.classList.contains('w-listbox-extended-checkbox') &&
          !td.classList.contains('w-listbox-toolbar-cell')
      );
      const obj: Record<string, string> = {};
      headers.forEach((h, i) => {
        obj[h] = cells[i]?.textContent?.trim() || '';
      });
      return obj;
    })
    .filter((obj) => Object.values(obj).some((v) => v));

  // During GET_DOM scan: open picker to read available choices, then close
  let pickerChoices: string[] = [];
  if (!answeringInProgress && addBtn) {
    const pickersBefore = new Set(
      Array.from(document.querySelectorAll('.w-picker'))
    );
    // pckselector's inner .w-picker is always in DOM; detect activation via openview class
    const pckInnerBefore = document.querySelector(
      '[data-widgetname="pckselector"] .w-picker'
    );
    const pckWasOpenBefore =
      pckInnerBefore?.classList.contains('openview') ?? false;
    addBtn.click();
    let waited = 0;
    let picker: Element | null = null;
    while (waited < 2000) {
      // Standard .w-picker overlay (new element added to DOM)
      for (const p of Array.from(document.querySelectorAll('.w-picker'))) {
        if (
          !pickersBefore.has(p) &&
          p.querySelectorAll('.w-picker-item').length > 0
        ) {
          picker = p;
          break;
        }
      }
      if (picker) break;
      // pckselector: same .w-picker element gains openview class when screen becomes active
      const pckInnerNow = document.querySelector(
        '[data-widgetname="pckselector"] .w-picker'
      ) as Element | null;
      if (
        pckInnerNow &&
        !pckWasOpenBefore &&
        pckInnerNow.classList.contains('openview') &&
        pckInnerNow.querySelectorAll('.w-picker-item').length > 0
      ) {
        picker = pckInnerNow;
        break;
      }
      await new Promise((r) => setTimeout(r, 100));
      waited += 100;
    }
    if (picker) {
      pickerChoices = Array.from(
        picker.querySelectorAll('.w-picker-item .text')
      )
        .map((el) => el.textContent?.trim() || '')
        .filter(Boolean);
      // Click the inner backbtn <i> specifically — clicking the .backdown container div
      // may not trigger Raintree's event handler
      const backBtn = (document.querySelector('.backdown .backbtn') ??
        document.querySelector('[id^="backbutton-"]') ??
        document.querySelector('.backbtn')) as HTMLElement | null;
      if (backBtn) {
        backBtn.click();
      } else {
        document.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Escape',
            bubbles: true,
            cancelable: true
          })
        );
      }
      await new Promise((r) => setTimeout(r, 500));
    } else {
      console.warn(
        `[Raintree] buildRowListField: picker did not open for "${label}"`
      );
      // If pckselector opened but had no items, close it to avoid getting stuck
      const pckInnerStray = document.querySelector(
        '[data-widgetname="pckselector"] .w-picker'
      ) as HTMLElement | null;
      if (
        pckInnerStray &&
        !pckWasOpenBefore &&
        pckInnerStray.classList.contains('openview')
      ) {
        const strayBack = (document.querySelector('.backdown .backbtn') ??
          document.querySelector('[id^="backbutton-"]') ??
          document.querySelector('.backbtn')) as HTMLElement | null;
        strayBack?.click();
        await new Promise((r) => setTimeout(r, 300));
      }
    }
  }

  const listboxId = listbox.getAttribute('id') || '';
  const batchKey = `${subsectionName}.${label}`;

  // Flush: process each queued row object in order.
  // The fill phase may have navigated to a different sub-tab, leaving this listbox
  // out of the DOM. Re-activate the sub-tab by its label before filling.
  _pickerBatchFlushers.set(batchKey, async () => {
    const targets = (_pickerBatch.get(batchKey) ?? []) as Record<
      string,
      string
    >[];
    _pickerBatch.delete(batchKey);
    if (targets.length === 0) return;

    if (listboxId && !document.getElementById(listboxId)) {
      const tabItem = (
        Array.from(
          document.querySelectorAll('.w-tab-control-tabs .tab-item')
        ) as HTMLElement[]
      ).find(
        (t) =>
          (t.getAttribute('aria-label')?.trim() ||
            t.querySelector('span')?.textContent?.trim()) === subsectionName
      );
      if (tabItem && !tabItem.classList.contains('active')) {
        tabItem.click();
        await new Promise((r) => setTimeout(r, 500));
      }
    }

    const rows = isComplaintsOrPrecautions
      ? _dedupeListRows(targets, headers[0], existingRowData)
      : targets;
    for (const rowObj of rows) {
      await fillRowListRow(listboxId, rowObj, headers, false, isGoals && (isDailyNote || isDischarge));
    }
  });

  // Build the list-style Comprehend structure the backend expects.
  // Each element is a row object where every column is a field using ONLY the valid backend keys:
  //   "Existing Value", "Description", "Choices", "Type"
  // Choices are attached ONLY to the first row (prototype). Cap at 499 to stay within OpenAI's
  // 500-item enum limit (backend appends one sentinel, making the total exactly 500).
  const MAX_PICKER_CHOICES = 499;
  const cappedPickerChoices = pickerChoices.slice(0, MAX_PICKER_CHOICES);
  const isGoals = label.toLowerCase().includes('goal');
  const isComplaintsOrPrecautions =
    /chief complaint|contraindication|precaution/i.test(label);
  const isActivities =
    label.toLowerCase().includes('activit') ||
    label.toLowerCase().includes('exercis') ||
    subsectionName.toLowerCase().includes('activit')
  const isServiceBased =
    label.toLowerCase().includes('modali') ||
    subsectionName.toLowerCase().includes('modali');

  const _noteType = (Array.from(document.querySelectorAll('label[for*="notetype"]'))
    .find((el) => el.textContent?.trim() && el.textContent.trim() !== 'Note Type') as HTMLElement | null)
    ?.textContent?.trim() ?? '';
  const isInitialEval  = /initial\s*eval/i.test(_noteType);
  const isProgressNote = /progress|re.?eval/i.test(_noteType);
  const isDischarge    = /discharge/i.test(_noteType);
  const isDailyNote    = /daily/i.test(_noteType);

  // Columns the AI should never see or fill (dates, provider, special).
  const _shouldSkipColumn = (h: string) =>
    /^date\s*\d*$/i.test(h.trim()) || /^provider$/i.test(h.trim()) || /^special$/i.test(h.trim());
  const _isCommentColumn = (h: string) => /^comment|^note/i.test(h.trim());
  const _isResultColumn  = (h: string) => /^result$/i.test(h.trim());
  const _isTimeColumn    = (h: string) => /^time$/i.test(h.trim());

  // Time-based (exercise) CPT codes.
  const _EXERCISE_CPT_CHOICES = [
    'Aquatic (97113) - [TP003]',
    'Cognitive Retraining (97532) - [TP041]',
    'FCE (97750) - [TP022]',
    'HEP/Pt Education/SelfCare Management (97535) - [TP012]',
    'Manual Therapy (97140) - [TP007]',
    'Neuro Re-Ed (97112) - [TP002]',
    'Orthotic Mgmt Training (97763) - [TP040]',
    'Prosthetic Management Training (97761) - [TP025]',
    'Sensory Integration (97533) - [TP011]',
    'Therapeutic Activity (97530) - [TP009]',
    'WC Management (97542) - [TP014]',
    'Caregiver Training Intl 30m (97550) - [TP150]',
    'Behav & QL Analysis of Voice (92524) - [TE018]',
    'SP Cog Skill Therapeutic Addl 15m (97130) - [TP052]',
    'Speech/Hearing (92508) - [TP036]',
    'Evaluate Swallowing Function (92610) - [TE008]',
    'Checkout for P&O - established pt. - (97762) - [TP026]',
    'E-Stim- Attended (97032) - [TM010]',
    'Gait Training (97116) - [TP004]',
    'Iontophoresis (97033) - [TP044]',
    'Massage Therapy (97124) - [TP005]',
    'No Charge',
    'Orthotic Mgt (97760) - [TP024]',
    'Prosthetic Mgt (97000) - [TP000]',
    'Ther-Ex (97110) - [TP001]',
    'Ultrasound (97035) - [TM045]',
    'Wound Care (97602) - [TP019]',
    'Speech Sound Prod w/Lang Eval (92523) - [TE017]',
    'SP Cog Skill Therapeutic Intl 15m (97129) - [TP051]',
    'Speech/Hearing Therapy (92507) - [TP035]',
    'Oral Function Therapy (92526) - [TP043]',
    'Hot or Cold Packs Therapy (97010) - [TM001]',
    'Vestibular Rehab Prog (S9476) - [TP048]',
    'PHYSICAL PERFORMANCE TEST (97750) - [TP022]',
    'Re-Eval Minutes',
    'Canalith Repositioning (95992) - [TP047]',
    'Initial Eval Minutes'
  ];

  // Service-based (modality) CPT codes.
  const _SERVICE_CPT_CHOICES = [
    'Hot/Cold Pack (97010) - [TM001]',
    'Mechanical Traction (97012) - [TM002]',
    'E-Stim (un) (97014) [TM003]',
    'Vaso Device (97016) [TM004]',
    'Paraffin Bath (97018) [TM005]',
    'Whirlpool (97022) [TM006]',
    'E-Stim (att) (97032) - [TM010]',
    'Diathermy (97024) [TM007]',
    'Infrared (97026) [TM008]',
    'Ultrasound (97035) [TM035]',
    'Contrast Bath (97034) - [TM012]',
    'Ionto (97033) [TM044]',
    'Wound care (97602) [TM602]',
    'Canalith Repositioning (95992) - [TP047]',
    'Application of Finger Cast (29086) - [TP049]',
    'Elbow (L3763) - [TS007]',
    'Vestibular Rehabilitation (S97476) - [TP048]',
    'Speech Sound Prod w/Lang Eval (92523) - [TE017]',
    'Behav & QL Analysis of Voice (92524) - [TE018]',
    'SP Cog Skill Therapeutic Intl 15m (97129) - [TP051]',
    'SP Cog Skill Therapeutic Addl 15m (97130) - [TP052]',
    'Speech/Hearing Therapy (92507) - [TP035]',
    'Evaluate Swallowing Function (92610) - [TE008]',
    'Speech/Hearing Therapy Group (92508) - [TP036]',
    'Oral Function Therapy (92526) - [TP043]',
    'Caregiver Training Intl 30m (97550) - [TP150]'
  ];

  // Service-based (modality) picklist for the "Service Based, HEP, Notes To Treating
  // Therapist" table. Hardcoded to mirror the live Raintree Modalities picker.
  const _SERVICE_BASED_ACTIVITY_CHOICES: string[] = [
    'Canalith Repositioning',
    'Caregiver Education',
    'Cold Laser',
    'cold pack',
    'Contrast Bath',
    'Cryotherapy',
    'DC stimulation',
    'E-Stim (unattended)',
    'EMS',
    'Eval - Speech Sound Prod w/ Lang',
    'Eval - Swallow',
    'Fluidotherapy',
    'Infrared',
    'Mechanical Traction',
    'Moist Heat',
    'Paraffin',
    'Speech Therapy',
    'Speech Therapy - Group',
    'Splint',
    'Swallow Therapy',
    'Vaso Device',
    'Vestibular Rehabilitation',
    'Whirlpool',
    'Wound care'
  ];

  // Time-based (exercise) picklist for the "Time-Based" / Exercises table. Hardcoded to
  // mirror the live Raintree exercise picker so the Activities table always has a reliable,
  // exact-match choice list rather than depending solely on runtime-scraped
  // cappedPickerChoices. NOTE: this list applies ONLY to the time-based exercise table —
  // service-based modalities use _SERVICE_BASED_ACTIVITY_CHOICES above.
  const _TIME_BASED_ACTIVITY_CHOICES: string[] = [
  "1/2 kneel lifts",
  "1/2 Kneeling Dorsiflexion Mobilization",
  "1/2 Kneeling HS to Hip Flexor Strech",
  "1/2 Kneeling Landmine Press",
  "1/2 Kneeling Lat Pull down",
  "1/2 kneeling mace chops",
  "1/2 Kneeling Sport Band Scapular Depression",
  "1st toe stretch",
  "1st Toe TB Resisted Extension",
  "1st toe Tb resisted flexion",
  "3 Way Bicep Curl",
  "3 Way SLR",
  "4 Way TB ankle",
  "4-way Cane AAROM",
  "5-Star Stretch",
  "90/90 table top lift",
  "AAROM Elbow Flexion",
  "AAROM Hip",
  "AAROM Supine Press",
  "AAROM- Cervical/Thoracic Spine",
  "ABC c/ Ball",
  "Abdominal Machine",
  "Abdominal Sets",
  "Adductor Eccentric",
  "ADL - Bed Mobility",
  "ADL - Box Carry",
  "ADL - Dressing Practice",
  "ADL - Transfer Training",
  "Agility Ladder",
  "Airex Balance",
  "airplanes",
  "alt clams, supine",
  "alternating glute sets",
  "Ambulation with Ball Toss",
  "Ambulation with Horizontal Tracking",
  "Andi exercise",
  "Angel Wings",
  "Anke alphabet",
  "Ankle CARs",
  "Ankle Circles",
  "Ankle DF c/ KB",
  "Ankle DF/inversion Diagonal",
  "Ankle PF/Inversion Diagonal",
  "ankle pumps",
  "apley IR stretch",
  "Aquatic- Noodle DKTC",
  "Aquatic- PPT w/march at wall",
  "Aquatic-Alt UE/LE",
  "Aquatic-Ankle 4 way",
  "Aquatic-Biceps/Triceps",
  "Aquatic-Corner Stetch",
  "Aquatic-Current SLS",
  "Aquatic-Current Swimming",
  "Aquatic-Gastroc/Soleus Stretch",
  "Aquatic-Hamstring Stretch",
  "Aquatic-Heel Raises",
  "Aquatic-Heel Slides against wall",
  "Aquatic-Hip 4-way",
  "Aquatic-Hip IR/ER",
  "Aquatic-Jogging",
  "Aquatic-Kickboard Push Downs",
  "Aquatic-Kickboard push/pull",
  "Aquatic-Kickboard rotation",
  "Aquatic-Noodle cycle",
  "Aquatic-Noodle Hang",
  "Aquatic-Noodle Leg Press",
  "Aquatic-Noodle Scissor",
  "Aquatic-Noodle Ski",
  "Aquatic-Pelvic Tilts against wall",
  "Aquatic-Piriformis Stretch",
  "Aquatic-Quad Stretch",
  "Aquatic-Seated Knee Extension",
  "Aquatic-Shoulder Abduction/Adduction",
  "Aquatic-Shoulder Flexion/Extension",
  "Aquatic-Shoulder IR/ER",
  "Aquatic-Shouler Hor Abd/Add",
  "Aquatic-Sidestepping",
  "Aquatic-SKTC",
  "Aquatic-SLS",
  "Aquatic-Squats",
  "Aquatic-Standing Knee Flexion",
  "Aquatic-Standing Marching",
  "Aquatic-Step down large step",
  "Aquatic-Step down small step",
  "Aquatic-Step Up large step",
  "Aquatic-Step up small step",
  "Aquatic-Toe Raises",
  "Aquatic-Walking Forward",
  "Aquatic-Wall Stretch",
  "Aquautic-Walking Backward",
  "arav exercise sepcial",
  "Arch push ups",
  "Arm bar",
  "Arm Bars EC",
  "Arm Circle Circuit",
  "Arm Rotation with Core Stabilization",
  "Arnold Press",
  "AROM ankle DF/PF",
  "AROM ankle INV/EV",
  "AROM Dorsi-Flexion",
  "AROM Eversion",
  "AROM Inversion",
  "AROM Plantar-Flexion",
  "Arom toe flexion/extension",
  "AROM- 4 Way SLR",
  "AROM- Finger[s]",
  "AROM- LAQ's",
  "AROM- SAQ",
  "AROM- Single Knee to Chest",
  "AROM- Towel wall flexion",
  "AROM- Triceps Extension",
  "AROM-Cervical Extension",
  "AROM-Cervical Flexion",
  "AROM-Cervical Rotation",
  "AROM-Cervical SB",
  "AROM-Elbow Extension",
  "AROM-Elbow Flexion",
  "AROM-Forearm Pronation/Supination",
  "AROM-Forearm Supination",
  "AROM-Hip Abduction",
  "AROM-Hip Adduction",
  "AROM-Hip ER",
  "AROM-Hip Extension",
  "AROM-Hip Flexion",
  "AROM-Hip IR",
  "AROM-Knee Extension",
  "AROM-Knee Flexion",
  "AROM-Shoulder Abduction",
  "AROM-Shoulder Adduction",
  "AROM-Shoulder ER @ 90 deg",
  "AROM-Shoulder ER in Neutral",
  "AROM-Shoulder Extension",
  "AROM-Shoulder Flexion",
  "AROM-Shoulder IR @ 90 deg",
  "AROM-Shoulder IR in Neutral",
  "AROM-Trunk Extension",
  "AROM-Trunk Flexion",
  "AROM-Trunk Rotation",
  "AROM-Wrist Extension",
  "AROM-Wrist Flexion",
  "AROM-Wrist RD",
  "AROM-Wrist UD",
  "Axis Board Configuration 8",
  "Axis Board- Diagonal Stabilization",
  "Axis Board- Fore Foot Focus",
  "Axis Board- Medial Stabilization",
  "Axis Board- Rear Foot Focus",
  "Back Ed Training: Lifting/Squat Mechanics",
  "Back Ed Training: Pushing/ Pulling",
  "Back Ed Training: Rotation",
  "Back Ed Training: Sitting Posture",
  "Back Ed Training: Sleep Positioning",
  "Back Ed Training: Standing Posture",
  "Balance Beam-1/2 Foam Roller",
  "Balance Board - A/P",
  "Balance Board - M/L",
  "Balance on Foam Mat",
  "Balance- DynaDisc Ex",
  "Balance- EQ Bd: A/P, M/L",
  "Balance- Grape vine",
  "Balance- Parallel Stance Eyes Closed",
  "Balance- Parallel Stance Eyes Open",
  "Balance- SLS",
  "Balance- Tandem Stance",
  "Balance- Two Track",
  "Balance-Foam-Parallel Stance",
  "Balance-Foam-SLB",
  "Balance-Foam-Tandem Stance",
  "Balance-SL LE- 2 cone touch",
  "Balance-SL LE- 4 cone touch",
  "Balance-SL UE- 2 cone touch",
  "Balance-SL UE- 4 cone touch",
  "Balance-Tandem walking- fd/bk",
  "Ball Bridge",
  "Ball Circles - Wall",
  "Ball LIft",
  "Ball Pass behind the back",
  "Ball Roll Ups - Wall",
  "Ball Toss forward",
  "Banded Good Mornings",
  "Banded Sissy Planks",
  "BAPS-Circles",
  "BAPS-Diagonals",
  "BAPS-Inv/Ev",
  "BAPS-PF/DF",
  "BAPS-PRE's",
  "BAPS-Standing",
  "basic shoudler movement and isometric combination",
  "BB Bent Over Rows",
  "BB Deadlifts",
  "BB Hip Thrusters",
  "BB RDLs",
  "Bear Crawl shoulder stability",
  "Bench Press",
  "Bent Over Row",
  "Bicep Isometrics",
  "Biceps Curls- DB",
  "Bike- Recumbent",
  "Bike- Stationary",
  "Bilateral ER w/scap retarction",
  "Biodex - CPM",
  "Biodex Ankle Inversion/Eversion",
  "Biodex Ankle Plantar/Dorsiflexion",
  "Biodex Balance",
  "Biodex Balance System LOS Easy",
  "Biodex Balance System LOS Hard",
  "Biodex Balance System LOS Moderate",
  "Biodex Balance System Moderate",
  "Biodex Balance System Static",
  "Biodex Balance System Weight Shift",
  "Biodex Knee Flexion/Extension",
  "Biodex Shoulder Flexion/Extension",
  "Biodex Shoulder Internal External Rotation",
  "Biosway- LOS",
  "Biosway- Maze",
  "Biosway- Wt Bearing",
  "Biosway- Wt Shifting",
  "Birddog",
  "BKFO",
  "BL Kb carry",
  "blank",
  "Body Ball Passive CW CCW",
  "Body Blade",
  "Body Blade at Side",
  "Body Blade Extension Arc",
  "Body Blade Flexion RC",
  "Body Blade in 90 Degree Adb",
  "Body Blade in 90 Degree Flex",
  "Body Blade SLS",
  "Body on Head Turns",
  "BOSU (Ball Down)-2 Foot Stance Ball Toss",
  "BOSU (Ball Down)-Single Leg Squat",
  "BOSU (Ball Down)-SLB Ball Toss",
  "BOSU (Ball Down)-SLB Eyes Closed",
  "BOSU (Ball Down)-SLB Eyes Open",
  "BOSU (Ball Down)-Squats",
  "BOSU (Ball Up)-2 Foot Stance Ball Toss",
  "BOSU (Ball Up)-Lunges",
  "BOSU (Ball Up)-Single Leg Squat",
  "BOSU (Ball Up)-SLB Ball Toss",
  "BOSU (Ball Up)-SLB Eyes Closed",
  "BOSU (Ball Up)-SLB Eyes Open",
  "BOSU (Ball Up)-Squats",
  "BOSU Jogging",
  "Bosu Lunges",
  "Bosu Lunges: Forward",
  "Bosu Lunges: Lateral",
  "BOSU push ups",
  "BOSU Sitting",
  "Bottoms Up 90-90 KB Carry",
  "bottoms up KB carry",
  "Bounce Back Ball Throw",
  "Boxing",
  "Bracing",
  "Bracing w/ Heel Slides",
  "Bracing With Leg Extension",
  "Bracing With Marching",
  "Bracing- Clamshell fall outs",
  "Brandt-Daroff Exercises",
  "Breathing Exercises Supine",
  "Bridge w/ HS Sliders",
  "Bridging w/LE on Ther Ball",
  "Bridging- Hip Abd/Add",
  "Bridging- Marching",
  "Bridging- PF/DF",
  "Bridging- Traditional",
  "Bridging-Single Leg",
  "Broad Jumps",
  "BTE Key Turning",
  "BTE Large Knob Turning",
  "BTE Pinch",
  "BTE Pronation Supination",
  "BTE Pump Handle",
  "BTE Screw Driver",
  "BTE Small Knob Turning",
  "BTE Wrist Flexion/Extension",
  "BTE- Driver Steering Wheel",
  "BTE- Gripper",
  "BTE- Rope Climbing",
  "Bulgarian Split Squats",
  "Butt Kicks",
  "Cable Column Forward & Retrowalking",
  "Cable Column Forward Walking",
  "Cable Column Lateral Walking",
  "Cable Column retro walking",
  "Cable Column Rows",
  "Cable Column Terminal Knee Extension (TKE)",
  "Cable Column- 4 Way SLR",
  "Cable Column- Lat Pulldown",
  "Cable Column- Punches",
  "Cable Column-Biceps Curl",
  "Cable Column-Forward Punch",
  "Cable Column-Lateral Pull Down",
  "Cable Column-Shoulder Abd",
  "Cable Column-Shoulder Add",
  "Cable Column-Shoulder Diagonal 1",
  "Cable Column-Shoulder Diagonal 2",
  "Cable Column-Shoulder ER",
  "Cable Column-Shoulder Ext",
  "Cable Column-Shoulder Flexion",
  "Cable Column-Shoulder Horiz Abd",
  "Cable Column-Shoulder Horiz Add",
  "Cable Column-Shoulder IR",
  "Cable Column-Triceps Ext",
  "Calf Raise (Bilateral) on Floor",
  "Calf Raise (Bilateral) on Step",
  "Calf Raise (Single Leg) on Floor",
  "Calf Raise (Single Leg) on Step",
  "Calf Raises on Leg Press",
  "Calf stretch with strap",
  "Cane-Shoulder Abduction",
  "Cane-Shoulder Chest Press",
  "Cane-Shoulder D1/D2",
  "Cane-Shoulder ER in Neutral",
  "Cane-Shoulder Extension",
  "Cane-Shoulder Flexion",
  "Cane-Shoulder Horiz Abd/Add",
  "Cane-Shoulder IR in Neutral",
  "Cane-Shoulder IR/ER in Abduction",
  "Cane-ShoulderIR/Add-Behind the Back",
  "car lift",
  "cara exercise special",
  "Caregiver Education",
  "Cariocas",
  "Cat/Camel stretching",
  "cc 1/2 kneeling single arm rows",
  "CC Bicep Curl",
  "cc facepulls",
  "CC Hip Extension",
  "CC RDL (Rope Pull hroughs)",
  "cc rows",
  "CC SA Chest Press",
  "CC SA Row",
  "CC TKE",
  "cc tricep extensions",
  "CC Tricep Pushdowns",
  "Cervical Extension SNAGs",
  "Cervical Flexion Supine (Stabilization)",
  "Cervical Flexion with Rotation",
  "Cervical Lower Trap Stab",
  "Cervical Retractions",
  "Cervical Traction c/ Towel",
  "chair dips",
  "Chair Push Ups",
  "Chair: sit to stand",
  "charmil palloff scpecial",
  "Cheerleaders",
  "Cherry Pickers",
  "childs pose stretch",
  "chin tucks (seated)",
  "Chin Ups",
  "chops @ CC",
  "clamshells",
  "Clamshells w/ Reverse Clamshells",
  "Clean and Press",
  "Close Grip Push Up",
  "Codmans Circles",
  "Codmans Flex/Ext",
  "Codmans Horiz Abd/Add",
  "compound strength circuit",
  "Convergence drill",
  "Copenhagen Planks",
  "Core Side Bending @ CC",
  "core stab",
  "Corrective Stretching",
  "Cross Body Oblique Sets c/ PB",
  "CS Flex/Ext/SB/Rot ISOS",
  "CS Rotation SNAG",
  "Cue step ambulation exercise",
  "Cybex YW",
  "DB Bench Press",
  "DB Bent Over Rows",
  "DB Chest Press",
  "DB Hammer Curls",
  "DB ISOS Hold",
  "DB Lawnmowers",
  "DB OH Press",
  "DB Radial Deviation",
  "DB Shoulder Abduction",
  "DB Shoulder Flexion",
  "DB Standing Side Crunch",
  "DB Tricep Extension",
  "db wrist extension",
  "DB wrist flexion",
  "dead bugs",
  "Dead Hang",
  "Dead lifts",
  "Deceleration Drills",
  "Decline Fly",
  "decline squats",
  "Depth Squats",
  "Diagonal Lift",
  "Diaphragmatic Breathing",
  "digi flex individual finger flexion",
  "DKTC- Double Knees to Chest",
  "DNF Lift",
  "Donkey Kicks",
  "Doorway Pec Stretch",
  "Dot Drill",
  "Dowel Pronation/Supination",
  "dowel radial deviation",
  "dowel rotation",
  "Dowel Ulnar Deviation",
  "Down Dog Toe Taps",
  "Dual 400 Hams",
  "Dual 400 Quads",
  "Dual 500 Shoulder Press",
  "Dual 500 UT",
  "Dual 600 Trunk Extensions",
  "Dumbbell Bench",
  "dumbell wrist extension",
  "Dynamic Plank to Downdog",
  "Dynamic Walking Quad stretch",
  "Dynamic Warm Up",
  "Dynastick",
  "E-Stim(Attended)",
  "eccentric calf raises",
  "Eccentric DB Wrist",
  "Eccentric Gastroc Heel Loweing",
  "Eccentric Hamstrings- Prone Heel Kicks",
  "Eccentric Hamstrings- Russian Kneeling",
  "eccentric heel raises (2 up- 1 down)",
  "Eccentric Quadriceps- Kneeling",
  "Eccentric SLR",
  "Elbow Flexion Stretch at Doorway",
  "elbow Step out",
  "Elbow Supination/Extension PROM",
  "Elevated Hamstring Bridge",
  "Eliptical",
  "Elliptical",
  "EOB Explosive HS Kicks on PB",
  "EOB hamstring curls",
  "EOB hip IR/ER",
  "Eply's Maneuver",
  "EQ Board- Squats",
  "ER Scap Pulses",
  "ER step out at 90/90",
  "Eval - Speech Sound Prod w/ Lang",
  "Evaluation Time",
  "eversion side steps",
  "Facial Ex for Bell's Palsy",
  "Farmers Walk",
  "FCE (Functional Capacity Evaluation)",
  "figure 8 cone walk + stop and go",
  "Finger Flexion",
  "Finger Tip glides",
  "finger web - opening (thumb ext focus)",
  "Finkelstein Stretch",
  "Fire Hydrants",
  "Fitter- seated for LE ex",
  "Fitter- Std- Hip Ext",
  "Fitter-Std- 3 Way",
  "Fitter-Std- Hip Abd",
  "Fitter-Std- Skiing w/poles",
  "Fitter-Std- TKE",
  "Fitter-UE- P/U position w/pad- Speed Horiz Abd/Add",
  "Fitter-UE- P/U position w/pad- Speed Sh Flex",
  "Flexbar - Green",
  "Flexbar - Red",
  "Flexbar- Yellow",
  "Floor Clock- SLS",
  "foam roll pec stretch",
  "Foam roller flexion + habd",
  "Foam Roller OH Press",
  "Foam Roller Thread the Needle",
  "Foam roller touchdown",
  "Foam Roller- Glute",
  "Foam Roller- Lat Focus",
  "Foam Roller- Quad",
  "Foam Roller- Stabilization- 90/90",
  "Foam Roller- Stabilization- Qudraped-Bird Dog",
  "Foam Roller- Stabilization- Std- Squats [flat side up]",
  "Foam Roller- Thoracic Extension",
  "Foam Roller-Bird Dog",
  "Foam Roller-Stabilization- Dying Bug",
  "Foam Roller-Stabilization- Heel Lifts",
  "Foam-Parallel Stance Eyes Closed",
  "Foam-Parallel Stance Eyes Open",
  "Foot Push Ups",
  "Forward Foot Slides for Ankle WB DF",
  "forward foot taps",
  "Forward Step and Twist",
  "Forward Stepping Strategy",
  "Frog Walks",
  "Front Rack Marching",
  "Function: Carrying Objects at Waist Height",
  "Function: Carrying Objects Overhead",
  "Function: Climbing Ladders",
  "Function: Crawling",
  "Function: Crouching/ Squatting",
  "Function: Driving",
  "Function: Dynamic Balance on Even Surfaces",
  "Function: Fine Motor- Nuts/Bolts",
  "Function: Function: Climbing Steps/ Elevations/ Ramps",
  "Function: Hammering",
  "Function: Lifting from Floor to Overhead",
  "Function: Lifting from Floor to Waist",
  "Function: Lifting from Waist to Overhead",
  "Function: Multi-Limb Coordination Activities",
  "Function: Overhead Reaching",
  "Function: Patient Transfers",
  "Function: Push/ Pull Activities",
  "Function: Repetitive Gripping",
  "Function: Repetitive Materals Handling",
  "Function: Repetitive Pinching",
  "Function: Static Tasks w/Isometric Strength",
  "Function: Sustained Foward Bending Positions",
  "Function: Sustained Sitting Posture Tasks",
  "Function: Sustained Standing Tasks",
  "Function: Tall- 1/2 Kneeling Tasks",
  "Function: Tasks that involve being on Hands and Knees",
  "Function: Tasks that involve Trunk/ Core Strength",
  "Function: Typing Tasks",
  "Function: Using hand Tools [i.e. screwdrivers]",
  "Function: Walking- All Surfaces",
  "Gait - Heel Walking",
  "Gait - Progression to No AD",
  "Gait - Standard Walker",
  "Gait - Straight Cane",
  "Gait - Using Rolling Walker",
  "Gait - Walking Backwards",
  "Gait - Walking Forward",
  "Gait - Walking Sideways",
  "Gait Training",
  "Gait Training- Crutches",
  "Gait Training- Curb Stepping",
  "Gait- Cone Weave",
  "Gait- Elevations",
  "Gait- Lead Ups",
  "Gait- Obstacle Course",
  "Gait- Stair Climbing",
  "gastroc ez strap",
  "Gaze Stabilization Horizontal",
  "Goblet Squat",
  "Gorilla Rows",
  "Great Toe Flexion",
  "gripping with squeeze ball",
  "Group Therapy",
  "HABD",
  "Half Foam Roller Activities",
  "Half Foam Roller Shoulder Retractions",
  "Half Foam Roller T band",
  "Half Moon Yoga Pose",
  "hamstring curl machine",
  "hamstring curls",
  "Hamstring isometric w/ PB",
  "Hamstring Scoops",
  "Hand Resisted Spanish Squats",
  "Hand Therapy -Forearm wheel",
  "Hand Therapy gripper calibrated",
  "Hand Therapy- 2# bar sup/pro",
  "Hand Therapy- Action grip",
  "Hand Therapy- Ball Presses on table/wall",
  "Hand Therapy- Blocking ex extension",
  "Hand Therapy- Blocking ex flexion",
  "Hand Therapy- Blue Foam roll flat fist",
  "Hand Therapy- body foam fitness stick",
  "Hand Therapy- Bunnel block brown",
  "Hand Therapy- Bunnel block grip yellow",
  "Hand Therapy- Carpal stretch pad L/R",
  "Hand Therapy- carrying dumbells walking",
  "Hand Therapy- Cone activity grasp",
  "Hand Therapy- cylinder grip",
  "Hand Therapy- Desensitization Downey Dowels",
  "Hand Therapy- Desensitization Particals 1/2/3",
  "Hand Therapy- Dextreity Re-training",
  "Hand Therapy- Digi-flex yellow/red/green",
  "Hand Therapy- Digisqueze gripper",
  "Hand Therapy- Eggerciser",
  "Hand Therapy- Elbow ext/flex onball",
  "Hand Therapy- Elbow extension weighted stretch",
  "Hand therapy- Elbow flex closed chain wall stretch",
  "Hand Therapy- elbow flex IM towel roll",
  "Hand Therapy- Ergo bead ball",
  "Hand Therapy- Ergonomic training/ Pt ed for work/home",
  "Hand Therapy- Fine motor manipulatives",
  "Hand Therapy- Finger platter",
  "Hand Therapy- Finger ringer",
  "Hand Therapy- Finger Web",
  "Hand Therapy- Flex bar wrist/forearm- org/red/green",
  "Hand Therapy- Flexor tendon gliding ex",
  "Hand Therapy- Foam roll finger/hand ext",
  "Hand Therapy- Foam roll IM grips",
  "Hand Therapy- Func Dexterity Test-place/displace/turn",
  "Hand Therapy- Graded pinch",
  "Hand Therapy- Gray iso grips sm/lg",
  "Hand Therapy- Grey silicone grip",
  "Hand Therapy- Hammer- sup/pro 7oz/16oz/24oz",
  "Hand Therapy- Hand Helper exerciser",
  "Hand Therapy- Hand tree",
  "Hand Therapy- Hand weights",
  "Hand Therapy- Hand X trainer beginner/ intermediate",
  "Hand Therapy- Handyexerciser",
  "Hand Therapy- Handygym- wrist/hand CCW/CW",
  "Hand Therapy- Heart grip",
  "Hand Therapy- Highlighter intrinsichook grip",
  "Hand Therapy- Juxterciser",
  "Hand Therapy- Kettle ball 10#",
  "Hand Therapy- Medi-ball yellow/orange on table for wrist flex/ext",
  "Hand Therapy- O'Conner Tool Dexterity",
  "Hand Therapy- Opposition",
  "Hand Therapy- Orthotic checkout/ modification/pt ed",
  "Hand Therapy- Orthotic fabrication/fitting",
  "Hand Therapy- PNF patterns",
  "Hand Therapy- Purdue Peg Board",
  "Hand Therapy- Putty stamping",
  "Hand Therapy- Ring grip Medium/Heavy",
  "Hand Therapy- Rosenbusch Dexterity",
  "Hand Therapy- Spring Grip- white/yell/red/blk",
  "Hand Therapy- Swanson grip",
  "Hand Therapy- T-foam ex yell/pink/blu",
  "Hand Therapy- Tennis ball palmar stretch",
  "Hand Therapy- Theraband gel grip",
  "Hand Therapy- Theratube-UE wrist/ elbow",
  "Hand Therapy- Thumbciser",
  "Hand Therapy- Towel Scrubbing",
  "Hand Therapy- Towel slides on table",
  "Hand Therapy- Tr- peg board",
  "Hand Therapy- Via Prohandss finger exerciser yellow/blue",
  "Hand Therapy- Wand for sup/pro stretch",
  "Hand Therapy- Wand Large shoulder/ elbow",
  "Hand Therapy- Weighted ball- green/yellow/blue/purple",
  "Hand Therapy- weighted wand",
  "Hand Therapy- Wrist extension stretch on table top",
  "Hand Therapy- Wrist flex stretch over wedge",
  "Hand Therapy- Wristerciser",
  "Hanging Knee Raises",
  "Head on Body Turns",
  "Heather exercise special",
  "heel raise iso holds 45",
  "Heel Raise: Isometric hold",
  "Heel Raises",
  "Heel raises + Fasciitis fighter",
  "heel scoops",
  "Heel Slides on Wall",
  "Heel Slides-Active",
  "Heel Slides-with Strap",
  "Heel to Butt",
  "Heel Toe Walking",
  "Heel/Toe Raises",
  "Heels Elevated Bridge",
  "HEP/Pt Education/SelfCare Mngt - Completed and reviewed at IE",
  "HEP/Pt Education/SelfCare Mngt Final program for DC",
  "HEP/Pt Education/SelfCare Mngt Revised",
  "HEP/Pt Education/SelfCare Mngt Updated",
  "Hex Bar Squat",
  "High Knees",
  "high plank",
  "Hip 4 Way T band Stabilization",
  "Hip Abd w/Slide Board",
  "Hip Abduction -Supine [GE]",
  "Hip Abduction standing",
  "Hip Abduction-Side Lying-PRE's",
  "Hip Abduction-Supine-PRE's",
  "Hip Adbuction-Standing-PRE's",
  "Hip Adduction Standing-PRE's",
  "Hip Adduction-Side Lying-PRE's",
  "Hip Adduction-Supine-PRE's",
  "hip adductor w/ tb at CC",
  "Hip Butterflys",
  "Hip Extension- knee flexed-prone",
  "Hip Extension-knee ext- prone",
  "Hip Extension-Prone- Knee extended-PRE's",
  "Hip Extension-Prone- Knee flexed-PRE's",
  "Hip Extension-Standing-PRE's",
  "Hip Flexion- Seated",
  "Hip Flexion-Standing-PRE's",
  "Hip Flexion-Supine-PRE's",
  "Hip Hike",
  "Hip Lift Overs",
  "hip openers",
  "Hip T band Abd and Iso Add (Tband Bolster)",
  "Hip- S/L Clam Shells",
  "Hip- Supine Clam Shells",
  "Home Management",
  "Hook Lying Hip Flexion",
  "Hooklying Ankle DF",
  "Hooklying Hip ADD ball squeeze",
  "Humeral Head Depression rotator cuff",
  "Hurdle Walking on Flat Surface.",
  "Hurdle Walking- Fd",
  "Hurdle Walking- Lateral",
  "In and Out Squat Jumps",
  "inch worm",
  "Independent Activities",
  "Independent Activity",
  "Inferior Glide Isometric",
  "Iontophoresis",
  "IR walkouts",
  "Iron Cross SOS",
  "Iso ER Strap",
  "Iso ER Strap w/ Flexion",
  "Isometric Ankle Dorsi-Flexion",
  "Isometric Ankle Eversion",
  "Isometric Ankle Inversion",
  "Isometric Ankle Plantar-Flexion",
  "Isometric Ankle Pronation",
  "Isometric Ankle Supination",
  "Isometric Cerivcal",
  "Isometric Elbow Extension",
  "Isometric Elbow Flexion",
  "Isometric Hip Abduction",
  "Isometric Hip Adduction",
  "Isometric Hip ER",
  "Isometric Hip Extension",
  "Isometric Hip Flexion",
  "Isometric Hip IR",
  "Isometric Knee Extension",
  "Isometric Knee extension into belt",
  "Isometric Knee Flexion",
  "Isometric Shoulder 6-Way",
  "Isometric Shoulder Abduction",
  "Isometric Shoulder Adduction",
  "Isometric Shoulder ER",
  "Isometric Shoulder Extension",
  "Isometric Shoulder Flexion",
  "Isometric Shoulder Horiz Abd",
  "Isometric Shoulder Horiz Add",
  "Isometric Shoulder IR",
  "isometric thumb abduction",
  "Isometric Thumb Extension",
  "Isometric Wrist Extension",
  "Isometric Wrist Flexion",
  "Isometric Wrist Pronation",
  "Isometric Wrist Radial Deviation",
  "Isometric Wrist Supination",
  "Isometric Wrist Ulnar Deviation",
  "Isometric- Quad sets",
  "Isometrics",
  "Isometrics- Hamstring Sets",
  "Isometrics- Prone Quad Sets",
  "ITY",
  "IYT",
  "Jaw Clench c/ Tongue Depressor",
  "Jaw Open and Close",
  "Jaw Side-to-Side",
  "Jefferson Curls",
  "JM -  Ankle",
  "JM - AC/SC/Scap Thoracic Joint",
  "JM - Cervical Spine",
  "JM - Elbow",
  "JM - Glenohumeral Joint/GH",
  "JM - Hind/Mid/Forefoot",
  "JM - Hip Joint",
  "JM - Knee/Patella",
  "JM - Lateral Distractions",
  "JM - Lateral Patella Glide",
  "JM - Long Axis Distractions",
  "JM - Lumbar Shift Correction",
  "JM - Lumbar Spine",
  "JM - Proximal-Distal Tibiofibular Joint",
  "JM - radio-ulnar Joint",
  "JM - Sacro-Ilac Joint",
  "JM - Sidelying Lumbar Rotation Mob",
  "JM - Talocrural/ Subtalar Joint",
  "JM - Thoracic Spine",
  "JM - TMJ",
  "JM - Wrist/Hand/Digits",
  "JM-First Rib",
  "KB Halos",
  "KB Hang Clean to Squat",
  "KB OH Carry",
  "KB RDL",
  "KB Reverse Chops",
  "KB Squat Clean and Press",
  "KB Sumo Squat to Upright Row",
  "KB Swings",
  "Kicking Soccer Ball",
  "kissing toes",
  "Knee Bag Hang",
  "knee driver",
  "Knee Extension Isometrics",
  "Knee Extension Machine",
  "Knee Extension Stretch c/ Ankle Weight",
  "Knee Fallouts",
  "Kneeling Adductor Rocks",
  "Kneeling Hip flexor stretch",
  "Kneeling Hip Flexor to HS Dynamic Stretch",
  "Kneeling Hip Thrusters",
  "Kneeling Prayer Stretch",
  "Kneeling Prayer Thoracic Mobilization with Dowel",
  "Kneeling Windmills for T-Spine Rotation",
  "L Sit SLRs",
  "Landmine Squats",
  "LAQ",
  "lat step up/over",
  "Lat Stretch at CC",
  "Lateral Bounding",
  "lateral lunges",
  "Lateral Stepping Strategy",
  "LE: ckc/okc x4 -cc",
  "Leg Curl - Single Leg",
  "Leg Curl Machine - BLE",
  "Leg Press - BLE",
  "Leg Press - SL",
  "Leg Swings",
  "Lift and Carry Basket",
  "Lizard Stretch",
  "LLPS Elbow Ext Stretch",
  "Long Lever Bridge",
  "Long Sit QL Stretch",
  "Long Sitting Scoots",
  "Low Row Isometric at table",
  "Lower Abdominals Program 1b",
  "Lower Serratus S/L Stabilization",
  "Lower Stabilization S/L",
  "Lower Trap S/l stab",
  "Lower Trunk Rotation- LTR",
  "Lumbar Extension Machine",
  "Lumbar Hypers",
  "Lunge Jumps",
  "lunge matrix",
  "Lunge Switch",
  "Lunge Walk",
  "Lunges-Forward",
  "Lunges-Side",
  "Lunges-Step Back",
  "machine chest press",
  "Magnum Hip Machine",
  "Magnum Pull Down",
  "Magnum Rear Deltoid",
  "Magnum- leg curl",
  "Magnum- leg extension",
  "Magnum- Pec Dec",
  "Magnum-shoulder press",
  "Manual Therapy",
  "Manual Therapy - Cross Friction Massage",
  "Manual Therapy - Manual Cervical Traction",
  "Manual Therapy - Manual Lumbar Traction",
  "Manual Therapy - Myofascial Release/MFR",
  "Manual Therapy - Nerve Glides",
  "Manual Therapy - Scar Mobilization",
  "Manual Therapy - Soft Tissue Mobilization",
  "Manual Therapy - Subocciptal Release",
  "Manual Therapy - Tendon Glides",
  "Manual Therapy - Trigger Point Release",
  "Manual Therapy -Cervical/ UE Neural Stretch's",
  "Manual Therapy- Dry Needling",
  "Manual therapy- Passive Stretching w/ OP",
  "Manual Therapy- PROM",
  "Manual Therapy- Soft Tissue Release",
  "Manual Therapy-Taping- Kineso",
  "Manual Therapy-Taping- McConnell",
  "Manual- PNF to Facial mms",
  "Manual- PNF-UE/LE/Trunk/Pelvic Patterns",
  "Marble LE P/U",
  "Marching Gait",
  "Mat Walking",
  "Mat Walking With Hurdles",
  "Matts favorite exercise",
  "MB OH Carry High Knees",
  "MB Slams",
  "Mechanical Traction- C/S",
  "Mechanics for lifting",
  "MET- SI Joint",
  "MET: CS - ERSR/ L",
  "MET: CS - ESL/ R",
  "MET: CS - FRSR/L",
  "MET: CS - FSL/ R",
  "MET: Superior 1st Rib",
  "MET: TS - ERSR/ L",
  "MET: TS - ESR/ L",
  "MET: TS - FRSR/ L",
  "MET: TS - FSR/ L",
  "Mini Bike",
  "Mini Lunge",
  "Mini Squats",
  "Mini Squats w/PB",
  "Mirror Climb",
  "Mod Military Press",
  "Mountain Climbers",
  "Moura Lateral Walks",
  "MRE Manual Resistive Exercises",
  "Mulligan Snags",
  "Multi Func Ex- Squat to SLS",
  "Multi Func Ex- UE Supine Matrix Ex",
  "NBOS Ball roll at elevated table",
  "Neck bridges",
  "Neuro- Ankle Disassociation Motor Control",
  "Neuro- Elbow Disassociation Motor Control",
  "Neuro- Hip Disassociation Motor Control",
  "Neuro- Knee Disassociation Motor Control",
  "Neuro- Shouler Disassociation Motor Control",
  "Neuro- Trunk Disassociation Motor Control",
  "Neuro- Wrist/ Fingers Disassociation Motor Control",
  "Neuromuscular Re-Education",
  "Neuromuscular Reeducation",
  "No Monies",
  "Nordic HS Curls",
  "Nu-Step",
  "occ float",
  "OH DB Carry",
  "OH Stretch at CC",
  "OH Wall Ball Taps",
  "olivia's excercise",
  "open book",
  "Orthotic LE Training",
  "Orthotic UE Training",
  "Over head press",
  "overhead press machine",
  "Overhead Reach for Objects",
  "P-ball LTR",
  "P-ball partial situps",
  "P-ball rollout",
  "Pallof press",
  "paloff press",
  "Parallel Bars",
  "PB Against Wall Squat",
  "PB LTRs",
  "PB Plank w/ UE Push Out",
  "PB Plank with LE Roll-In",
  "PB Pushdown",
  "Pball wall walk",
  "pencil push ups",
  "Pendulum Exercises",
  "performance test",
  "Perturbations",
  "Phonophoresis",
  "Physio ball roll ins",
  "Physioball arm/leg lift",
  "PhysioBall Bridge w/LE roll ins",
  "Physioball Bridging",
  "Physioball CKC Hamstring Curl",
  "PhysioBall I's, Y's, T's, W's",
  "Physioball Jumping Jacks",
  "Physioball Marching",
  "Physioball Pelvic Clock",
  "Physioball Plank",
  "Physioball Prone Alternating Arm Raise",
  "Physioball Prone Hip Extension",
  "Physioball Prone Trunk Extensions",
  "Physioball Prone Walk Out",
  "Physioball Pushdowns",
  "Physioball Reverse Curls",
  "Physioball Roll Out",
  "Physioball Row",
  "Physioball Russian Twist",
  "Physioball Seated Pelvic Tilt",
  "Physioball Shoulder Extension",
  "Physioball Situps",
  "Physioball Supine LE Roll ins",
  "Physioball Supine Walk Out",
  "Physioball w/LE PRE's",
  "Physioball w/UE PRE's",
  "Physioball Wall Squat with Soleus Raises",
  "pigeon stretch",
  "Pilate's Core Ex w/LE Mvts",
  "Pilate's Core Ex w/UE Mvts",
  "Piriformis stretch",
  "pistol squats",
  "Plank KB Pull throughs",
  "Plank on Elbows with Hip Taps",
  "Plank Row",
  "Plank Shoulder Taps",
  "Plinth BOSU Plank w/ hip extension",
  "Plinth Reverse Hypers",
  "Plyo- Bridging w/jumping jacks",
  "Plyoback Toss- SL",
  "Plyometric- LE- Agility Ladder",
  "Plyometrics- Box Jumps",
  "Plyometrics- Box Lateral Step Overs",
  "Plyometrics- Clock Squat",
  "Plyometrics- crossovers",
  "Plyometrics- heel taps",
  "Plyometrics- LE Hopping",
  "Plyometrics- LE- BL Hopping",
  "Plyometrics- LE- Bounding",
  "Plyometrics- LE- High Skipping",
  "Plyometrics- LE- SL Hopping",
  "Plyometrics-LE- Depth Jumps- 6\",",
  "Plyometrics-LE- Depth Jumps-10\",",
  "Plyometrics-LE- Depth Jumps-12\",",
  "Plyometrics-LE- Depth Jumps-16\",",
  "Plyometrics-LE- Fd/Bkd Hurdle Jumping",
  "Plyometrics-LE- Jump Rope",
  "Plyometrics-LE- Jumping Up- 4\",",
  "Plyometrics-LE- Jumping Up- 6\",",
  "Plyometrics-LE- Jumping Up-10\",",
  "Plyometrics-LE- Jumping Up-12\",",
  "Plyometrics-LE- Lateral Hurdle Jumping",
  "Plyometrics-LE- Prone Speed Knee Flex Ball Kicks",
  "Plyometrics-LE- Skipping",
  "Plyometrics-LE- Speed Squats",
  "Plyometrics-LE- Speed Step Tapping",
  "Plyometrics-LE- Supine Speed Ball Kicks",
  "Plyometrics-LE- Trampoline",
  "Plyometrics-UE- Clap Push Ups",
  "Plyometrics-UE- Plyo Ball Chest Pass",
  "Plyometrics-UE- Plyo Ball Over Head Throws- Bwd",
  "Plyometrics-UE- Plyo Ball Over Head Throws- Fwd",
  "Plyometrics-UE- Plyo Ball Single Arm Throws",
  "Plyometrics-UE- Std Over the Shoulder Plyo Ball Catches",
  "Plyometrics-UE- Std Plyo Ball Drops",
  "Plyometrics-UE- Ther Ball Walk Outs to thigh",
  "Plyometrics-UE- Ther Ball Walk Outs to toes",
  "Plyometyrics- Lateral Shuffles",
  "PNF- UE/Trunk/LE",
  "POGO Hops",
  "Poliquin Step Downs",
  "Pool Kick Board Push Pull",
  "Pool Noodle Cycle Scissors",
  "Pool Walking",
  "Pos Tib Ankle Eversion",
  "Post Shoulder Rolls",
  "Post Tib Ankle Eversion in Standing",
  "posterior capsule stretch",
  "Posture Education",
  "Pot Stirrers",
  "Power Ball TE- 2/4/6#",
  "Power Web",
  "power web gripping",
  "power web opening",
  "PPT with TA act",
  "PRE's- 4 way SLR",
  "Preacher Curl",
  "PROM",
  "PROM Ankle Dorsi-Flexion",
  "PROM Ankle Eversion",
  "PROM Ankle Inversion",
  "PROM Ankle Planter-Flexion",
  "PROM Ankle Pronation",
  "PROM Ankle Supination",
  "PROM- Shoulder ER/IR in Plane of Scaption",
  "PROM-Elbow Extension",
  "PROM-Elbow Flexion",
  "PROM-Hip Abduction in Extension",
  "PROM-Hip Abduction in Flexion",
  "PROM-Hip ER",
  "PROM-Hip Extension",
  "PROM-Hip Flexion",
  "PROM-Hip IR",
  "PROM-Knee Extension",
  "PROM-Knee Flexion",
  "PROM-Pronation",
  "PROM-Shoulder Abduction",
  "PROM-Shoulder Adduction",
  "PROM-Shoulder ER in Abduction",
  "PROM-Shoulder ER in Neutral",
  "PROM-Shoulder Extension",
  "PROM-Shoulder Flexion",
  "PROM-Shoulder IR in Abduction",
  "PROM-Shoulder IR in Neutral",
  "PROM-Supination",
  "pronation with (femur resistance)",
  "pronation with dowel",
  "pronation with indian club",
  "Prone 90/90 ER",
  "Prone Chin Tucks",
  "prone glute squeeze",
  "Prone Hang for Knee Extension",
  "Prone Heel Squeezes",
  "Prone Hip Extension",
  "Prone Hip IR w/ TB",
  "Prone Horizontal Abduction",
  "Prone HS Curl w/ TB",
  "Prone I's",
  "Prone Knee Extension Isomtric into PB",
  "Prone Knee Flexion",
  "Prone Lumbar Distraction",
  "Prone lying",
  "Prone on elbows/POE",
  "prone press up to elbows (progress to full)",
  "Prone Pressups",
  "Prone Quad Stretch with SOS",
  "Prone Row",
  "Prone Rows",
  "Prone Scorpions",
  "Prone Shoulder Extension",
  "Prone Shoulder Flexion",
  "Prone Shoulder Hurdles",
  "prone transverse abdominis bracing",
  "Prone Ts",
  "Prone W",
  "prone windshield wipers",
  "Prone YTW",
  "Prosthetic LE Training",
  "Prosthetic UE Training",
  "Prostretch",
  "Protrusion/Retrusion",
  "PT Rhythmic Stabs",
  "Pull Overs",
  "Push Up Plus",
  "Push Ups w/Basketball Rolls",
  "Push Ups- Kneeling",
  "Push Ups- Plinth",
  "Push Ups- Therapy Ball",
  "Push Ups- Traditional",
  "Putty - thumb war pinch",
  "Putty- Orange Finger Pinch",
  "Putty- Orange Finger Roll",
  "Putty- Orange Grip",
  "Putty- Yellow Finger Pinch",
  "Putty- Yellow Finger Roll",
  "Putty- Yellow Grip",
  "QL Hip Hike",
  "Quadriped lumbar flexion",
  "Quadruped Elevated Pelvic Rocks",
  "Quadruped Hip Abduction",
  "Quadruped Hover",
  "Quadruped Kickbacks",
  "Quadruped Pelvic Tilts",
  "Quadruped- Alt. LE Lifts",
  "Quadruped- Alternating UE Lifts",
  "Quantum Multihip",
  "Quarter Lunge Iso Hold",
  "RDL",
  "Re-Evaluation",
  "reactive cone drill",
  "Reactive Drills",
  "Rebounder Calf Raises",
  "Reevaluation Time",
  "REIL",
  "REIS",
  "Renegade Row",
  "Resistance Band QL Stretch",
  "Resisted Finger Extension",
  "Resisted Jaw Opening",
  "Resisted pronator strengthening with weight on tbar",
  "Resisted Walking",
  "Reverse Bridge Shoulder Stretch",
  "Reverse Clamshells",
  "Reverse Curls",
  "reverse nordic curls",
  "Reverse Pendulums",
  "Reverse Step Down",
  "Reverse Wall- Ball Tap",
  "rhomberg on foam",
  "rhomberg on foam w/ eyes closed",
  "Rhomberg- Balance",
  "Rhythmic Stabilization",
  "Ring of Fire",
  "robberies",
  "Rockaway obstacle course",
  "Rocker Board Ant/Post",
  "Rocker Board Med/Lat",
  "ROM with RockFloss",
  "Roman Chair Back Extensions",
  "romberg stance + eyes closed / no foam",
  "Row machine",
  "Rubber Band Finger Extension",
  "runner's step up",
  "Runners Stretch (Soleus)",
  "Runnners Stretch with Rotation",
  "SA Roll Up",
  "SA Wall Push Ups",
  "Saccades",
  "SAQ",
  "sarahs exercise",
  "sarahs exercise 2",
  "Scap Push Ups",
  "Scap Stabilization- Wall Ball CW/CCW",
  "Scaption c/ ER Rollups",
  "Scaption- DB's",
  "Scapula Clock",
  "Scapula Punches",
  "Scapula Squeezes",
  "Scapular Depressions into Table",
  "SCM Stretch",
  "Seal Walks",
  "seated BRO",
  "seated clamshells",
  "Seated DF/PF",
  "Seated Ellipitical",
  "Seated ER with dumbell",
  "seated figure 4",
  "Seated Flexion Stretch",
  "Seated Forward Folds",
  "Seated Good Mornings",
  "Seated Habituation",
  "Seated Hamstring Curl",
  "seated heel raises",
  "Seated Heel/Toe Raises",
  "Seated Hip ER/IR Strengthening",
  "Seated Hip IR strengthening",
  "Seated Hip Marching",
  "Seated Individual finger extension",
  "Seated Knee Extension",
  "seated knee flexion AAROM with opposite LE",
  "Seated Knee Flexion- AROM",
  "Seated Lat Stretch c/ Dowel",
  "Seated Lumbar flexion",
  "Seated Multifidi Activiation Isometric",
  "Seated Posterior Tibialis INV PREs",
  "Seated Posterior Tibialis Sweeps",
  "Seated Quadriceps Isometric",
  "Seated Quadriceps Leg Extension Machine",
  "Seated reach down to up",
  "Seated Shoulder ER Stretch @ table",
  "Seated Shoulder Flexion with Cane",
  "seated thoracic extension",
  "Seated Thoracic Rotations",
  "Seated thumb flexion/extension",
  "Seated Toe Taps",
  "seated wall angels",
  "Seayed Hamstring Curl w/ TB",
  "Sebass KE",
  "Serratus Punch",
  "Serratus Slides with foam roller",
  "Serving Tray",
  "Short Arc T",
  "Shoulder - Std - I's",
  "Shoulder - Std - T's",
  "Shoulder - Std - Y's",
  "Shoulder ABCs",
  "Shoulder Abduction-Side Lying",
  "Shoulder Butterflys",
  "Shoulder Clocks",
  "Shoulder ER @ CC",
  "Shoulder ER Stretch at Wall",
  "Shoulder ER with wand",
  "Shoulder Extension",
  "Shoulder External Rotation Sidelying",
  "Shoulder Flexion PROM c/ Hand in Standing",
  "Shoulder Flexion with Hands Clasped",
  "Shoulder Flexion- Side Lying",
  "Shoulder IR",
  "Shoulder IR Walkouts",
  "Shoulder Matrix",
  "shoulder moblity circuit",
  "Shoulder Pulley",
  "Shoulder Retractions",
  "Shoulder rolls",
  "Shoulder Wall Clock",
  "Shoulder- AAROM- Wand- Anterior Horizontal Abd/Add",
  "Shoulder- AAROM- Wand- Chest Press",
  "Shoulder- AAROM- Wand- D1/D2 Pattern",
  "Shoulder- AAROM- Wand- Extension",
  "Shoulder- AAROM- Wand- Flexion",
  "Shoulder- Empty Can exercise",
  "Shoulder- PRE- TB External Rotation",
  "Shoulder- Prone- Lift Offs-subscapularis",
  "Shoulder- Suprispinatus Raise",
  "Shoulder- table/incline slider",
  "Shoulder-CKC-BUE on Table/Plinth Rhythmatic Stab",
  "Shoulder-CKC-On Extended UE w/Web Glide Contralareal",
  "Shoulder-CKC-On Extended UE-Reach w/Contralateral UE",
  "Shoulder-CKC-POE--Reach w//Contralateral UE",
  "Shoulder-CKC-Table/Plinth Push Ups",
  "Shoulder-CKC-Table/Plinth-Spider Walks w/Contralateral",
  "Shoulder-Prone- Capitial I's",
  "Shoulder-Prone- Scapula Retraction/Depression",
  "Shoulder-Prone- Small i's",
  "Shoulder-Prone- T's",
  "Shoulder-Prone- W's",
  "Shoulder-Prone- Y's",
  "Shoulder-Supine- I's",
  "Shoulder-Supine- T's",
  "Shoulder-Supine- W's",
  "Shoulder-Supine- Y's",
  "Shrugs",
  "Shuttle - HR Left",
  "Shuttle - HR Rt",
  "Shuttle - LP Left",
  "Shuttle - LP Plyomretics",
  "Shuttle - LP Rt",
  "Shuttle -LP BL",
  "Shuttle- HR BL",
  "Side Glides (Lateral Shift Correction)",
  "Side lying hip IR c/ Bolster",
  "Side Lying Lumbar Gapping",
  "Side Lying Pelvic Rocks",
  "Side Lying Shoulder Abduction with Circles",
  "Side lying to side lying",
  "Side Plank with Clamshells",
  "Side Step",
  "Side Step Lateral Walking",
  "Side Step/Squat",
  "Sidelying Seals",
  "single arm chest press",
  "single leg bosu bridge",
  "Single Leg Bridge",
  "single leg deadlift",
  "Single leg RDL",
  "Single Leg Squat",
  "Skater Jumps",
  "Skull Crushers",
  "SL Ball Slams",
  "SL hip hinge",
  "Slant Board Calf Stretch",
  "Slant Board Soleus Stretch",
  "Slantboard",
  "Slantboard Squats",
  "SLB Ball Toss",
  "SLB with Body Blade",
  "SLB with Weight Pick Up",
  "SLB-Eyes Closed",
  "SLB-Eyes Open",
  "Sleeper Stretch",
  "SLER",
  "Slide Board",
  "SLR flexion and abduction",
  "SLR- Extension and Abduction",
  "SLR- Flexion/Extension/Abduction",
  "SLR-PRE's",
  "SLS Around the Worlds",
  "SLS ball toss at rebounder",
  "SLS ball toss w/trampoline",
  "SLS on Foam",
  "SLS with shld ext at cable column",
  "SLS+ alternating finger taps",
  "Soleus Press",
  "Soleus Raise",
  "Soleus stretching",
  "SOS gastroc",
  "SOS Quadriceps",
  "spanish squat",
  "Speech Therapy",
  "Speech Therapy - Group",
  "split squat iso- mini",
  "Split squats",
  "Split Stance TKEs",
  "Split Stance w/ Trunk Rotation",
  "Sport Band Lateral Hip Distraction in 1/2 Kneeling",
  "Sport Band OH Press",
  "Squaits Narrow",
  "Squat to Reverse Lunge",
  "Squats",
  "Squats Speed",
  "Squats Wide",
  "Squats- single leg",
  "Stabilization - 4-pt Arm Extension",
  "Stabilization - 4-pt Arm/Leg Extension",
  "Stabilization - 4-pt Leg Extension",
  "Stabilization - 4-pt Pelvic Tilt",
  "Stabilization - Modified Std Bird Dog",
  "Stabilization - Pelvic Tilt with Bridge",
  "Stabilization - Plank on Elbows",
  "Stabilization - Plank on Extended Arms",
  "Stabilization - Prone Alt Arm Extension",
  "Stabilization - Prone Alt Arm/Leg",
  "Stabilization - Prone Alt LE Extension",
  "Stabilization - Quadraped Bird Dog",
  "Stabilization - Side Plank",
  "Stabilization - Standing Pelvic Tilt",
  "Stabilization - Supine 1\" Foot Lifts\",",
  "Stabilization - Supine 1\" Heel Slides\",",
  "Stabilization - Supine 90/90 w/LE weights",
  "Stabilization - Supine 90/90 w/o LE weights",
  "Stabilization - Supine Bent Leg Lower",
  "Stabilization - Supine Bridge w/TKE",
  "Stabilization - Supine Dying Bug w/o UE-LE weights",
  "Stabilization - Supine Dying Bug w/UE/LE weights",
  "Stabilization - Supine March/Alt Arms",
  "Stabilization - Supine Marching Bridge",
  "Stabilization - Supine Pelvic Tilts",
  "Stabilization - Supine Straight Leg Lower",
  "Stabilization - TA Contraction",
  "Stabilization - TA/Ball Squeeze/TKE",
  "Stabilization Dynamic Elbow Plank Arms",
  "Stabilization Dynamic Elbow Plank Legs",
  "Stabilization Dynamic Side Plank Arms",
  "Stabilization Dynamic Side Plank Legs",
  "Stabilization- 90/90 alt foot taps",
  "Stabilization- Lumbar Spine- Standing Hip Hinge",
  "Stabilization- w/cable column antirotation",
  "Stabilization- with 2/dowels",
  "Staggard Stance Arch Lifts",
  "Stair Climber",
  "Stand TA brace",
  "Stand to floor/Floor to Stand transistions",
  "stand to Y from head down - full cervical flex/ext",
  "Standing Backward Bends Extension in Standing",
  "Standing Cane ER Stretch",
  "standing hamstring curl",
  "Standing Hip Abduction",
  "Standing Hip Extension",
  "Standing hip flexor stretch",
  "Standing Hip Taps w/ Glute Set at Table",
  "Standing Knee flexion",
  "standing march",
  "Standing Marching",
  "Standing OH Press with Y Pulse at Wall",
  "Standing PF/DF",
  "standing row and reach",
  "Standing Sh. ER 90-90",
  "Standing Shoulder PRE's- all motions",
  "Standing SLR- Abduction and Extension",
  "Standing TKEs",
  "Standing-Rotator Cuff Combo-PRE's",
  "Standing-Scap Retraction-PRE's",
  "Standing-Shoulder Abduction-PRE's",
  "Standing-Shoulder ER-PRE's",
  "Standing-Shoulder Extension-PRE's",
  "Standing-Shoulder Flexion-PRE's",
  "Standing-Shoulder IR-PRE's",
  "Standing-Shoulder Press-PRE's",
  "Standing-Shoulder Rolls-PRE's",
  "Standing-Shoulder Scaption-PRE's",
  "Standing-Suprapinatus-PRE's",
  "Star Lunges",
  "Star Taps",
  "Steamboats",
  "Step Back- Eccentrics",
  "Step Downs",
  "Step Over Hurdles",
  "step tap",
  "Step Throughs",
  "step TKE",
  "Step Up-Backward",
  "Step Up-Forward",
  "Step Up-Side",
  "step up/over",
  "Stepper - sitting",
  "Stepper - standing",
  "Stool Hip IR/ER",
  "Stool Scoots",
  "Stool Taps",
  "stop and go walking drill",
  "Straight Arm Pull Downs",
  "Stretch Hip ER",
  "Stretch ITB",
  "Stretch Prone hip IR",
  "Stretch Quadriceps EZ strap",
  "Stretch seated hip ER",
  "Stretch SOS Hamstrings",
  "Stretch SOS Hip Rotators",
  "Stretch SOS Shoulder Internal Rotation",
  "Stretch- Elbow/Forearm/Wrist",
  "Stretch- Hip Figure 4",
  "Stretch- ITB/TFL",
  "Stretch- Levator Scapula",
  "Stretch- Piriformis EZ Strap",
  "Stretch- Plantar Fascia",
  "Stretch- Prayer",
  "Stretch- Quadraceps",
  "Stretch- Quadratus",
  "Stretch- Rhomboid",
  "Stretch- Scalene",
  "Stretch- Seated Knee Flexion",
  "Stretch- Soleus",
  "Stretch- Triceps",
  "Stretch- Upper Trap",
  "Stretch-Shoulder",
  "Stretching",
  "Stretching Cervical Spine",
  "Stretching First Rib Mobilization",
  "Stretching TMJ",
  "Stretching w/Joint Mobilizations",
  "Stretching w/MFR Techiques",
  "Stretching w/Soft Tissue Mobilizations",
  "Stretching- Corner Pec Stretch",
  "Stretching- Door External Rotation Stretch",
  "Stretching- Gastroc",
  "Stretching- Hamstrings EZ Strap",
  "Stretching- Iliopsoas",
  "Stretching- NMR - Contract Relax",
  "Suitcase Carry",
  "supination with (femur)",
  "supination with dowel",
  "supination with indian club",
  "supination with therabar and weight",
  "Supine 90-90 Ball Toss",
  "Supine 90/90 ADD/HS ISOS",
  "Supine Bicep Curl with TB",
  "Supine Cervical Extension Over Towel Roll",
  "Supine Chest Fly",
  "supine chin tuck",
  "Supine D2 FLexion w/ TB",
  "Supine DNF c/ Rotation",
  "Supine Elbow Flexion",
  "Supine Glute Sets",
  "Supine Hip Flexion on Box",
  "Supine Hip Flexor Stretch",
  "Supine LLLD Knee Extension Stretch with Heel Prop",
  "Supine OH Press",
  "Supine Salutes",
  "Supine shoulder 90/90 ER",
  "Supine Shoulder Abduction",
  "Supine Shoulder Flexion",
  "Supine Shoulder IR ECC Focus",
  "Supine Shoulder Scaption",
  "Supine SLR",
  "Supine Thoracic Mobilization with Tennis Ball",
  "Swallow Therapy",
  "T band horse kicks",
  "T- band Cervical Spine Isometrics",
  "T- Band Wrist Extension",
  "T-Band AA Shoulder Abduction",
  "T-Band AA Shoulder Flexion",
  "T-Band Clamshells",
  "T-Band Dorsi-Flexion",
  "T-Band Eversion",
  "T-Band Forward Punch",
  "T-Band Hip Abd",
  "T-Band Hip Add",
  "T-Band Hip Ext",
  "T-Band Hip External Rotation",
  "T-Band Hip Flex",
  "T-Band Inversion",
  "T-Band Knee Extension (Sitting)",
  "T-Band Knee Flexion (Prone)",
  "T-Band Knee Flexion (Sitting)",
  "T-Band Lat Pull Down",
  "T-Band LE Diagonal 1",
  "T-Band LE Diagonal 2",
  "T-Band Plantar-Flexion",
  "T-band Row",
  "T-Band Scaption",
  "T-Band Scapular Retraction",
  "T-Band Shoulder Abduction",
  "T-Band Shoulder ER in Abduction",
  "T-band Shoulder ER in Neutral",
  "T-Band Shoulder Extension",
  "T-Band Shoulder Flexion",
  "T-band Shoulder Horiz Abd",
  "T-band Shoulder Horiz Add",
  "T-Band Shoulder IR in Abduction",
  "T-Band Shoulder IR in Neutral",
  "T-Band Side Step/Squat",
  "T-Band Squats",
  "T-Band Standing 4 Way SLR",
  "T-Band UE Diagonal 1",
  "T-Band UE Diagonal 2",
  "T-band UE Wall Spider Walks",
  "T-Band Wrist Extension",
  "T-Band Wrist Flexion",
  "T-Band Wrist Radial Deviation",
  "T-Band Wrist Ulna Deviation",
  "T-Band- Monster Walks- fwd/bwd",
  "T-Band- Monster Walks- lateral",
  "T-band- TKE's",
  "TA",
  "TA Activation with Heel Taps",
  "TA Activation with LE March",
  "Table Top Glides",
  "tailgaters",
  "Tandem ambulation",
  "tandem stance",
  "tantrums",
  "TB bridge progressing to Hip thrust",
  "TB ER Uppercut",
  "TB ER w/scap retraction",
  "TB ER/IR Isometrics",
  "tb wrist extension",
  "tb wrist flexion",
  "Tband ER pullout",
  "Tennis/Golf Ball Rolls",
  "TENS Instruction",
  "Therapeutic Activities",
  "Therapeutic Exercise",
  "Therastick",
  "thread needle",
  "thumb abduction band resisted",
  "thumb adduction band resisted",
  "thumb extension band resisted",
  "tibial IR/ER",
  "TMJ Rotation",
  "TMJ Translation",
  "Toe Curls",
  "toe point to extension stretch",
  "toe point to toe extension stretch",
  "Toe Raises",
  "Toe Walk",
  "Toe-Heel Walk",
  "Total Gym - Prone Lateral Pull Up",
  "Total Gym Bilateral Squat",
  "Total Gym Calf Raise",
  "Total Gym Hamsting Curl",
  "Total Gym Single Leg Squat",
  "Towel Scrunches",
  "Towel slides up wall",
  "Tractor Pull",
  "Treadmill-Jog",
  "Treadmill-Retro-walking",
  "Treadmill-Run",
  "Treadmill-Side Steps",
  "Treadmill-Walk",
  "Tricep Kick Back",
  "Tricep Pushups",
  "Truck Driver",
  "TRX Alternating Lateral Lunges",
  "TRX Bridge",
  "TRX Pikes",
  "TRX Reverse Crunches",
  "TRX rows",
  "trx squat",
  "TRX Ys",
  "TTR ER HIP BIL",
  "Turkish Get Up Crunch",
  "Turkish Get Ups",
  "UBC",
  "UBE",
  "Ultrasound",
  "UT/LS Stretch",
  "Vertical Jump w/ 90 deg Turn Landing on SL",
  "vestib: Eyes locked on pen with head turns",
  "Vestibular - A-lock: Eyes with head rotation",
  "Vestibular Ball Toss",
  "Vestibular Tracking",
  "VOR Cancellation",
  "VOR x1",
  "VOR x2",
  "waiter carry",
  "walking vor x1",
  "Walking with head turns",
  "Wall Angels",
  "Wall Bicep Stretch",
  "Wall Ladder",
  "Wall Pec Stretch",
  "Wall plank w/t-band",
  "Wall plank with TS rotation",
  "wall planks",
  "Wall Push Ups- High Position",
  "Wall Push Ups- Low Position",
  "Wall Push Ups- Middle Position",
  "Wall sit",
  "Wall Slides",
  "Wall Slides with Lift Offs",
  "Wall Squat Heel Raises",
  "wall walk",
  "Warrior 1 to 2 Rotations",
  "Web Slide",
  "Web Slide Angels",
  "Web Slide Bicep Curls",
  "Web Slide D1 Flexion",
  "Web Slide ER @ 90 deg Sh. Flex.",
  "Web Slide External Rotation",
  "Web Slide High Rows",
  "Web Slide Horizontal Abduction",
  "Web Slide Internal Rotation",
  "Web Slide Internal/External Rotation",
  "Web Slide IR @ 90 deg Sh. Flex",
  "Web Slide Lawnmowers",
  "Web Slide Mid Rows",
  "Web Slide Pronation",
  "Web Slide Punch",
  "Web Slide SA Chest Fly",
  "Web Slide Shoulder Adduction",
  "Web Slide Shoulder Extension",
  "Web Slide SLS activities",
  "Web Slide Triceps",
  "Webslide 4-Way Chops",
  "Webslide D1/D2 UE Flex/Ext",
  "Webslide Supination",
  "Webslide- OH Press",
  "Weight Shift",
  "Wobble Board",
  "Wobble Board-Balance 2 Feet Eyes Closed",
  "Wobble Board-Balance 2 Feet Eyes Open",
  "Wobble Board-SLB Eyes Closed",
  "Wobble Board-SLB Eyes Open",
  "Work Tasks- Carrying Objects at Waist Height",
  "Work Tasks- Climbing ladders",
  "Work Tasks- Crawling",
  "Work Tasks- Crouching/ Squatting",
  "Work Tasks- Driving",
  "Work Tasks- Dynamic balance on even surfaces",
  "Work Tasks- Fine Motor- nuts/bolts",
  "Work Tasks- Hammering",
  "Work Tasks- Lifting from Waist to Overhead",
  "Work Tasks- Lifting:  Floor to Waist",
  "Work Tasks- Lifting: Floor to Overhaed",
  "Work Tasks- Multi-limb coordination",
  "Work Tasks- OH reaching",
  "Work Tasks- Patient tranfers",
  "Work Tasks- Push/Pull",
  "Work Tasks- Repetitive Gripping",
  "Work Tasks- Repetitive Pinching",
  "Work Tasks- Repetivtive Handling",
  "Work Tasks- Screwdrivers",
  "Work Tasks- Static Activities/ isometric strength",
  "Work Tasks- Steps/Elevations",
  "Work Tasks- Sustained Forward Bending",
  "Work Tasks- Sustained Sitting for Work Duties",
  "Work Tasks- Sustained Standing for Work Duties",
  "Work Tasks- Tall- 1/2 Kneeling",
  "Work Tasks- Trunk/ Core Strength",
  "Work Tasks- Typing",
  "Work Tasks- Walking- all surfaces",
  "Work Tasks- Wrenches",
  "Work Tasks: Carrying Objects Overhead",
  "Work Tasks: Low Level/ on hands and knees",
  "Work Tasks: UE  Plyometric Activities for Job Simulation",
  "wound care",
  "wrist circles",
  "wrist curls w/ dowel",
  "Wrist extension + BFR 30-15-15-15",
  "wrist extension / flexion AROM",
  "wrist extension mobilization on table",
  "wrist extension stretch",
  "Wrist Extensor Stretch",
  "Wrist Flexion Stretch",
  "Wrist Radial/Ulnar Deviation",
  "Wrist Roller",
  ];

  const _CPT_CHOICES = isServiceBased ? _SERVICE_CPT_CHOICES : _EXERCISE_CPT_CHOICES;
  const _CPT_DESCRIPTION =
    'Only choose a CPT code if you are at least 80% confident it matches an activity ' +
    'performed in this session. Leave blank if uncertain.';

  const buildRowEntry = (
    row: Record<string, string>,
    includeChoices: boolean
  ): Record<string, any> => {
    const obj: Record<string, any> = {};
    headers.forEach((h, i) => {
      // Skip date/provider/special columns — AI should not fill these
      if (i > 0 && _shouldSkipColumn(h)) return;
      // New goal rows: never fill comments regardless of note type
      if (isGoals && includeChoices && i > 0 && _isCommentColumn(h)) return;
      // Existing goal rows: expose columns based on note type
      if (isGoals && !includeChoices && i > 0) {
        if (isDailyNote && !_isCommentColumn(h)) return;
        if ((isProgressNote || isDischarge) && !_isCommentColumn(h) && !_isResultColumn(h)) return;
      }
      // Existing activity rows on Daily Notes: never expose Comment — only Sets/Reps (and other
      // non-comment columns) may be updated.
      if (isActivities && isDailyNote && !includeChoices && i > 0 && _isCommentColumn(h)) return;
      // Activities table: never fill the Time column, on any note type, new or existing rows.
      if (isActivities && i > 0 && _isTimeColumn(h)) return;

      const _existingVal = row[h] || null;
      const fieldDef: Record<string, any> =
        _existingVal !== null ? { _existingValue: _existingVal } : {};
      if (i === 0 && isServiceBased && includeChoices) {
        // Service-based (modality) table has its own, much shorter Raintree picklist —
        // never offer the time-based exercise names here. The list is inlined into the
        // description rather than sent as a `_choices` enum (see _describeChoices).
        fieldDef['_description'] = _describeChoices(
          'Value MUST be one of the allowed values listed below, copied exactly as written. ' +
            'Only select a modality that was ACTUALLY performed during this session — ' +
            'do NOT select an item merely because it sounds similar to something else. ' +
            'If no allowed value is an accurate match, omit it rather than picking incorrectly. ' +
            'Do not duplicate an existing row.',
          _SERVICE_BASED_ACTIVITY_CHOICES
        );
      } else if (
        i === 0 &&
        includeChoices &&
        cappedPickerChoices.length > 0 &&
        !isGoals &&
        !isActivities
      ) {
        fieldDef['_choices'] = cappedPickerChoices;
        fieldDef['_description'] =
          'Value MUST be one of the listed Choices exactly as written. ' +
          'Only select items that were ACTUALLY performed or discussed in this session — ' +
          'do NOT select an item merely because it sounds similar to something else. ' +
          'The name you select must precisely match the activity performed. ' +
          'If no choice is an accurate match, omit it rather than picking incorrectly. ' +
          'Do not duplicate an existing row.';
      } else if (i === 0 && isActivities && includeChoices) {
        // Time-based exercise list is far larger than the 499-item enum cap, so it is
        // inlined into the description instead of being sent as a `_choices` enum.
        fieldDef['_description'] = _describeChoices(
          'Value MUST be one of the allowed values listed below, copied exactly as written. ' +
            'Only select an item that was ACTUALLY performed or discussed in this session — ' +
            'do NOT select an item merely because it sounds similar to something else. ' +
            'If more than one value plausibly matches the activity performed, choose the most specific, ' +
            'longest matching option (e.g. prefer "Standing-Shoulder Abduction-PRE\'s" over "Shoulder Butterflys" ' +
            'if both could apply) rather than a shorter, more generic one. ' +
            'If no allowed value is an accurate match, omit it rather than picking incorrectly. ' +
            'Do not duplicate an existing row.',
          _TIME_BASED_ACTIVITY_CHOICES
        );
      } else if (i === 0 && isGoals && includeChoices) {
        const _existingGoalNames = existingRowData
          .map((r) => r[headers[0]])
          .filter(Boolean);
        const _noRepeat = _existingGoalNames.length > 0
          ? 'Do NOT create a goal that is the same as or similar to any existing goal. ' +
            'Existing goals: ' + _existingGoalNames.map((g) => `"${g}"`).join('; ') + '.'
          : 'Do not duplicate an existing goal.';
        const _newGoalInstruction = isProgressNote
          ? 'ONLY create a new goal when the clinician explicitly states that a new goal should be added or the documentation clearly identifies it as a new goal. ' +
            'Never infer that a new goal is needed based on progress, objective findings, treatment performed, goals being met, or changes in function or plan of care. ' +
            'If there is any uncertainty, do not create it. '
          : '';
        fieldDef['_description'] =
          'Write the full SMART goal text exactly as it should appear in the patient record. ' +
          'Do not use @@ or placeholder syntax — write the complete resolved goal. ' +
          _newGoalInstruction +
          _noRepeat;
      } else if (i === 0 && isActivities && includeChoices) {
        fieldDef['_description'] =
          'Write the exercise or activity name in 20 characters or fewer. ' +
          'Do not duplicate an existing row.';
      }
      // CPT code column: provide choices and 80%-confidence instruction
      if (i > 0 && /cpt/i.test(h)) {
        fieldDef['_choices'] = _CPT_CHOICES;
        fieldDef['_description'] = _CPT_DESCRIPTION;
      }
      // Add column-specific choices/descriptions for goal columns
      if (isGoals) {
        const hNorm = h.toLowerCase().replace(/[\s\-\/]/g, '');
        if (hNorm === 'shortlong' || hNorm === 'shortterm') {
          fieldDef['_choices'] = ['Long Term', 'Short Term'];
        } else if (hNorm === 'result') {
          fieldDef['_choices'] = [
            'Discontinued',
            'Met',
            'New',
            'Not Met',
            'Ongoing',
            'Partially met'
          ];
          if (isProgressNote) {
            fieldDef['_description'] =
              'Default to leaving this field unchanged. ' +
              'Only update when: (1) the visit explicitly identifies the goal status, or ' +
              '(2) an objective measurement documented in the visit can be directly compared to the goal criteria and clearly satisfies or partially satisfies it. ' +
              'Mark Met only when documented findings meet or exceed every measurable criterion. ' +
              'Mark Partially Met only when findings clearly show measurable progress but do not satisfy all criteria. ' +
              'Do not update based on treatment performed, general improvement, perceived improvement, or clinical judgment alone. ' +
              'If the documented information cannot be directly compared to the goal criteria, leave the existing value unchanged.';
          } else if (isDischarge) {
            fieldDef['_description'] =
              'This is a discharge note — mark all goals as Met unless the documentation explicitly states otherwise.';
          }
        } else if (hNorm === 'timeframe') {
          fieldDef['_description'] =
            'Treatment timeframe, e.g., "2 weeks", "4 weeks", "1 month", "10+ weeks", "8-12 weeks", "4-6 weeks". ' +
            'If the timeframe is a range, keep the hyphen between the two numbers (e.g. "8-12 weeks" or "4-6 weeks") — ' +
            'never merge the two numbers together (do not write "812 weeks" or "46 weeks").';
        } else if (isProgressNote && _isCommentColumn(h)) {
          fieldDef['_description'] =
            'Default to leaving this blank. ' +
            'Only populate when the visit documents a clinically meaningful update specific to this goal that adds value beyond the goal, Results status, or objective measurements. ' +
            'Must be concise, clinician-facing, objective, and specific to this goal. ' +
            'Describe documented progress, lack of progress, persistent limitations, or barriers to achieving the goal. ' +
            'Never reference the goal criteria, target values, thresholds, or explain why the Results status was selected (e.g., do not write "meets criterion", "goal achieved", "reached target"). ' +
            'Do not include patient-reported statements ("patient reports", "states", "feels"). ' +
            'Do not repeat measurements documented elsewhere unless necessary to explain progress toward this specific goal. ' +
            'Keep the comment limited to the primary progress update. Do not append additional descriptors such as pain, end-range tightness, stiffness, guarding, compensation, cueing, or movement quality. ' +
            'If there is any uncertainty or the comment does not clearly add clinical value, leave blank.';
        } else if (isDischarge && _isCommentColumn(h)) {
          fieldDef['_description'] =
            'Default to leaving this blank. ' +
            'Only populate if the goal is Not Met at discharge and a brief explanation is documented or clinically apparent. ' +
            'If a comment is needed, use a single concise sentence (typically 3–10 words) stating only the primary reason the goal was not achieved. ' +
            'Do not restate measurements, progress, or final objective findings. ' +
            'Do not summarize the patient\'s overall discharge status. ' +
            'Examples: "Progress limited by persistent pain.", "Discharged before goal completion.", "Progress limited by inconsistent attendance.", "Residual weakness remains." ' +
            'Leave blank when the goal was met, or when the Results field alone adequately reflects the final status.';
        }
      }
      if (isComplaintsOrPrecautions && i === 0 && includeChoices) {
        const _existingNames = existingRowData
          .map((r) => r[headers[0]])
          .filter(Boolean);
        fieldDef['_description'] =
          (fieldDef['_description'] ? fieldDef['_description'] + ' ' : '') +
          'Only add items explicitly stated in this session — never infer. ' +
          'Do not add placeholder entries indicating nothing was reported; if nothing is explicitly stated, omit. ' +
          'Do NOT add an item that duplicates or means the same as an existing row or another item you add.' +
          (_existingNames.length > 0
            ? ' Existing rows: ' + _existingNames.map((n) => `"${n}"`).join('; ') + '.'
            : '');
      }
      if (isComplaintsOrPrecautions && i > 0 && /severity/i.test(h)) {
        fieldDef['_description'] = 'Max 10 characters. Leave blank if not stated.';
      }
      obj[h] = fieldDef;
    });
    // Goals listboxes include Goal Name so existing rows can carry their resolved text.
    // The prototype omits Goal Name — the primary Goals field now carries the full goal text.
    if (isGoals && !includeChoices) {
      obj['Goal Name'] = { _existingValue: null };
    }
    return obj;
  };

  // Goals listboxes: send existing rows (so the AI can update comment/result) followed
  // by a prototype row (so the AI can also suggest new goals).
  // Existing rows carry _existingValue for each column and a description telling the AI
  // to only update comment/result — NOT to re-add them as new entries.
  // The prototype (with _existingValue: null) signals "add new goals here".
  // Non-goals listboxes keep the original behaviour (send all existing rows).
  let rowStructure: Record<string, any>[];
  const prototype = buildRowEntry(
    Object.fromEntries(headers.map((h) => [h, ''])),
    true
  );
  if (isGoals) {
    const _buildExistingGoalRows = () =>
      existingRowData.map((row) => buildRowEntry(row, false));

    if (isInitialEval) {
      rowStructure = [prototype];
    } else if (isDailyNote) {
      rowStructure = existingRowData.length > 0 ? _buildExistingGoalRows() : [];
    } else if (isProgressNote) {
      rowStructure = existingRowData.length > 0
        ? [..._buildExistingGoalRows(), prototype]
        : [prototype];
    } else if (isDischarge) {
      rowStructure = existingRowData.length > 0 ? _buildExistingGoalRows() : [];
    } else {
      rowStructure = existingRowData.length > 0
        ? [..._buildExistingGoalRows(), prototype]
        : [prototype];
    }
  } else if (isServiceBased) {
    // Service-based modalities: prototype only — entered fresh each visit.
    rowStructure = [prototype];
  } else if (isActivities) {
    // Time-based exercises: send existing rows (AI can update sets/reps/status) + prototype.
    // Primary field of existing rows is read-only — only comment/sets/reps/etc. may change.
    // On Daily Notes, Comment is never updated — only Sets/Reps (when applicable) or other
    // non-comment fields may change; Comment is excluded from the payload entirely (see
    // buildRowEntry's isActivities && isDailyNote skip above).
    rowStructure =
      existingRowData.length > 0
        ? [
            ...existingRowData.map((row) => {
              const entry = buildRowEntry(row, false);
              const primaryField = entry[headers[0]];
              if (primaryField && typeof primaryField === 'object') {
                primaryField['_description'] = isDailyNote
                  ? 'EXISTING activity — do NOT change this name. ' +
                    'Never update Comment. Only update Sets and Reps if applicable (i.e., explicitly documented in the transcript). ' +
                    'Leave Sets/Reps unchanged if not mentioned.'
                  : 'EXISTING activity — do NOT change this name. ' +
                    'Only update Comment, Sets, Reps, Done Today, or Status if mentioned in the transcript. ' +
                    'Never update Time.';
              }
              return entry;
            }),
            prototype
          ]
        : [prototype];
  } else {
    // All other listboxes: send existing rows so the AI can update/extend them.
    rowStructure =
      existingRowData.length > 0
        ? existingRowData.map((row, idx) => buildRowEntry(row, idx === 0))
        : [prototype];
  }

  fields[label] = {
    // flex++ list: backend expand_flexpp_lists_recursively handles array sizing
    Structure: rowStructure,
    // Input receives one row object at a time — answerFlexQuestion iterates the AI's array
    Input: (val: Record<string, string> | string) => {
      if (isEmptyOrNull(val as string)) return;
      let rowObj: Record<string, string>;
      if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        rowObj = val as Record<string, string>;
      } else {
        const parts = String(val)
          .split('|')
          .map((v) => v.trim());
        rowObj = {};
        headers.forEach((h, i) => {
          rowObj[h] = parts[i] || '';
        });
      }
      if (!_pickerBatch.has(batchKey)) _pickerBatch.set(batchKey, []);
      _pickerBatch.get(batchKey)!.push(rowObj);
    }
  };
  return true;
}

// Absence placeholders the AI adds alongside real items (e.g. "None Reported").
const _ABSENCE_ROW_RE =
  /^(none( reported)?|not discussed|n\/a|no precautions or contraindications)$/i;

/** Drop rows already on the chart or repeated in the batch; drop absence placeholders when a
 *  real item exists, otherwise keep at most one. */
function _dedupeListRows(
  targets: Record<string, string>[],
  primaryKey: string,
  existing: Record<string, string>[]
): Record<string, string>[] {
  const norm = (v?: string) => (v || '').trim().toLowerCase();
  const seen = new Set(existing.map((r) => norm(r[primaryKey])).filter(Boolean));
  const unique = targets.filter((r) => {
    const v = norm(r[primaryKey]);
    if (!v || seen.has(v)) return false;
    seen.add(v);
    return true;
  });
  const isAbsence = (v?: string) => _ABSENCE_ROW_RE.test((v || '').trim());
  const hasReal =
    unique.some((r) => !isAbsence(r[primaryKey])) ||
    existing.some((r) => norm(r[primaryKey]) && !isAbsence(r[primaryKey]));
  const absence = unique.filter((r) => isAbsence(r[primaryKey]));
  const real = unique.filter((r) => !isAbsence(r[primaryKey]));
  return hasReal ? real : absence.slice(0, 1);
}

/** Find a row in a listbox by matching the primary (first non-checkbox) cell text. */
function findRowInListbox(
  listbox: Element,
  primaryValue: string
): Element | null {
  const pvLower = primaryValue.toLowerCase();
  const rows = Array.from(listbox.querySelectorAll('tbody tr'));
  const getCellText = (row: Element) =>
    (Array.from(row.querySelectorAll('td'))
      .filter((td) => !td.classList.contains('w-listbox-extended-checkbox'))[0]
      ?.textContent?.trim() || '');

  // First pass: exact match or matchesChoice (handles wildcards / smart quotes)
  for (const row of rows) {
    if (matchesChoice(primaryValue, getCellText(row))) return row;
  }

  // Second pass: strip trailing ellipsis then prefix-match (handles DOM truncation)
  for (const row of rows) {
    const cellNorm = getCellText(row).toLowerCase().replace(/[…]|\.{2,}$/, '').trim();
    if (cellNorm.length >= 20 && pvLower.startsWith(cellNorm)) return row;
    if (cellNorm.length >= 20 && cellNorm.startsWith(pvLower)) return row;
  }

  return null;
}

/**
 * Add or edit one row in a row-list listbox.
 * rowObj keys are column header names; first column is the primary/picker value.
 * Column 0 is auto-filled by the picker; columns 1+ are filled in the edit form.
 */
async function fillRowListRow(
  listboxId: string,
  rowObj: Record<string, string>,
  headers: string[],
  skipIfExists = false,
  skipIfNew = false
) {
  const primaryKey = headers[0];
  const primaryValue = rowObj[primaryKey]?.trim();
  if (isEmptyOrNull(primaryValue)) return;

  // Track any TinyMCE memo field filled in this row so we can flush the diff
  // sync (and wait for server ACK) before clicking OK — Raintree commits memo
  // content to its store via a periodic sync, not on OK.
  let filledMemoEl: HTMLElement | null = null;
  let filledMemoXHTML: string | null = null;

  // When the AI generates a free-text goal (no choices sent), we pick a @@ picker item
  // as a vessel to create the row, then overwrite the goal text in the edit form.
  // vesselText is the picker item's text used to find the row; primaryValue has the real goal.
  let vesselText: string | null = null;

  let freshListbox: Element | null = listboxId
    ? document.getElementById(listboxId)
    : null;
  if (!freshListbox) {
    console.warn(
      `[Raintree] fillRowListRow: listbox not found id="${listboxId}"`
    );
    return;
  }

  const existingRow = findRowInListbox(freshListbox, primaryValue);
  if (existingRow && skipIfExists) return;
  if (!existingRow && skipIfNew) return;
  let targetRow: Element | null = existingRow ?? null;

  if (!existingRow) {
    // Add via picker
    const addBtn = freshListbox.querySelector(
      '.w-listbox-toolbar-button.mdi-plus-box'
    ) as HTMLElement | null;
    if (!addBtn) {
      console.warn(
        `[Raintree] fillRowListRow: addBtn not found for "${primaryValue}"`
      );
      return;
    }

    // If pckselector is already open (stuck from a previous run), use it directly
    // without clicking addBtn — clicking addBtn while picker is open could corrupt state
    let picker: Element | null = null;
    const pckAlreadyOpen = document.querySelector(
      '[data-widgetname="pckselector"] .w-picker'
    ) as Element | null;
    if (
      pckAlreadyOpen?.classList.contains('openview') &&
      pckAlreadyOpen.querySelectorAll('.w-picker-item').length > 0
    ) {
      picker = pckAlreadyOpen;
    }

    if (!picker) {
      const pickersBefore = new Set(
        Array.from(document.querySelectorAll('.w-picker'))
      );
      // pckselector's inner .w-picker is always in DOM; detect activation via openview class
      const pckInnerBefore2 = document.querySelector(
        '[data-widgetname="pckselector"] .w-picker'
      );
      const pckWasOpenBefore2 =
        pckInnerBefore2?.classList.contains('openview') ?? false;
      addBtn.click();
      let waited = 0;
      while (waited < 2000) {
        // Standard .w-picker overlay (new element added to DOM)
        for (const p of Array.from(document.querySelectorAll('.w-picker'))) {
          if (
            !pickersBefore.has(p) &&
            p.querySelectorAll('.w-picker-item').length > 0
          ) {
            picker = p;
            break;
          }
        }
        if (picker) break;
        // pckselector: same .w-picker gains openview class when screen becomes active
        const pckInnerNow2 = document.querySelector(
          '[data-widgetname="pckselector"] .w-picker'
        ) as Element | null;
        if (
          pckInnerNow2 &&
          !pckWasOpenBefore2 &&
          pckInnerNow2.classList.contains('openview') &&
          pckInnerNow2.querySelectorAll('.w-picker-item').length > 0
        ) {
          picker = pckInnerNow2;
          break;
        }
        await new Promise((r) => setTimeout(r, 100));
        waited += 100;
      }
    }
    if (!picker) {
      console.warn(
        `[Raintree] fillRowListRow: picker did not open for "${primaryValue}"`
      );
      // Close any picker that may have opened without being detected
      const anyClose = (document.querySelector(
        '.w-picker-toolbar .mdi-content-save:not(.disabled)'
      ) ??
        document.querySelector('.backdown .backbtn') ??
        document.querySelector('[id^="backbutton-"]') ??
        document.querySelector('.backbtn') ??
        document.querySelector('.backdown')) as HTMLElement | null;
      if (anyClose) {
        anyClose.click();
        await new Promise((r) => setTimeout(r, 400));
      }
      return;
    }

    // Find and select the matching picker item (@@-aware)
    const items = Array.from(picker.querySelectorAll('.w-picker-item'));
    let matched = false;
    for (const item of items) {
      const text = item.querySelector('.text')?.textContent?.trim() || '';
      if (matchesChoice(primaryValue, text)) {
        const cb = item.querySelector(
          'input[type="checkbox"]'
        ) as HTMLInputElement | null;
        if (cb && !cb.checked) {
          // Click the item div itself (Raintree may listen on the container, not the checkbox)
          (item as HTMLElement).click();
          await new Promise((r) => setTimeout(r, 50));
          // Also click the checkbox directly if it's still unchecked
          if (!cb.checked) cb.click();
          await new Promise((r) => setTimeout(r, 100));
        }
        matched = true;
        break;
      }
    }
    if (!matched) {
      // For Goals: pick a @@ vessel item so the row can be added, then override
      // the goal text in the edit form with the AI's full goal string.
      // Prefer a @@ template (it has a text input in the edit form for the override).
      const vesselItem = (
        items.find((item) =>
          (item.querySelector('.text')?.textContent?.trim() || '').includes('@@')
        ) ?? items[0]
      ) as HTMLElement | null;

      if (vesselItem) {
        const cb = vesselItem.querySelector(
          'input[type="checkbox"]'
        ) as HTMLInputElement | null;
        if (cb && !cb.checked) {
          vesselItem.click();
          await new Promise((r) => setTimeout(r, 50));
          if (!cb.checked) cb.click();
          await new Promise((r) => setTimeout(r, 100));
        }
        vesselText =
          vesselItem.querySelector('.text')?.textContent?.trim() || '';
        matched = true;
      } else {
        console.warn(
          `[Raintree] fillRowListRow: no picker items available for "${primaryValue}"`
        );
        // Close picker without saving
        const saveOrBackNoMatch = (document.querySelector(
          '.w-picker-toolbar .mdi-content-save:not(.disabled)'
        ) ??
          document.querySelector('.backdown .backbtn') ??
          document.querySelector('[id^="backbutton-"]') ??
          document.querySelector('.backbtn') ??
          document.querySelector('.backdown')) as HTMLElement | null;
        saveOrBackNoMatch?.click();
        let nmCloseWait = 0;
        while (nmCloseWait < 2000) {
          await new Promise((r) => setTimeout(r, 100));
          nmCloseWait += 100;
          const pckChk = document.querySelector(
            '[data-widgetname="pckselector"] .w-picker'
          );
          if (!pckChk?.classList.contains('openview')) break;
        }
        await new Promise((r) => setTimeout(r, 400));
        return;
      }
    }

    // Save commits the checked selection; back would discard it
    const saveBtn2 = document.querySelector(
      '.w-picker-toolbar .mdi-content-save:not(.disabled)'
    ) as HTMLElement | null;
    const backBtn2 = (document.querySelector('.backdown .backbtn') ??
      document.querySelector('[id^="backbutton-"]') ??
      document.querySelector('.backbtn') ??
      document.querySelector('.backdown')) as HTMLElement | null;
    const closeTarget = saveBtn2 ?? backBtn2;
    closeTarget?.click();
    // Wait for picker to close before polling for the new row
    let pckCloseWait = 0;
    while (pckCloseWait < 2000) {
      await new Promise((r) => setTimeout(r, 100));
      pckCloseWait += 100;
      const pckChk2 = document.querySelector(
        '[data-widgetname="pckselector"] .w-picker'
      );
      if (!pckChk2?.classList.contains('openview')) break;
    }
    // Poll for the newly added row — Raintree's React reconciler may take
    // longer than a single fixed wait, especially for the last row in a list.
    // When a vessel item was used, search by the vessel's text (not the AI goal text).
    const rowSearchText = vesselText || primaryValue;
    let rowWaited = 0;
    while (rowWaited <= 3000) {
      freshListbox = listboxId
        ? document.getElementById(listboxId)
        : freshListbox;
      if (freshListbox) {
        targetRow = findRowInListbox(freshListbox, rowSearchText);
        if (targetRow) break;
      }
      await new Promise((r) => setTimeout(r, 200));
      rowWaited += 200;
    }
    if (!targetRow) {
      console.warn(
        `[Raintree] fillRowListRow: newly added row not found for "${rowSearchText}"`
      );
      return;
    }
  }

  // "Goal Name" carries the @@ -resolved goal text for goals with placeholders.
  const goalNameValue =
    (rowObj['Goal Name'] as string | undefined)?.trim() || '';

  // Nothing extra to fill in the edit form — done
  const hasExtraData =
    !!goalNameValue || headers.slice(1).some((h) => rowObj[h]?.trim());
  if (!hasExtraData) return;

  // Right-click the first data cell to open Raintree's context menu, then click Edit.
  const firstDataCell = Array.from(targetRow.querySelectorAll('td')).find(
    (td) =>
      !td.classList.contains('w-listbox-extended-checkbox') &&
      !td.classList.contains('w-listbox-toolbar-cell')
  ) as HTMLElement | null;
  const clickTarget = firstDataCell ?? (targetRow as HTMLElement);
  clickTarget.dispatchEvent(
    new MouseEvent('contextmenu', {
      bubbles: true,
      cancelable: true,
      button: 2
    })
  );

  // Wait for the context menu, then click the Edit item. Raintree renders
  // its context menu as `.popup-overlay > .popup` with `.popup-item[aria-label]`
  // children — NOT `.w-context-menu`. Match on aria-label so we pick the right
  // action regardless of the menu's text formatting (which uses `<u>` for the
  // keyboard-shortcut letter).
  let contextMenu: Element | null = null;
  for (let attempt = 0; attempt < 20; attempt++) {
    await new Promise((r) => setTimeout(r, 100));
    contextMenu = document.querySelector('.popup-overlay') ?? null;
    if (contextMenu) break;
  }
  if (contextMenu) {
    const editItem = contextMenu.querySelector(
      '.popup-item[aria-label="Edit"]'
    ) as HTMLElement | null;
    if (editItem) {
      editItem.click();
    } else {
      console.warn(
        '[Raintree] fillRowListRow: Edit item not found in context menu'
      );
      return;
    }
  } else {
    // Fallback: click row to focus then use toolbar pen button
    console.warn(
      '[Raintree] fillRowListRow: context menu did not open, trying toolbar'
    );
    clickTarget.click();
    await new Promise((r) => setTimeout(r, 200));
    const freshListbox2 = listboxId
      ? document.getElementById(listboxId)
      : freshListbox;
    const freshEditBtn = freshListbox2?.querySelector(
      '.w-listbox-toolbar-button.mdi-pen'
    ) as HTMLElement | null;
    if (!freshEditBtn) {
      console.warn(
        `[Raintree] fillRowListRow: editBtn not found after row select`
      );
      return;
    }
    freshEditBtn.click();
  }

  // Poll for the edit form container — up to 2 s in 100 ms increments
  let formContainer: Element | null = null;
  for (let attempt = 0; attempt < 20; attempt++) {
    await new Promise((r) => setTimeout(r, 100));
    formContainer =
      document
        .querySelector('[data-widgetname="btnok"]')
        ?.closest('.screen-inner') ?? null;
    if (formContainer) break;
  }

  // Fill columns 1+ in the edit form by matching label[for] text to header names.
  // Scope to formContainer so we don't match stale labels from other parts of the page.
  const labelRoot = formContainer ?? document;

  // Clear all AI-known secondary fields first so stale existing values don't persist
  // when the AI returns empty / "Not Discussed" for a column.
  for (const h of Object.keys(rowObj)) {
    if (h === headers[0] || h === 'Goal Name') continue;
    const clbl = Array.from(labelRoot.querySelectorAll('label[for]')).find(
      (l) => l.textContent?.trim().toLowerCase() === h.toLowerCase()
    ) as HTMLLabelElement | null;
    if (!clbl) continue;
    const cFieldId = clbl.getAttribute('for');
    if (!cFieldId) continue;
    const cFieldEl = document.getElementById(cFieldId) as HTMLElement | null;
    if (!cFieldEl) continue;
    if (/cpt/i.test(h)) {
      // CPT field is a w-picker — uncheck any selected radio button
      const cPickerEl = (formContainer ?? document).querySelector('.w-picker') as HTMLElement | null;
      if (cPickerEl) {
        const checkedRadio = cPickerEl.querySelector('input[type="radio"]:checked') as HTMLInputElement | null;
        if (checkedRadio) {
          checkedRadio.checked = false;
          checkedRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      continue;
    }
    if (cFieldEl.tagName === 'INPUT' || cFieldEl.tagName === 'TEXTAREA') {
      nativeInputFill(cFieldEl as HTMLInputElement, '');
    } else if (cFieldEl.tagName === 'SELECT') {
      const cSel = cFieldEl as HTMLSelectElement;
      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLSelectElement.prototype, 'value'
      )?.set;
      if (nativeSetter) nativeSetter.call(cSel, '');
      else cSel.value = '';
      cSel.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  for (let i = 1; i < headers.length; i++) {
    const colValue = rowObj[headers[i]]?.trim();
    if (isEmptyOrNull(colValue)) continue;

    // CPT column uses a w-picker widget (radio buttons) — click the matching item directly
    if (/cpt/i.test(headers[i])) {
      const pickerEl = (formContainer ?? document).querySelector('.w-picker') as HTMLElement | null;
      if (pickerEl) {
        const items = Array.from(pickerEl.querySelectorAll('.w-picker-item'));
        const match = items.find((item) => {
          const txt = (item.querySelector('.text') as HTMLElement | null)?.textContent?.trim() || '';
          return matchesChoice(colValue, txt);
        });
        if (match) {
          const radio = match.querySelector('input[type="radio"]') as HTMLInputElement | null;
          if (radio) radio.click();
        } else {
          console.warn(
            `[Raintree] fillRowListRow: no CPT picker match for "${headers[i]}" value="${colValue}"`
          );
        }
        continue;
      }
    }

    const lbl = Array.from(labelRoot.querySelectorAll('label[for]')).find(
      (l) => l.textContent?.trim().toLowerCase() === headers[i].toLowerCase()
    ) as HTMLLabelElement | null;
    if (!lbl) {
      console.warn(
        `[Raintree] fillRowListRow: label not found for column "${headers[i]}"`
      );
      continue;
    }
    const fieldId = lbl.getAttribute('for');
    if (!fieldId) continue;
    const fieldEl = document.getElementById(fieldId) as HTMLElement | null;
    if (!fieldEl) {
      console.warn(
        `[Raintree] fillRowListRow: element not found for "${headers[i]}" id="${fieldId}"`
      );
      continue;
    }

    if (fieldEl.tagName === 'INPUT' || fieldEl.tagName === 'TEXTAREA') {
      // Row-edit modal is Raintree's native widget system (not React).
      // Must simulate full focus→input→blur sequence so Raintree's change
      // handler picks up the new value before the OK button is clicked.
      const inputEl = fieldEl as HTMLInputElement;
      inputEl.focus();
      inputEl.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
      nativeInputFill(inputEl, colValue);
      inputEl.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
      inputEl.blur();
    } else if (fieldEl.tagName === 'SELECT') {
      const sel = fieldEl as HTMLSelectElement;
      const opt = Array.from(sel.options).find(
        (o) =>
          matchesChoice(colValue, o.textContent?.trim() || '') ||
          matchesChoice(colValue, o.value)
      );
      if (opt) {
        sel.value = opt.value;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        console.warn(
          `[Raintree] fillRowListRow: no select option match for "${headers[i]}" value="${colValue}"`
        );
      }
    } else if (fieldEl.classList.contains('w-memo-content')) {
      // TinyMCE on popup memo fields (e.g. Goals Comment) uses lazy init — it only
      // activates on a trusted user click, so bubbleFocus/focus won't work.
      // Instead: try setTinyMCEContent immediately; if no editor found, force-init
      // via main_world.js (which clones settings from a live editor) and retry.
      //
      // Also: Raintree's edit modal opens with the memo blank, then async-loads
      // the row's existing comment a few hundred ms later. If we setContent
      // before that load settles, the async load overwrites our value and OK
      // saves the wrong thing. Poll innerHTML stability before setContent.
      const waitForMemoSettle = async (
        el: HTMLElement,
        quietMs: number,
        timeoutMs: number
      ): Promise<void> => {
        let last = el.innerHTML;
        let stableSince = Date.now();
        const start = Date.now();
        while (Date.now() - start < timeoutMs) {
          await new Promise((r) => setTimeout(r, 100));
          const cur = el.innerHTML;
          if (cur !== last) {
            last = cur;
            stableSince = Date.now();
          }
          if (Date.now() - stableSince >= quietMs) return;
        }
      };
      const trySetContent = async (
        id: string,
        value: string
      ): Promise<string> => {
        const reqId = `${id}_${Date.now()}`;
        return new Promise<string>((resolve) => {
          const handler = (e: MessageEvent) => {
            if (
              e.source === window &&
              e.data.action === 'setTinyMCEContent_response' &&
              e.data.reqId === reqId
            ) {
              window.removeEventListener('message', handler);
              resolve(e.data.result as string);
            }
          };
          window.addEventListener('message', handler);
          window.postMessage(
            { action: 'setTinyMCEContent', id, value, reqId },
            '*'
          );
          setTimeout(() => {
            window.removeEventListener('message', handler);
            resolve('timeout');
          }, 2000);
        });
      };

      const forceInitTinyMCE = async (id: string): Promise<string> => {
        const reqId = `init_${id}_${Date.now()}`;
        return new Promise<string>((resolve) => {
          const handler = (e: MessageEvent) => {
            if (
              e.source === window &&
              e.data.action === 'initTinyMCEEditor_response' &&
              e.data.reqId === reqId
            ) {
              window.removeEventListener('message', handler);
              resolve(e.data.result as string);
            }
          };
          window.addEventListener('message', handler);
          window.postMessage({ action: 'initTinyMCEEditor', id, reqId }, '*');
          setTimeout(() => {
            window.removeEventListener('message', handler);
            resolve('timeout');
          }, 4000);
        });
      };

      // Phase 1: ensure TinyMCE has bound the editor for this memo. Try
      // cloning settings from a live editor first; fall back to focus-init
      // polling. Don't setContent yet — Raintree's edit modal async-loads
      // the row's existing comment, and a setContent that fires before the
      // load completes gets clobbered.
      const initResult = await forceInitTinyMCE(fieldEl.id);
      if (initResult === 'no_existing_editors' || initResult === 'no_tinymce') {
        // Focus-init polling: dispatch bubbling focusin + element.focus, wait
        // for contentEditable to flip true (TinyMCE's lazy bind trigger).
        let el: HTMLElement | null = fieldEl;
        bubbleFocus(el);
        el.focus();
        let waited = 0;
        while (el.contentEditable !== 'true' && waited < 3000) {
          await new Promise((r) => setTimeout(r, 200));
          waited += 200;
          el = document.getElementById(fieldEl.id) as HTMLElement | null;
          if (!el) break;
          if (waited % 1000 === 0) {
            bubbleFocus(el);
            el.focus();
          }
        }
      }

      // Phase 2: wait for Raintree's async content-load to settle (600ms with
      // no DOM mutation). Without this, our setContent races the load and the
      // server commit ends up with the original value, not ours.
      await waitForMemoSettle(fieldEl, 600, 4000);

      // Phase 3: single setContent — now the final state of the editor.
      const setResult = await trySetContent(fieldEl.id, colValue);

      if (setResult === 'success') {
        await new Promise((r) => setTimeout(r, 300));
      } else {
        console.warn(
          `[Raintree] fillRowListRow: TinyMCE fill failed (${setResult}), falling back to Widget.HTML`
        );
      }
      // Track the memo element + XHTML so we can commit the edit below
      // (fallback Widget.HTML stamp uses the XHTML, primary path doesn't).
      filledMemoEl = fieldEl;
      filledMemoXHTML = buildRaintreeXHTML(colValue);
    } else {
      // Wrapper element — look for a nested select or input (e.g. combobox wrapper div)
      const nestedSel = fieldEl.querySelector(
        'select'
      ) as HTMLSelectElement | null;
      const nestedInput = fieldEl.querySelector(
        'input'
      ) as HTMLInputElement | null;
      if (nestedSel) {
        const opt = Array.from(nestedSel.options).find(
          (o) =>
            matchesChoice(colValue, o.textContent?.trim() || '') ||
            matchesChoice(colValue, o.value)
        );
        if (opt) {
          nestedSel.value = opt.value;
          nestedSel.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          console.warn(
            `[Raintree] fillRowListRow: no nested select option match for "${headers[i]}" value="${colValue}"`
          );
        }
      } else if (nestedInput) {
        nativeInputFill(nestedInput, colValue);
      } else {
        console.warn(
          `[Raintree] fillRowListRow: unhandled element type for "${headers[i]}" tag=${fieldEl.tagName}`
        );
      }
    }
  }

  // Fill goal text into the edit form.
  // Case 1 (vessel fallback): vesselText is set — the AI returned free-form goal text.
  //   Use primaryValue as the override; find the @@ text input in the vessel row's form.
  // Case 2 (picker match with @@): the picked goal template has @@ placeholders.
  //   Use goalNameValue (the AI's resolved text) to fill the @@ input.
  const goalTextToFill = vesselText
    ? primaryValue
    : goalNameValue && primaryValue?.includes('@@')
    ? goalNameValue
    : null;

  if (goalTextToFill) {
    const formInputs = Array.from(
      labelRoot.querySelectorAll('input[type="text"], textarea')
    ) as HTMLInputElement[];
    const atAtInput = formInputs.find((el) => el.value.includes('@@'));
    // When using a vessel, fall back to the first text input if no @@ input is found.
    const targetInput = atAtInput ?? (vesselText ? formInputs[0] ?? null : null);
    if (targetInput) {
      targetInput.focus();
      targetInput.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
      nativeInputFill(targetInput, goalTextToFill);
      targetInput.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
      targetInput.blur();
    } else {
      console.warn(
        '[Raintree] fillRowListRow: no goal text input found in edit form'
      );
    }
  }

  await new Promise((r) => setTimeout(r, 300));

  // Real user typing calls inst.setMemoChanged() on each keystroke (marks the
  // modal row dirty so OK propagates to the parent listbox) AND triggers
  // periodic syncs of the diff to the server. Our programmatic setContent does
  // neither, so OK closes the modal with the cell reverting. Drive both
  // explicitly via commitRaintreeMemoEdit, then give the sync ~300ms to land.
  if (filledMemoEl) {
    const committed = commitRaintreeMemoEdit(filledMemoEl);
    if (committed) {
      // Give the diff sync a moment to land on the server before OK closes.
      await new Promise((r) => setTimeout(r, 700));
    } else {
      // No React fiber (e.g. PCKINPUTFORM memo) — setTinyMCEContent already fired
      // blur+change events; give them time to propagate before clicking OK.
      await new Promise((r) => setTimeout(r, 500));
      if (filledMemoXHTML) {
        setRaintreeWidgetHTML(filledMemoEl, filledMemoXHTML);
      }
    }
  }

  // Re-query from document (formContainer may be stale after TinyMCE remount)
  // Click the inner w-image-button (role="button"), not the outer widget wrapper
  const okWrapper = document.querySelector(
    '[data-widgetname="btnok"]'
  ) as HTMLElement | null;
  const okBtn = (okWrapper?.querySelector('.w-image-button') ??
    okWrapper) as HTMLElement | null;
  if (okBtn) {
    okBtn.click();
    // Wait for the modal to actually close (btnok leaves the DOM) before
    // the next row opens its context menu — fixed 500ms is too short and
    // causes the next row to operate against a still-open modal.
    let closeWait = 0;
    while (
      closeWait < 3000 &&
      document.querySelector('[data-widgetname="btnok"]')
    ) {
      await new Promise((r) => setTimeout(r, 100));
      closeWait += 100;
    }
    await new Promise((r) => setTimeout(r, 200));

    // Exercises PCKINPUTFORM: after OK, Raintree may navigate back to the
    // exercises picker screen (openview) instead of the main form. If the
    // picker is now open again, commit+close it so the next iteration can
    // find the listbox and call addBtn without the picker blocking the DOM.
    const pickerAfterOK = document.querySelector(
      '[data-widgetname="pckselector"] .w-picker'
    ) as HTMLElement | null;
    if (pickerAfterOK?.classList.contains('openview')) {
      const saveAfterOK = (document.querySelector(
        '.w-picker-toolbar .mdi-content-save:not(.disabled)'
      ) ??
        document.querySelector('.backdown .backbtn') ??
        document.querySelector('[id^="backbutton-"]') ??
        document.querySelector('.backbtn')) as HTMLElement | null;
      if (saveAfterOK) {
        saveAfterOK.click();
        let postOKWait = 0;
        while (postOKWait < 2000) {
          await new Promise((r) => setTimeout(r, 100));
          postOKWait += 100;
          const pckChk3 = document.querySelector(
            '[data-widgetname="pckselector"] .w-picker'
          );
          if (!pckChk3?.classList.contains('openview')) break;
        }
        await new Promise((r) => setTimeout(r, 300));
      }
    }
  } else {
    console.warn('[Raintree] fillRowListRow: btnOK not found');
  }
}

// If the "Abandon Changes?" dialog is showing, click Save and wait for it to close.
// The dialog is the innermost .screen.screen-active — querySelectorAll + last() is needed
// because querySelector only returns the first match (the main note, not the topmost dialog).
// maxWaitMs: if > 0, poll for the dialog to appear before giving up (handles async appearance).
async function dismissAbandonChanges(maxWaitMs = 0): Promise<void> {
  const findDialog = (): HTMLElement | null => {
    const allActive = Array.from(
      document.querySelectorAll('.screen.screen-active')
    );
    return (
      (allActive
        .reverse()
        .find(
          (s) =>
            s.querySelector('.screen-caption')?.textContent?.trim() ===
              'Abandon Changes?' ||
            s.getAttribute('aria-label') === 'Abandon Changes?'
        ) as HTMLElement | null) ?? null
    );
  };

  let dialog = findDialog();
  if (!dialog && maxWaitMs > 0) {
    const deadline = Date.now() + maxWaitMs;
    while (!dialog && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 100));
      dialog = findDialog();
    }
  }
  if (!dialog) return;

  // Strategy 1: DYNMENU item at index 2 (Top: 2 in Raintree protocol = Save).
  // Order is always Yes=0, No=1, Save=2 — matches the e.MouseClick {Top: 2} the server expects.
  const menuItems = Array.from(dialog.querySelectorAll('.w-text-dialog'));
  let saveBtn = (menuItems[2] ?? null) as HTMLElement | null;

  // Strategy 2: aria-keyshortcuts="s" (older Raintree dialog style)
  if (!saveBtn) {
    saveBtn = dialog.querySelector(
      '[aria-keyshortcuts="s"]'
    ) as HTMLElement | null;
  }

  // Strategy 3: any leaf element whose text is exactly "Save"
  if (!saveBtn) {
    for (const el of Array.from(dialog.querySelectorAll('*'))) {
      if (el.children.length === 0 && el.textContent?.trim() === 'Save') {
        saveBtn =
          (el.closest(
            '[role="button"], button, .screen-item, .w-image-button'
          ) as HTMLElement | null) ?? (el as HTMLElement);
        break;
      }
    }
  }

  // Strategy 4: keyboard 's' on the dialog itself
  if (!saveBtn) {
    console.warn(
      '[Raintree] dismissAbandonChanges: Save button not found — trying keyboard'
    );
    dialog.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 's',
        bubbles: true,
        cancelable: true
      })
    );
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 100));
      if (!dialog.classList.contains('screen-active')) return;
    }
    return;
  }

  saveBtn.click();
  for (let i = 0; i < 30; i++) {
    await new Promise((r) => setTimeout(r, 100));
    if (!dialog.classList.contains('screen-active')) break;
  }
  await new Promise((r) => setTimeout(r, 300));
}

// Informational "Notice" popups (single OK button) that Raintree throws up mid-fill —
// e.g. "Note that only the focused line is affected when there are some lines selected...".
// They're purely advisory but they steal focus and swallow keystrokes, so a row fill that
// runs into one will silently write into nothing. Click OK and carry on.
// Returns true if a notice was found and dismissed.
async function dismissNoticeDialog(maxWaitMs = 0): Promise<boolean> {
  // Locate by caption text rather than by a `.screen.screen-active` wrapper: the notice
  // markup is `.screen-inner > .screen-header > .screen-caption` and the outer wrapper does
  // not reliably carry `screen-active`, so keying off that class missed the dialog entirely.
  const findDialog = (): HTMLElement | null => {
    // Two independent signals, most reliable first:
    //  (a) Raintree stamps the screen id into every child id — "id._MSG_NOTICE!!11.btn_0",
    //      "backbutton-_MSG_NOTICE!!11". That token is unambiguous and locale-independent.
    //  (b) the visible caption text "Notice" (breaks under a non-English locale, hence (a)).
    const anchors = [
      ...Array.from(document.querySelectorAll('[id*="_MSG_NOTICE"]')),
      ...Array.from(document.querySelectorAll('.screen-caption')).filter(
        (c) => (c.textContent || '').trim() === 'Notice'
      )
    ].reverse(); // innermost / most recently opened dialog first
    for (const cap of anchors) {
      const container = (cap.closest('.screen') ||
        cap.closest('.screen-inner') ||
        cap.parentElement?.parentElement) as HTMLElement | null;
      if (!container) continue;
      // Must be on screen — stale hidden dialogs linger in the DOM.
      if (
        container.offsetParent === null &&
        container.getClientRects().length === 0
      )
        continue;
      // Only auto-dismiss when OK is the sole action — anything offering a real choice
      // (Yes/No, Save/Discard) must not be answered on the user's behalf.
      if (!container.querySelector('[aria-label="OK"], [aria-keyshortcuts="o"]'))
        continue;
      return container;
    }
    return null;
  };

  /** The dialog is gone once it leaves the DOM or stops being rendered. Can't rely on the
   *  `screen-active` class here for the same reason findDialog can't. */
  const isGone = (el: HTMLElement): boolean =>
    !el.isConnected ||
    (el.offsetParent === null && el.getClientRects().length === 0);

  let dialog = findDialog();
  if (!dialog && maxWaitMs > 0) {
    const deadline = Date.now() + maxWaitMs;
    while (!dialog && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 100));
      dialog = findDialog();
    }
  }
  if (!dialog) return false;

  let okBtn = dialog.querySelector(
    '[aria-label="OK"], [aria-keyshortcuts="o"]'
  ) as HTMLElement | null;

  // Fallback: any leaf whose text is "OK" (the markup nests it as <span><u>O</u>K</span>,
  // so match on the trimmed text of the button wrapper rather than an exact leaf match).
  if (!okBtn) {
    for (const el of Array.from(
      dialog.querySelectorAll('.w-image-button, [role="button"], button')
    )) {
      if ((el.textContent || '').replace(/\s+/g, '').toUpperCase() === 'OK') {
        okBtn = el as HTMLElement;
        break;
      }
    }
  }
  if (!okBtn) {
    console.warn('[Raintree] dismissNoticeDialog: OK button not found');
    return false;
  }

  // Raintree's image-buttons are role="button" divs; a bare .click() is usually enough but
  // some handlers only listen for pointer/mouse sequences, so escalate if it doesn't close.
  const clickStrategies: Array<() => void> = [
    () => okBtn!.click(),
    () => {
      for (const type of ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click']) {
        okBtn!.dispatchEvent(
          new MouseEvent(type, { bubbles: true, cancelable: true, view: window })
        );
      }
    },
    () => {
      // aria-keyshortcuts="o" — the accelerator on the OK button.
      okBtn!.focus?.();
      for (const target of [okBtn!, dialog!]) {
        for (const key of ['o', 'Enter']) {
          target.dispatchEvent(
            new KeyboardEvent('keydown', { key, bubbles: true, cancelable: true })
          );
        }
      }
    }
  ];

  console.log('[Raintree] dismissNoticeDialog: notice detected, clicking OK');
  for (const attempt of clickStrategies) {
    attempt();
    for (let i = 0; i < 15; i++) {
      await new Promise((r) => setTimeout(r, 100));
      if (isGone(dialog)) {
        await new Promise((r) => setTimeout(r, 150));
        console.log('[Raintree] dismissNoticeDialog: notice dismissed');
        return true;
      }
    }
    console.warn(
      '[Raintree] dismissNoticeDialog: notice still open, escalating click strategy'
    );
  }

  console.warn('[Raintree] dismissNoticeDialog: could not dismiss notice');
  return false;
}

// ─── Objective Exam (objview) ─────────────────────────────────────────────────

// Which ROM/MMT measurement column is writable depends on the note type:
//   Initial Eval                  → "Initial" only
//   Progress Note / Re-Evaluation → "Current" only
//   Daily Note (and anything else) → none
// "Goal" and "Last" are never written on any note type — Goal is clinician-set targets and
// Last is the previous visit's values.
const OBJ_EXAM_MEASUREMENT_COLS = new Set(['Initial', 'Goal', 'Last', 'Current']);

/** The single measurement column we're allowed to populate, or null to populate none. */
function objExamWritableColumn(): 'Initial' | 'Current' | null {
  if (_noteTypeKind === 'initialEval') return 'Initial';
  if (_noteTypeKind === 'progressNote') return 'Current';
  return null;
}

/** True when a parsed column must be kept out of the schema for the current note type.
 *  Only gates measurement columns — Comments, "noright", sensory Right/Left etc. pass
 *  through untouched. */
function skipObjExamColumn(col: string): boolean {
  if (!OBJ_EXAM_MEASUREMENT_COLS.has(col)) return false;
  return col !== objExamWritableColumn();
}

// Normalize a raw column token to a display name.
function normalizeObjExamCol(raw: string): string {
  const r = raw.toLowerCase();
  if (r === 'init' || r === 'initial') return 'Initial';
  if (r === 'goal') return 'Goal';
  if (r === 'comm' || r === 'comments') return 'Comments';
  if (r === 'last') return 'Last';
  if (r === 'curr' || r === 'current') return 'Current';
  return raw;
}

// Parse a Raintree objview input name like "RAROM_Init_Flex" into its parts.
// Primary pattern: [R|L](AROM|PROM)_Col_Movement  (shoulder/standard ROM)
// Fallback pattern: anything with _Init_|_Goal_|_Comm_ in the middle — used for
// Gross UE ROM, Elbow, and other panels with non-standard naming.
// Returns null only when no column token can be found at all.
function parseObjExamInputName(
  nameOrId: string
): { panel: string; col: string; movement: string } | null {
  // Primary: [RL](AROM|PROM|FPROM)_Col_Movement
  const primary = nameOrId.match(
    /^([RL])(AROM|PROM|FPROM)_(Init|goal|Comm|Last|Curr(?:ent)?)_(.+)$/i
  );
  if (primary) {
    const side = primary[1].toUpperCase() === 'R' ? 'Right' : 'Left';
    const type = primary[2].toUpperCase();
    const col = normalizeObjExamCol(primary[3]);
    const movRaw = primary[4];
    const movement = movRaw;
    return { panel: `${side} ${type}`, col, movement };
  }

  // Gross UE ROM/MMT pattern: gs_UE_<movement>_<R|L><AROM|PROM|MMT>_<col>
  // e.g. gs_UE_shFlex_RAROM_init, gs_UE_shAbd_LPROM_comm, gs_UE_shFlex_LMMT_init
  const grossUe = nameOrId.match(
    /^gs_UE_([^_]+)_(R|L)(AROM|PROM|MMT)_(init|goal|last|curr|comm)$/i
  );
  if (grossUe) {
    const side = grossUe[2].toUpperCase() === 'R' ? 'Right' : 'Left';
    const type = grossUe[3].toUpperCase();
    const col = normalizeObjExamCol(grossUe[4]);
    return { panel: `${side} ${type}`, col, movement: grossUe[1] };
  }

  // Gross LE ROM pattern: gs_le_<movement>_(r|l)(arom|prom)_(init|goal|last|curr|comm)
  // e.g. gs_le_hipAbd_rarom_init, gs_le_hipER_larom_goal, gs_le_kneeext_rprom_comm
  const grossLe = nameOrId.match(
    /^gs_le_([^_]+)_(R|L)(AROM|PROM)_(init|goal|last|curr|comm)$/i
  );
  if (grossLe) {
    const side = grossLe[2].toUpperCase() === 'R' ? 'Right' : 'Left';
    const type = grossLe[3].toUpperCase();
    const col = normalizeObjExamCol(grossLe[4]);
    const movCode = grossLe[1];
    const movement = movCode;
    return { panel: `${side} ${type}`, col, movement };
  }

  // Flexibility panel: flex_<movCode>_(right|left)[_goal]
  // e.g. flex_pecMa_right → Initial, flex_pecMa_right_goal → Goal
  // The select scanner will resolve movCode to a human-readable label and re-append the side.
  const flexPat = nameOrId.match(/^flex_(.+?)_(right|left)(?:_(goal))?$/i);
  if (flexPat) {
    const [, movCode, sideRaw, goalToken] = flexPat;
    const col = goalToken ? 'Goal' : 'Initial';
    const sideTitle = sideRaw[0].toUpperCase() + sideRaw.slice(1).toLowerCase();
    return { panel: 'Flexibility', col, movement: `${movCode} ${sideTitle}` };
  }

  // Sensory panel: <panelCode>_(right|left)<num>[_goal]
  // e.g. sharp_right1 → Initial, sharp_right1_goal → Goal, sharp_left1 → Initial Left
  // The select scanner will resolve the row number to a dermatome label (e.g. C4) from the init-right element.
  // Must come before trailPat so sharp_right1_goal is not consumed by the generic _goal branch.
  const sensoryPat = nameOrId.match(
    /^([a-zA-Z]+)_(right|left)(\d+)(?:_(goal))?$/i
  );
  if (sensoryPat) {
    const [, panelCode, sideRaw, num, goalToken] = sensoryPat;
    const sideTitle = sideRaw[0].toUpperCase() + sideRaw.slice(1).toLowerCase();
    const col = goalToken ? `${sideTitle} Goal` : sideTitle; // "Right", "Right Goal", "Left", "Left Goal"
    return { panel: panelCode, col, movement: num }; // movement = row number; scanner resolves to dermLabel
  }

  // Simple lateral (single-word prefix, no number): crep_right, crep_left[_goal]
  // Returns movement='' → column-only field, no movement level in the output path.
  const simpleLateral = nameOrId.match(
    /^([a-zA-Z]+)_(right|left)(?:_(goal))?$/i
  );
  if (simpleLateral) {
    const [, panelCode, sideRaw, goalToken] = simpleLateral;
    const sideTitle = sideRaw[0].toUpperCase() + sideRaw.slice(1).toLowerCase();
    const col = goalToken ? `${sideTitle} Goal` : sideTitle;
    return { panel: panelCode, col, movement: '' };
  }

  // Multi-token lateral: girth_joint_right, girth_joint_right_goal (prefix contains underscores)
  // Returns movement=prefix (e.g. 'girth_joint'); scanner resolves it to a human label.
  const multiLateral = nameOrId.match(/^(.+?)_(right|left)(?:_(goal))?$/i);
  if (multiLateral) {
    const [, prefix, sideRaw, goalToken] = multiLateral;
    const sideTitle = sideRaw[0].toUpperCase() + sideRaw.slice(1).toLowerCase();
    const col = goalToken ? `${sideTitle} Goal` : sideTitle;
    return { panel: prefix.split('_')[0], col, movement: prefix };
  }

  // CamelCase lateral: UEGaitRight1, LEGaitLeft3 — side (Right|Left) embedded in PascalCase name, no underscore.
  // Returns movement=row-number so the scanner can resolve it to a label via positional lookup.
  const camelLateral = nameOrId.match(/^(.+?)(Right|Left)(\d+)$/);
  if (camelLateral) {
    const [, prefix, sideRaw, num] = camelLateral;
    return { panel: prefix, col: sideRaw, movement: num };
  }

  // Numbered slot selects (no right/left suffix): tend1, tend2 — ONLY short all-lowercase prefixes.
  // Restricting to lowercase prevents matching camelCase names like LEGait1, UEGait1, PostureUE1.
  const numberedSlot = nameOrId.match(/^([a-z]{2,8})(\d+)$/);
  if (numberedSlot) {
    const [, prefix, num] = numberedSlot;
    return { panel: prefix, col: 'Location', movement: num };
  }

  // Generic trailing-suffix: <prefix>_(init|goal|comm) where column marker is at the end.
  // Handles Core Stability (cs_mmtup_init) and other panels that don't put the column in the middle.
  // Panel-level names like "elbow_comm" (single-token prefix → no movement) return null below.
  const trailPat = nameOrId.match(/^(.+?)_(init|goal|comm)$/i);
  if (trailPat) {
    const [, prefix, colToken] = trailPat;
    const col = normalizeObjExamCol(colToken);
    const prefParts = prefix.split('_');
    const movement = prefParts.length > 1 ? prefParts.slice(1).join(' ') : '';
    if (movement) {
      return { panel: prefParts[0], col, movement };
    }
  }

  // Generic fallback: Prefix_Col_Suffix  (e.g. GRAROM_Init_ShldFlexR, ElbFlex_goal_R)
  // Requires at least one non-empty token AFTER the column marker (i.e. a real movement).
  // Panel-level memos like "elbow_comm" (no movement suffix) intentionally return null here
  // so the caller falls through to panel-level comment handling.
  const parts = nameOrId.split('_');
  for (let i = 1; i < parts.length - 1; i++) {
    const colNorm = normalizeObjExamCol(parts[i]);
    if (['Initial', 'Goal', 'Comments', 'Last', 'Current'].includes(colNorm)) {
      const movement = parts
        .slice(i + 1)
        .join(' ')
        .trim();
      if (!movement) continue; // no movement token → not a movement-level field
      const panel = parts.slice(0, i).join(' ');
      return { panel, col: colNorm, movement };
    }
  }

  return null;
}

// Scan a single panel within the objview exam form and collect field specs.
// captionOverride: when set, use this as the panel caption instead of reading .w-panel-caption-text
// forCapture includes read-only history; ordinary scans keep their write restrictions.
function scanObjExamPanelFields(
  panel: Element,
  mainTabName: string,
  subTabName: string | null,
  result: Map<string, ObjExamFieldSpec>,
  captionOverride?: string,
  forCapture = false
) {
  // Borderless panels (e.g. Gross UE ROM row panels) have no caption text.
  // For these, panelCaption is derived per-field from the parsed input name instead.
  const isBorderless = panel.classList.contains('w-panel-borderless');
  let panelCaption: string;
  if (captionOverride) {
    panelCaption = captionOverride;
  } else if (isBorderless) {
    panelCaption = ''; // Per-field caption will come from parsed.panel
  } else {
    const rawCaption =
      panel.querySelector('.w-panel-caption-text')?.textContent?.trim() || '';
    // Strip trailing single-letter section markers like "(D)", "(S)" etc.
    panelCaption = rawCaption.replace(/\s*\([A-Z]\)\s*$/, '').trim();
  }
  if (!panelCaption && !isBorderless) return; // Skip non-borderless panels with no caption

  const panelContent = panel.querySelector('.w-panel-content') || panel;

  // Prefix every flat label with "mainTab|subTab|" so identical panel/movement names in
  // different tabs (e.g. "Elbow Comments" in both ROM and MMT) don't overwrite each other.
  const tabPrefix = `${mainTabName}|${subTabName ?? ''}|`;

  // Dropdown fields — catches both Raintree's custom .w-combobox-openview and plain <select>
  const seenSelectNames = new Set<string>();
  panelContent
    .querySelectorAll('.w-combobox-openview, select')
    .forEach((sel) => {
      const el = sel as HTMLSelectElement;
      const nameAttr = el.getAttribute('name') || el.id;
      if (!nameAttr || seenSelectNames.has(nameAttr)) return;
      seenSelectNames.add(nameAttr);
      const choices = Array.from(el.options)
        .map((o) => o.textContent?.trim() || '')
        .filter(Boolean);

      // Try to determine which movement this select belongs to by parsing its name,
      // or fall back to "noright" (panel-level dropdown like the ROM "noright" field).
      const parsed = parseObjExamInputName(nameAttr);
      if (parsed && (forCapture || !skipObjExamColumn(parsed.col))) {
        // Movement-level select (e.g. grade fields in MMT or Gross UE ROM)
        // When parsed.panel encodes a side ("Right AROM"/"Left AROM" from gs_le_*/gs_UE_*)
        // and panelCaption is the generic combined form ("AROM"), prefer parsed.panel so
        // right and left inputs get distinct captions instead of overwriting each other.
        const _sideInParsed = /^(Right|Left)\s/i.test(parsed.panel);
        const _sideInCaption = /^(Right|Left)\s/i.test(panelCaption);
        const effectiveCaption =
          _sideInParsed && !_sideInCaption
            ? parsed.panel
            : panelCaption || parsed.panel;
        if (!effectiveCaption) return;
        let captionOverride: string | null = null; // set by sensoryLateral for muscle group headers
        let movement = parsed.movement;
        if (!nameAttr.match(/^([RL])(AROM|PROM|FPROM)_/i)) {
          const flexLateral = nameAttr.match(
            /^flex_(.+?)_(right|left)(?:_goal)?$/i
          );
          if (flexLateral) {
            // Flex panel: the muscle-name label is ONLY on the init-right select (flex_<mov>_right).
            // All other positions (goal-right, init-left, goal-left) have column-header labels
            // ("Goal", "Left") that must not be used as movement names. So unconditionally read
            // from the init-right element. When the init-right has no proper bound label (e.g.
            // Biceps uses for="" on the muscle label), fall back to the widget at the same row.
            const [, movCode, sideRaw] = flexLateral;
            const sideTitle =
              sideRaw[0].toUpperCase() + sideRaw.slice(1).toLowerCase();
            const COLUMN_TOKENS = new Set(['right', 'left', 'goal', 'initial']);
            const initRightName = `flex_${movCode}_right`;
            const initRightEl = panelContent.querySelector(
              `[name="${initRightName}"], [id="${initRightName}"]`
            ) as HTMLElement | null;
            let muscleName = movCode; // raw code fallback
            if (initRightEl?.id) {
              const directLabel = panelContent.querySelector(
                `label[for="${initRightEl.id}"]`
              ) as HTMLElement | null;
              const directText =
                directLabel?.textContent
                  ?.trim()
                  .replace(/\s*\([\d\-–]+\)\s*$/, '')
                  .trim() || '';
              if (directText && !COLUMN_TOKENS.has(directText.toLowerCase())) {
                muscleName = directText;
              } else {
                // Positional fallback: the muscle label has for="" (e.g. Biceps, Triceps).
                // Find a .w-label widget at the same CSS top position in the panel.
                const initWidget = initRightEl.closest(
                  '.widget'
                ) as HTMLElement | null;
                const rowTop = initWidget?.style.top;
                if (rowTop) {
                  for (const w of Array.from(
                    panelContent.querySelectorAll('.widget')
                  )) {
                    if (
                      w === initWidget ||
                      (w as HTMLElement).style.top !== rowTop
                    )
                      continue;
                    const lbl = w.querySelector(
                      '.w-label label'
                    ) as HTMLElement | null;
                    const lblText =
                      lbl?.textContent
                        ?.trim()
                        .replace(/\s*\([\d\-–]+\)\s*$/, '')
                        .trim() || '';
                    if (lblText && !COLUMN_TOKENS.has(lblText.toLowerCase())) {
                      muscleName = lblText;
                      break;
                    }
                  }
                }
              }
            }
            movement = `${muscleName} ${sideTitle}`;
          } else {
            const sensoryLateral = nameAttr.match(
              /^([a-zA-Z]+)_(right|left)(\d+)(?:_goal)?$/i
            );
            if (sensoryLateral) {
              // Row label is only on the init-right element. Other positions carry column-header
              // labels ("Right", "Left", "Goal") that must not be used. Side is already in
              // parsed.col so movement is just the row label (dermLabel).
              // For multi-group panels (e.g. Palpation with Quadriceps + Adductors), the nearest
              // bold for="" label above the field is used as captionOverride (muscle group name).
              const [, panelCode, , num] = sensoryLateral;
              const COLUMN_TOKENS_S = new Set([
                'right',
                'left',
                'goal',
                'initial'
              ]);
              const initRightName = `${panelCode}_right${num}`;
              const initRightEl = panelContent.querySelector(
                `[name="${initRightName}"], [id="${initRightName}"]`
              ) as HTMLElement | null;
              let dermLabel: string = num;
              if (initRightEl?.id) {
                const directLabel = panelContent.querySelector(
                  `label[for="${initRightEl.id}"]`
                ) as HTMLElement | null;
                const directText =
                  directLabel?.textContent
                    ?.trim()
                    .replace(/\s*\([\d\-–]+\)\s*$/, '')
                    .trim() || '';
                if (
                  directText &&
                  !COLUMN_TOKENS_S.has(directText.toLowerCase())
                ) {
                  dermLabel = directText;
                }
              }
              // Positional fallback: use inline CSS top (absolute positioning within panel)
              // to find a label widget in the same row — consistent with the flex branch approach.
              const initWidget = initRightEl?.closest(
                '.widget'
              ) as HTMLElement | null;
              const rowTopStr = initWidget?.style.top || '';
              const rowTopPx = rowTopStr ? parseInt(rowTopStr) : -1;
              if (dermLabel === num && rowTopPx >= 0) {
                for (const w of Array.from(
                  panelContent.querySelectorAll('.widget')
                )) {
                  if (
                    w === initWidget ||
                    (w as HTMLElement).style.top !== rowTopStr
                  )
                    continue;
                  const lbl = w.querySelector(
                    '.w-label label'
                  ) as HTMLElement | null;
                  const text = (lbl?.textContent || '')
                    .trim()
                    .replace(/\s*\([\d\-–]+\)\s*$/, '')
                    .trim();
                  if (text && !COLUMN_TOKENS_S.has(text.toLowerCase())) {
                    dermLabel = text;
                    break;
                  }
                }
              }
              // Section header: find the nearest bold for="" label above this field.
              // This gives the muscle group name (e.g. "Quadriceps Femoris") when multiple
              // groups share the same row label (e.g. "Tenderness") in the same sub-tab.
              if (rowTopPx > 0) {
                let bestHeaderTop = -1;
                let bestHeaderText = '';
                for (const w of Array.from(
                  panelContent.querySelectorAll('.widget')
                )) {
                  const wTopStr = (w as HTMLElement).style.top || '';
                  const wTopPx = wTopStr ? parseInt(wTopStr) : -1;
                  if (wTopPx < 0 || wTopPx >= rowTopPx) continue;
                  const wLabel = w.querySelector(
                    '.w-label'
                  ) as HTMLElement | null;
                  const styleStr = wLabel?.getAttribute('style') || '';
                  const isBold =
                    styleStr.includes('font-weight: 600') ||
                    styleStr.includes('font-weight:600');
                  if (!isBold) continue;
                  const lbl = wLabel?.querySelector(
                    'label'
                  ) as HTMLElement | null;
                  const text = (lbl?.textContent || '').trim();
                  if (!text || COLUMN_TOKENS_S.has(text.toLowerCase()))
                    continue;
                  if (wTopPx > bestHeaderTop) {
                    bestHeaderTop = wTopPx;
                    bestHeaderText = text;
                  }
                }
                if (bestHeaderText) captionOverride = bestHeaderText;
              }
              movement = dermLabel; // "C4", "C5", ...; side is in parsed.col
            } else if (
              movement !== '' &&
              (!/^\d+$/.test(parsed.movement) ||
                parsed.col === 'Right' ||
                parsed.col === 'Left')
            ) {
              // Non-flex, non-sensory label lookup.
              // Skip for column-only fields (movement='' from simpleLateral).
              // Skip for slot-index fields (movement=digit, col=Location from numberedSlot).
              // Allow for camelLateral fields (movement=digit, col=Right|Left) so row labels can be resolved.
              const COLUMN_TOKENS_L = new Set([
                'right',
                'left',
                'goal',
                'initial'
              ]);
              const rawLabelElS = el.id
                ? (panelContent.querySelector(
                    `label[for="${el.id}"]`
                  ) as HTMLElement | null)
                : null;
              // Skip hidden/zero-width labels (e.g. Raintree date labels on MMT/ROM rows)
              const labelEl = (rawLabelElS?.hidden || rawLabelElS?.style?.width === '0px') ? null : rawLabelElS;
              let labelText =
                labelEl?.textContent
                  ?.trim()
                  .replace(/\s*\([\d\-–]+\)\s*$/, '')
                  .trim() || '';
              // Discard if label is just a column header (e.g. "Right", "Goal")
              if (labelText && COLUMN_TOKENS_L.has(labelText.toLowerCase()))
                labelText = '';
              // Discard date-pattern labels (e.g. visible "06-30-26" on MMT shshrug row)
              if (labelText && /^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/.test(labelText))
                labelText = '';
              if (!labelText) {
                // For lateral-goal fields (xxx_right_goal), look up the base non-goal element label.
                const lateralGoalMatch = nameAttr.match(/^(.+)_goal$/i);
                if (lateralGoalMatch) {
                  const baseName = lateralGoalMatch[1];
                  const baseEl = panelContent.querySelector(
                    `[name="${baseName}"], [id="${baseName}"]`
                  ) as HTMLElement | null;
                  if (baseEl?.id) {
                    const baseLabel = panelContent.querySelector(
                      `label[for="${baseEl.id}"]`
                    ) as HTMLElement | null;
                    const baseText =
                      baseLabel?.textContent
                        ?.trim()
                        .replace(/\s*\([\d\-–]+\)\s*$/, '')
                        .trim() || '';
                    if (
                      baseText &&
                      !COLUMN_TOKENS_L.has(baseText.toLowerCase())
                    )
                      labelText = baseText;
                  }
                }
              }
              if (!labelText) {
                const trailGoalMatch = nameAttr.match(/^(.+?)_goal$/i);
                if (trailGoalMatch) {
                  const initName = `${trailGoalMatch[1]}_init`;
                  const initSel = panelContent.querySelector(
                    `[name="${initName}"], [id="${initName}"]`
                  ) as HTMLElement | null;
                  if (initSel?.id) {
                    const initLabel = panelContent.querySelector(
                      `label[for="${initSel.id}"]`
                    ) as HTMLElement | null;
                    labelText =
                      initLabel?.textContent
                        ?.trim()
                        .replace(/\s*\([\d\-–]+\)\s*$/, '')
                        .trim() || '';
                  }
                }
              }
              if (
                !labelText &&
                (/^\d+$/.test(movement) ||
                  /^\d{1,2}-\d{2}-\d{2,4}$/.test(movement))
              ) {
                // Positional fallback for camelLateral fields (e.g. UEGaitRight1 whose label is "Right")
                // and for date-keyed Init fields (e.g. RPROM_Init_01-20-26 where movement="01-20-26"):
                // Find a non-bold label widget at the same CSS top position as the select widget.
                const elWidget = el.closest('.widget') as HTMLElement | null;
                const elTop = elWidget?.style.top;
                if (elTop) {
                  for (const w of Array.from(
                    panelContent.querySelectorAll('.widget')
                  )) {
                    if (
                      w === elWidget ||
                      (w as HTMLElement).style.top !== elTop
                    )
                      continue;
                    const wLabel = w.querySelector(
                      '.w-label'
                    ) as HTMLElement | null;
                    const styleStr = wLabel?.getAttribute('style') || '';
                    if (
                      styleStr.includes('font-weight: 600') ||
                      styleStr.includes('font-weight:600')
                    )
                      continue;
                    const lbl = wLabel?.querySelector(
                      'label'
                    ) as HTMLElement | null;
                    const text =
                      lbl?.textContent
                        ?.trim()
                        .replace(/\s*\([\d\-–]+\)\s*$/, '')
                        .trim() || '';
                    if (text && !COLUMN_TOKENS_L.has(text.toLowerCase())) {
                      labelText = text;
                      break;
                    }
                  }
                }
              }
              // Gross LE ROM: labels live in a sibling side panel outside panelContent.
              if (!labelText && nameAttr.match(/^gs_le_/i)) {
                const elWidget = el.closest('.widget') as HTMLElement | null;
                const elTop = elWidget?.style.top;
                const broadRoot = panel.parentElement;
                if (elTop && broadRoot) {
                  for (const w of Array.from(
                    broadRoot.querySelectorAll('.widget')
                  )) {
                    if (panelContent.contains(w)) continue;
                    if ((w as HTMLElement).style.top !== elTop) continue;
                    const lbl = w.querySelector(
                      '.w-label label'
                    ) as HTMLElement | null;
                    const text =
                      lbl?.textContent
                        ?.trim()
                        .replace(/\s*\([\d\-–]+\)\s*$/, '')
                        .trim() || '';
                    if (text && !COLUMN_TOKENS_L.has(text.toLowerCase())) {
                      labelText = text;
                      break;
                    }
                  }
                }
              }
              // Final date-pattern discard covering all fallback paths (direct label, trailGoalMatch, positional)
              if (labelText && /^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/.test(labelText))
                labelText = '';
              if (labelText) movement = labelText;
            }
          }
        }
        const finalCaption = captionOverride || effectiveCaption;
        // movement='' means column-only (no movement level) — store null and omit from flatLabel.
        const finalMovement = movement || null;
        const flatLabel = finalMovement
          ? `${tabPrefix}${finalCaption} ${finalMovement} ${parsed.col}`
          : `${tabPrefix}${finalCaption} ${parsed.col}`;
        result.set(flatLabel, {
          nameAttr,
          type: 'select',
          choices,
          mainTabName,
          subTabName,
          panelCaption: finalCaption,
          movement: finalMovement,
          column: parsed.col
        });
      } else if (panelCaption) {
        // Panel-level dropdown (no movement info in name, e.g. ROM "noright")
        const flatLabel = `${tabPrefix}${panelCaption} noright`;
        result.set(flatLabel, {
          nameAttr,
          type: 'select',
          choices,
          mainTabName,
          subTabName,
          panelCaption,
          movement: null,
          column: 'noright'
        });
      }
    });

  // Text/numeric inputs: only the measurement column writable for this note type
  // (Initial on an Initial Eval, Current on a Progress Note / Re-Eval) plus Comments.
  // Cast a wide net — some panels (e.g. Gross UE ROM) omit the .w-field-openview wrapper
  // or use input types other than "text".
  panelContent
    .querySelectorAll(
      'input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"])' +
        ':not([type="button"]):not([type="submit"])' +
        (forCapture ? '' : ':not(:disabled)')
    )
    .forEach((inp) => {
      const el = inp as HTMLInputElement;
      const nameAttr = el.getAttribute('name') || '';
      if (!nameAttr) return;
      const parsed = parseObjExamInputName(nameAttr);
      if (!parsed) return;
      if (!forCapture && skipObjExamColumn(parsed.col)) return;

      // For borderless panels, derive the panel caption from the parsed input name.
      // When parsed.panel encodes a side and panelCaption is the generic combined panel name,
      // prefer parsed.panel so right and left inputs get distinct captions.
      const _sideInParsedI = /^(Right|Left)\s/i.test(parsed.panel);
      const _sideInCaptionI = /^(Right|Left)\s/i.test(panelCaption);
      const effectiveCaption =
        _sideInParsedI && !_sideInCaptionI
          ? parsed.panel
          : panelCaption || parsed.panel;
      if (!effectiveCaption) return;

      // Try to get a human-readable movement name from a nearby label.
      let movement = parsed.movement;
      // Skip label lookup for column-only (movement='') and slot-index (movement=digit, col=Location) fields.
      // Allow for camelLateral fields (movement=digit, col=Right|Left) so row labels can be resolved.
      if (
        movement !== '' &&
        (!/^\d+$/.test(parsed.movement) ||
          parsed.col === 'Right' ||
          parsed.col === 'Left')
      ) {
        const COLUMN_TOKENS_I = new Set(['right', 'left', 'goal', 'initial']);
        const inputId = el.id;
        const rawLabelEl = inputId
          ? (panelContent.querySelector(
              `label[for="${inputId}"]`
            ) as HTMLElement | null)
          : null;
        // Skip hidden/zero-width labels (Raintree attaches hidden date labels to some ROM inputs,
        // e.g. <label hidden for="RAROM_Init_ER" style="width:0px">06-30-26</label>)
        const labelEl = (rawLabelEl?.hidden || rawLabelEl?.style?.width === '0px') ? null : rawLabelEl;
        let labelText =
          labelEl?.textContent
            ?.trim()
            .replace(/\s*\([\d\-–]+\)\s*$/, '')
            .trim() || '';
        // Discard column-header labels like "Right", "Left", "Goal" (hidden accessibility labels)
        if (labelText && COLUMN_TOKENS_I.has(labelText.toLowerCase()))
          labelText = '';
        // Discard date-pattern labels (e.g. visible "06-30-26" labels on PROM Flex Init inputs)
        if (labelText && /^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/.test(labelText))
          labelText = '';
        if (!labelText) {
          // For lateral-goal fields (xxx_right_goal), strip _goal and look up that element's label.
          const lateralGoalMatch = nameAttr.match(/^(.+)_goal$/i);
          if (lateralGoalMatch) {
            const baseName = lateralGoalMatch[1];
            const baseEl = panelContent.querySelector(
              `[name="${baseName}"], [id="${baseName}"]`
            ) as HTMLElement | null;
            if (baseEl?.id) {
              const baseLabel = panelContent.querySelector(
                `label[for="${baseEl.id}"]`
              ) as HTMLElement | null;
              const baseText =
                baseLabel?.textContent
                  ?.trim()
                  .replace(/\s*\([\d\-–]+\)\s*$/, '')
                  .trim() || '';
              if (baseText && !COLUMN_TOKENS_I.has(baseText.toLowerCase()))
                labelText = baseText;
            }
          }
        }
        if (!labelText && isBorderless) {
          // For Gross UE ROM: goal/PROM inputs have no direct label.
          // Find the label of the init input for the same movement+side (AROM or PROM).
          const gsm = nameAttr.match(/^gs_UE_([^_]+)_(R|L)(AROM|PROM|MMT)_/i);
          if (gsm) {
            const [, movCode, side] = gsm;
            const initCandidates = Array.from(
              panelContent.querySelectorAll(
                `input[name^="gs_UE_${movCode}_${side}"][name$="_init"]`
              )
            );
            for (const initEl of initCandidates) {
              const initId = (initEl as HTMLElement).id;
              if (!initId) continue;
              const initLabel = panelContent.querySelector(
                `label[for="${initId}"]`
              ) as HTMLElement | null;
              const initLabelText = initLabel?.textContent
                ?.trim()
                .replace(/\s*\([\d\-–]+\)\s*$/, '')
                .trim();
              if (initLabelText) {
                labelText = initLabelText;
                break;
              }
            }
          }
        }
        if (!labelText) {
          // Generic trailing-suffix fallback: look up the _init version's label.
          const trailGoal = nameAttr.match(/^(.+?)_(goal|comm)$/i);
          if (trailGoal) {
            const initName = `${trailGoal[1]}_init`;
            const initEl = panelContent.querySelector(
              `[name="${initName}"], [id="${initName}"]`
            ) as HTMLElement | null;
            if (initEl?.id) {
              const initLabel = panelContent.querySelector(
                `label[for="${initEl.id}"]`
              ) as HTMLElement | null;
              const initLabelText = initLabel?.textContent
                ?.trim()
                .replace(/\s*\([\d\-–]+\)\s*$/, '')
                .trim();
              if (initLabelText) labelText = initLabelText;
            }
          }
        }
        if (
          !labelText &&
          movement === parsed.movement &&
          movement.includes('_')
        ) {
          // multiLateral prefix (e.g. 'girth_joint'): look up _right counterpart label.
          // Handles left/left_goal fields whose own label is a discarded column token.
          const rightEl = panelContent.querySelector(
            `[name="${movement}_right"], [id="${movement}_right"]`
          ) as HTMLElement | null;
          if (rightEl?.id) {
            const rightLabel = panelContent.querySelector(
              `label[for="${rightEl.id}"]`
            ) as HTMLElement | null;
            const rightText =
              rightLabel?.textContent
                ?.trim()
                .replace(/\s*\([\d\-–]+\)\s*$/, '')
                .trim() || '';
            if (rightText && !COLUMN_TOKENS_I.has(rightText.toLowerCase()))
              labelText = rightText;
          }
        }
        if (
          !labelText &&
          (/^\d+$/.test(movement) || /^\d{1,2}-\d{2}-\d{2,4}$/.test(movement))
        ) {
          // Positional fallback for camelLateral inputs and date-keyed Init fields
          // (e.g. RPROM_Init_01-20-26 where movement="01-20-26"): find a non-bold label at the same CSS top.
          const elWidget = el.closest('.widget') as HTMLElement | null;
          const elTop = elWidget?.style.top;
          if (elTop) {
            for (const w of Array.from(
              panelContent.querySelectorAll('.widget')
            )) {
              if (w === elWidget || (w as HTMLElement).style.top !== elTop)
                continue;
              const wLabel = w.querySelector('.w-label') as HTMLElement | null;
              const styleStr = wLabel?.getAttribute('style') || '';
              if (
                styleStr.includes('font-weight: 600') ||
                styleStr.includes('font-weight:600')
              )
                continue;
              const lbl = wLabel?.querySelector('label') as HTMLElement | null;
              const text =
                lbl?.textContent
                  ?.trim()
                  .replace(/\s*\([\d\-–]+\)\s*$/, '')
                  .trim() || '';
              if (text && !COLUMN_TOKENS_I.has(text.toLowerCase())) {
                labelText = text;
                break;
              }
            }
          }
        }
        // Gross LE ROM: labels live in a sibling side panel outside panelContent.
        // Find the widget at the same CSS top in panel.parentElement.
        if (!labelText && nameAttr.match(/^gs_le_/i)) {
          const elWidget = el.closest('.widget') as HTMLElement | null;
          const elTop = elWidget?.style.top;
          const broadRoot = panel.parentElement;
          if (elTop && broadRoot) {
            for (const w of Array.from(broadRoot.querySelectorAll('.widget'))) {
              if (panelContent.contains(w)) continue;
              if ((w as HTMLElement).style.top !== elTop) continue;
              const lbl = w.querySelector(
                '.w-label label'
              ) as HTMLElement | null;
              const text =
                lbl?.textContent
                  ?.trim()
                  .replace(/\s*\([\d\-–]+\)\s*$/, '')
                  .trim() || '';
              if (text && !COLUMN_TOKENS_I.has(text.toLowerCase())) {
                labelText = text;
                break;
              }
            }
          }
        }
        if (labelText) movement = labelText;

        // flex_<movCode>_(right|left)[_goal] text inputs: the generic label lookup above
        // finds the muscle label (e.g. "Hamstring") for the right side but loses the side
        // token, and finds nothing for the left side (its label is "Left" = column token).
        // Re-attach the correct side and, for left-side fields, fall back to the right-side
        // element's label to get the muscle name.
        const flexInputFix = nameAttr.match(
          /^flex_(.+?)_(right|left)(?:_goal)?$/i
        );
        if (flexInputFix) {
          const [, movCode, sideRaw] = flexInputFix;
          const sideTitle =
            sideRaw[0].toUpperCase() + sideRaw.slice(1).toLowerCase();
          if (
            labelText &&
            !movement.toLowerCase().endsWith(sideTitle.toLowerCase())
          ) {
            // Right side: label resolved a name like "Hamstring", re-append side.
            movement = `${movement} ${sideTitle}`;
          } else if (!labelText) {
            // Left side: direct label is a discarded column token — look up the right-side
            // element's label to get the muscle name, then attach the left side.
            const initRightEl = panelContent.querySelector(
              `[name="flex_${movCode}_right"]`
            ) as HTMLElement | null;
            if (initRightEl?.id) {
              const lbl = panelContent.querySelector(
                `label[for="${initRightEl.id}"]`
              ) as HTMLElement | null;
              const txt =
                lbl?.textContent
                  ?.trim()
                  .replace(/\s*\([\d\-–]+\)\s*$/, '')
                  .trim() || '';
              const COL = new Set(['right', 'left', 'goal', 'initial']);
              if (txt && !COL.has(txt.toLowerCase()))
                movement = `${txt} ${sideTitle}`;
            }
          }
        }
      }

      const finalMovement = movement || null;
      const flatLabel = finalMovement
        ? `${tabPrefix}${effectiveCaption} ${finalMovement} ${parsed.col}`
        : `${tabPrefix}${effectiveCaption} ${parsed.col}`;
      result.set(flatLabel, {
        nameAttr,
        type: 'input',
        mainTabName,
        subTabName,
        panelCaption: effectiveCaption,
        movement: finalMovement,
        column: parsed.col
      });
    });

  // Memo fields (Comments — TinyMCE)
  panelContent.querySelectorAll('.w-memo-content').forEach((memo) => {
    const el = memo as HTMLElement;
    const memoName = el.getAttribute('name') || el.id;
    if (!memoName) return;
    const parsed = parseObjExamInputName(memoName);
    if (parsed && (forCapture || !skipObjExamColumn(parsed.col))) {
      const _sideInParsedM = /^(Right|Left)\s/i.test(parsed.panel);
      const _sideInCaptionM = /^(Right|Left)\s/i.test(panelCaption);
      const effectiveCaption =
        _sideInParsedM && !_sideInCaptionM
          ? parsed.panel
          : panelCaption || parsed.panel;
      if (!effectiveCaption) return;

      // When the primary RAROM/RPROM pattern assigns this memo to a different panel than
      // the physical panel it lives in (e.g. RAROM_Comm_Elbow found inside the "Elbow" panel),
      // treat it as a panel-level comment for the actual panel rather than as a movement field.
      const primaryMatch = memoName.match(/^([RL])(AROM|PROM|FPROM)_/i);
      if (primaryMatch && parsed.panel !== effectiveCaption) {
        const flatLabel = `${tabPrefix}${effectiveCaption} Comments`;
        result.set(flatLabel, {
          nameAttr: memoName,
          type: 'memo',
          mainTabName,
          subTabName,
          panelCaption: effectiveCaption,
          movement: null,
          column: 'Comments'
        });
        return;
      }
      // Try label lookup for a human-readable movement name.
      let movement = parsed.movement;
      if (!primaryMatch) {
        // Direct label lookup (works when the memo has a label[for] pointing to it)
        const memoId = el.id;
        const labelEl = memoId
          ? (panelContent.querySelector(
              `label[for="${memoId}"]`
            ) as HTMLElement | null)
          : null;
        const labelText = labelEl?.textContent
          ?.trim()
          .replace(/\s*\([\d\-–]+\)\s*$/, '')
          .trim();
        if (labelText) {
          movement = labelText;
        } else if (isBorderless) {
          // For borderless panels (Gross UE ROM), look up the label of the corresponding
          // init input (e.g. gs_UE_shFlex_RAROM_comm → gs_UE_shFlex_RAROM_init)
          const initName = memoName.replace(/_(comm)$/i, '_init');
          if (initName !== memoName) {
            const initEl = panelContent.querySelector(
              `input[name="${initName}"]`
            ) as HTMLElement | null;
            if (initEl?.id) {
              const initLabel = panelContent.querySelector(
                `label[for="${initEl.id}"]`
              ) as HTMLElement | null;
              const initLabelText = initLabel?.textContent
                ?.trim()
                .replace(/\s*\([\d\-–]+\)\s*$/, '')
                .trim();
              if (initLabelText) movement = initLabelText;
            }
          }
        } else {
          // Generic trailing-suffix fallback: for _comm memos with no direct label,
          // look up the corresponding _init field's label (e.g. cs_mmtup_comm → cs_mmtup_init).
          // The init field may be a select, not an input, so query any element by name or id.
          const trailComm = memoName.match(/^(.+?)_comm$/i);
          if (trailComm) {
            const initName = `${trailComm[1]}_init`;
            const initEl = panelContent.querySelector(
              `[name="${initName}"], [id="${initName}"]`
            ) as HTMLElement | null;
            if (initEl?.id) {
              const initLabel = panelContent.querySelector(
                `label[for="${initEl.id}"]`
              ) as HTMLElement | null;
              const initLabelText = initLabel?.textContent
                ?.trim()
                .replace(/\s*\([\d\-–]+\)\s*$/, '')
                .trim();
              if (initLabelText) movement = initLabelText;
            }
          }
        }
      }
      // Discard date-pattern labels that snuck in via fallback init-label lookups
      if (/^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/.test(movement))
        movement = parsed.movement;
      const flatLabel = `${tabPrefix}${effectiveCaption} ${movement} Comments`;
      result.set(flatLabel, {
        nameAttr: memoName,
        type: 'memo',
        mainTabName,
        subTabName,
        panelCaption: effectiveCaption,
        movement,
        column: 'Comments'
      });
    } else if (!parsed && panelCaption) {
      // Panel-level memo: try label lookup so multiple memos in the same panel get distinct keys.
      const memoId = el.id;
      const memoLabelEl = memoId
        ? (panelContent.querySelector(
            `label[for="${memoId}"]`
          ) as HTMLElement | null)
        : null;
      let memoMovement: string | null =
        memoLabelEl?.textContent
          ?.trim()
          .replace(/\s*\([\d\-–]+\)\s*$/, '')
          .trim() || null;
      if (!memoMovement) {
        // Positional fallback: find a non-bold label at the same CSS top position.
        const memoWidget = el.closest('.widget') as HTMLElement | null;
        const memoTop = memoWidget?.style.top;
        if (memoTop) {
          const COLUMN_TOKENS_M = new Set([
            'right',
            'left',
            'goal',
            'initial',
            'comments'
          ]);
          for (const w of Array.from(
            panelContent.querySelectorAll('.widget')
          )) {
            if (w === memoWidget || (w as HTMLElement).style.top !== memoTop)
              continue;
            const wLabel = w.querySelector('.w-label') as HTMLElement | null;
            const styleStr = wLabel?.getAttribute('style') || '';
            if (
              styleStr.includes('font-weight: 600') ||
              styleStr.includes('font-weight:600')
            )
              continue;
            const lbl = wLabel?.querySelector('label') as HTMLElement | null;
            const text =
              lbl?.textContent
                ?.trim()
                .replace(/\s*\([\d\-–]+\)\s*$/, '')
                .trim() || '';
            if (text && !COLUMN_TOKENS_M.has(text.toLowerCase())) {
              memoMovement = text;
              break;
            }
          }
        }
      }
      const flatLabel = memoMovement
        ? `${tabPrefix}${panelCaption} ${memoMovement} Comments`
        : `${tabPrefix}${panelCaption} Comments`;
      result.set(flatLabel, {
        nameAttr: memoName,
        type: 'memo',
        mainTabName,
        subTabName,
        panelCaption,
        movement: memoMovement,
        column: 'Comments'
      });
    }
  });

  // Count how many entries this panel added (for debugging missing sub-tabs)
  const inputs = panelContent.querySelectorAll(
    'input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"])' +
      ':not([type="button"]):not([type="submit"]):not(:disabled)'
  ).length;
  const selects = panelContent.querySelectorAll(
    '.w-combobox-openview, select'
  ).length;
  const memos = panelContent.querySelectorAll('.w-memo-content').length;
  const displayCaption = panelCaption || '(borderless)';
}

// Detect row-list listboxes (e.g. Joint Mobility) in an exam tab content area.
// Clicks the Add button to read picker choices, then navigates back to the exam form.
async function scanObjExamTabListboxes(
  tabContent: Element,
  mainTabName: string,
  subTabName: string | null,
  result: Map<string, ObjExamFieldSpec>
): Promise<void> {
  for (const lb of Array.from(tabContent.querySelectorAll('.w-listbox'))) {
    // Note: do NOT skip panel-embedded listboxes here — scanObjExamPanelFields handles
    // only input/select/checkbox/memo, not listboxes, so panel-listboxes (e.g. Joint Mobility)
    // would fall through completely if we skipped them.
    if (!lb.querySelector('.w-listbox-toolbar-button.mdi-pen')) continue; // must be editable row-list

    const headers = Array.from(lb.querySelectorAll('thead th'))
      .filter((th) => !th.classList.contains('w-listbox-extended-checkbox'))
      .map((th) => (th as HTMLElement).textContent?.trim() || '')
      .filter(Boolean);
    if (headers.length === 0) continue;

    // Choices are fetched in a post-scan pass (readObjExamListboxChoices) after all
    // tabs have been scanned, to avoid navigation corruption during scanning.
    const pickerChoices: string[] = [];

    const lbId = (lb as HTMLElement).id;
    // Use first column header as the key suffix so multiple listboxes in the same tab
    // (e.g. Balance and Special Tests in Balance/Special Tests sub-tab) don't overwrite each other.
    const primaryHeader = headers[0];
    const flatLabel = `${mainTabName}|${subTabName ?? ''}|${primaryHeader}`;
    result.set(flatLabel, {
      nameAttr: lbId,
      type: 'listbox',
      choices: pickerChoices.slice(0, 499),
      mainTabName,
      subTabName,
      panelCaption: primaryHeader, // used by objExamNestPath as the field name
      movement: null,
      column: 'listbox',
      listboxHeaders: headers
    });
  }
}

// Scan flat (non-panel, non-sub-tab) widgets in a tab content area.
// Used for tabs like Gait/Posture where combos, memos, and checkboxes sit outside
// any .w-panel and outside the sub-tab control, using absolute CSS positioning.
function scanObjExamFlatFields(
  tabContent: Element,
  mainTabName: string,
  subOuterCtrl: Element | null,
  result: Map<string, ObjExamFieldSpec>
): void {
  const isFlat = (el: Element): boolean => {
    if (subOuterCtrl && subOuterCtrl.contains(el)) return false;
    if (el.closest('.w-panel')) return false;
    return true;
  };

  const allWidgets = Array.from(tabContent.querySelectorAll('.widget')).filter(
    isFlat
  );
  if (allWidgets.length === 0) return;

  const getCssInt = (el: Element, prop: 'top' | 'left'): number => {
    const val = (el as HTMLElement).style[prop];
    return val ? parseInt(val, 10) : 0;
  };

  const isWidgetBold = (widget: Element): boolean => {
    const wLabel = widget.querySelector('.w-label') as HTMLElement | null;
    if (!wLabel) return false;
    const s = wLabel.getAttribute('style') || '';
    return s.includes('font-weight: 600') || s.includes('font-weight:600');
  };

  const getWidgetLabelText = (widget: Element): string =>
    (widget.querySelector('.w-label label') as HTMLElement | null)?.textContent
      ?.trim()
      .replace(/\s*\([\d\-–]+\)\s*$/, '')
      .trim() || '';

  type WidgetPos = {
    el: Element;
    top: number;
    left: number;
    text: string;
    isBold: boolean;
  };
  const labelWidgets: WidgetPos[] = [];
  for (const w of allWidgets) {
    const text = getWidgetLabelText(w);
    if (!text) continue;
    labelWidgets.push({
      el: w,
      top: getCssInt(w, 'top'),
      left: getCssInt(w, 'left'),
      text,
      isBold: isWidgetBold(w)
    });
  }
  labelWidgets.sort((a, b) =>
    a.top !== b.top ? a.top - b.top : a.left - b.left
  );

  // Column-header words must NOT be treated as section headings even when bold.
  const FLAT_COL_TOKENS = new Set([
    'right',
    'left',
    'r',
    'l',
    'goal',
    'initial'
  ]);
  const sectionHeadings = labelWidgets.filter(
    (l) => l.isBold && !FLAT_COL_TOKENS.has(l.text.toLowerCase())
  );

  const findSection = (top: number): string => {
    let section = '';
    for (const h of sectionHeadings) {
      if (h.top <= top + 5) section = h.text;
      else break;
    }
    return section;
  };

  // Non-bold label at same vertical row, to the left of the widget → movement/row name
  const findRowLabel = (wTop: number, wLeft: number): string => {
    const candidates = labelWidgets.filter(
      (l) => !l.isBold && Math.abs(l.top - wTop) <= 12 && l.left < wLeft
    );
    if (!candidates.length) return '';
    candidates.sort((a, b) => b.left - a.left);
    return candidates[0].text;
  };

  // Any label (bold column header or non-bold) directly above the widget at the same
  // horizontal column → column name (e.g. "Right" / "Left" headers over combo columns).
  const findColumnLabel = (wTop: number, wLeft: number): string => {
    const candidates = labelWidgets.filter(
      (l) => l.top < wTop && Math.abs(l.left - wLeft) <= 30
    );
    if (!candidates.length) return '';
    candidates.sort((a, b) => b.top - a.top);
    return candidates[0].text;
  };

  const tabPrefix = `${mainTabName}||`;

  for (const w of allWidgets) {
    const wTop = getCssInt(w, 'top');
    const wLeft = getCssInt(w, 'left');

    // Combobox / select
    const selectEl = w.querySelector(
      '.w-combobox-openview, select'
    ) as HTMLSelectElement | null;
    if (selectEl) {
      const nameAttr = selectEl.getAttribute('name') || selectEl.id;
      if (!nameAttr) continue;
      const section = findSection(wTop);
      if (!section) continue;
      const movement = findRowLabel(wTop, wLeft) || null;
      const colName =
        findColumnLabel(wTop, wLeft) ||
        w.getAttribute('aria-label')?.trim() ||
        selectEl.getAttribute('aria-label')?.trim() ||
        '';
      if (!colName) continue;
      const choices = Array.from(selectEl.options)
        .map((o) => o.textContent?.trim() || '')
        .filter(Boolean);
      const flatLabel = movement
        ? `${tabPrefix}${section} ${movement} ${colName}`
        : `${tabPrefix}${section} ${colName}`;
      result.set(flatLabel, {
        nameAttr,
        type: 'select',
        choices,
        mainTabName,
        subTabName: null,
        panelCaption: section,
        movement,
        column: colName,
        skipPanelInPath: true
      });
      continue;
    }

    // Memo (.w-memo-content)
    const memoEl = w.querySelector('.w-memo-content') as HTMLElement | null;
    if (memoEl) {
      const nameAttr = memoEl.getAttribute('name') || memoEl.id;
      if (!nameAttr) continue;
      const section = findSection(wTop);
      if (!section) continue;
      const memoId = memoEl.id;
      const labelEl = memoId
        ? (tabContent.querySelector(
            `label[for="${memoId}"]`
          ) as HTMLElement | null)
        : null;
      const colName =
        labelEl?.textContent
          ?.trim()
          .replace(/\s*\([\d\-–]+\)\s*$/, '')
          .trim() ||
        w.getAttribute('aria-label')?.trim() ||
        // Positional fallback: label to the left at the same row (label[for=""] pattern)
        findRowLabel(wTop, wLeft) ||
        getWidgetLabelText(w) ||
        nameAttr;
      const flatLabel = `${tabPrefix}${section} ${colName}`;
      result.set(flatLabel, {
        nameAttr,
        type: 'memo',
        mainTabName,
        subTabName: null,
        panelCaption: section,
        movement: null,
        column: colName,
        skipPanelInPath: true
      });
      continue;
    }

    // Checkbox
    const checkboxEl = w.querySelector(
      'input[type="checkbox"]'
    ) as HTMLInputElement | null;
    if (checkboxEl) {
      const nameAttr = checkboxEl.getAttribute('name') || checkboxEl.id;
      if (!nameAttr) continue;
      const section = findSection(wTop);
      if (!section) continue;
      const cbLabel =
        checkboxEl.getAttribute('aria-label')?.trim() ||
        w.getAttribute('aria-label')?.trim() ||
        getWidgetLabelText(w) ||
        nameAttr;
      const flatLabel = `${tabPrefix}${section} ${cbLabel}`;
      result.set(flatLabel, {
        nameAttr,
        type: 'checkbox',
        mainTabName,
        subTabName: null,
        panelCaption: section,
        movement: null,
        column: cbLabel,
        skipPanelInPath: true
      });
    }
  }
}

// Raintree embeds a component instance counter in IDs (e.g. "id.OESHO!!12.jointview").
// The counter changes between scan and fill, so fall back to a suffix match when exact lookup fails.
function findRaintreeElementById(nameAttr: string): HTMLElement | null {
  const exact = document.getElementById(nameAttr);
  if (exact) return exact;
  const baseName = nameAttr.split('.').pop();
  if (!baseName) return null;
  return document.querySelector(`[id$=".${baseName}"]`) as HTMLElement | null;
}

// After all tab scanning is done, click the Add button on each listbox to read its picker choices.
// Done as a post-scan pass so the full-screen navigation it triggers doesn't corrupt tab scanning.
async function readObjExamListboxChoices(
  maintabEl: Element,
  result: Map<string, ObjExamFieldSpec>
): Promise<void> {
  const CACHE_KEY = 'raintree_listbox_choices_v1';
  const TTL = 24 * 60 * 60 * 1000;
  let cache: Record<string, { choices: string[]; ts: number }> = {};
  try {
    cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
  } catch {}
  const now = Date.now();
  let cacheUpdated = false;

  for (const spec of Array.from(result.values())) {
    if (spec.type !== 'listbox') continue;

    // Serve from cache if fresh
    const entry = cache[spec.nameAttr];
    if (entry && now - entry.ts < TTL) {
      spec.choices = entry.choices;
      continue;
    }

    await navigateObjExamTab(maintabEl, spec.mainTabName, spec.subTabName);
    await new Promise((r) => setTimeout(r, 150));

    const lb = findRaintreeElementById(spec.nameAttr);
    if (!lb) continue;

    const addBtn = lb.querySelector(
      '.w-listbox-toolbar-button.mdi-plus-box'
    ) as HTMLElement | null;
    if (!addBtn) continue;

    const pickersBefore = new Set(
      Array.from(document.querySelectorAll('.w-picker'))
    );
    const screensBefore = new Set(
      Array.from(document.querySelectorAll('.screen.screen-active'))
    );
    addBtn.click();

    let waited = 0;
    let picker: Element | null = null;
    while (waited < 2000) {
      for (const p of Array.from(document.querySelectorAll('.w-picker'))) {
        if (
          !pickersBefore.has(p) &&
          p.querySelectorAll('.w-picker-item').length > 0
        ) {
          picker = p;
          break;
        }
      }
      if (picker) break;
      await new Promise((r) => setTimeout(r, 100));
      waited += 100;
    }

    if (picker) {
      spec.choices = Array.from(picker.querySelectorAll('.w-picker-item .text'))
        .map((el) => el.textContent?.trim() || '')
        .filter(Boolean)
        .slice(0, 499);

      cache[spec.nameAttr] = { choices: spec.choices, ts: now };
      cacheUpdated = true;

      const backBtn = document.querySelector(
        '.backbtn, [id^="backbutton-"]'
      ) as HTMLElement | null;
      if (backBtn) {
        backBtn.click();
      } else {
        document.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Escape',
            bubbles: true,
            cancelable: true
          })
        );
      }
      await new Promise((r) => setTimeout(r, 500));
    } else {
      // The Add button may have opened a full screen editor instead of a picker
      // (e.g. Joint Mobility). Detect any new active screen and close it via its
      // back button so it doesn't block closeObjExamForm later.
      const rogueScreen =
        Array.from(document.querySelectorAll('.screen.screen-active')).find(
          (s) => !screensBefore.has(s)
        ) ?? null;
      if (rogueScreen) {
        const rogueBack = (rogueScreen.querySelector('.backbtn') ??
          rogueScreen.querySelector('[id^="backbutton-"]') ??
          document.querySelector('.backbtn') ??
          document.querySelector('[id^="backbutton-"]')) as HTMLElement | null;
        if (rogueBack) {
          rogueBack.click();
        } else {
          document.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: 'Escape',
              bubbles: true,
              cancelable: true
            })
          );
        }
        for (let i = 0; i < 20; i++) {
          await new Promise((r) => setTimeout(r, 100));
          if (!rogueScreen.classList.contains('screen-active')) break;
        }
        await new Promise((r) => setTimeout(r, 200));
      } else {
        console.warn(
          `[Raintree] readObjExamListboxChoices: picker did not open for listbox "${spec.nameAttr}"`
        );
      }
    }
  }

  if (cacheUpdated) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch {}
  }
}

// Walk all tabs and sub-tabs in the a_maintab form and collect all fillable field specs.
async function scanObjExamFormFields(
  maintabEl: Element
): Promise<Map<string, ObjExamFieldSpec>> {
  const result = new Map<string, ObjExamFieldSpec>();

  // The exam opens as its own screen. a_maintab IS the exam tab container.
  // The first .w-tab-control-tabs inside it is the outer exam tab strip (ROM, MMT, etc.).
  const mainTabStrip = maintabEl.querySelector(
    '.w-tab-control-tabs'
  ) as HTMLElement | null;
  if (!mainTabStrip) return result;

  const outerCtrl = mainTabStrip.parentElement!.parentElement!;
  const mainTabItems = Array.from(
    mainTabStrip.querySelectorAll('.tab-item')
  ) as HTMLElement[];

  const originalActiveMain = mainTabItems.find((t) =>
    t.classList.contains('active')
  );

  for (const tabItem of mainTabItems) {
    const mainTabName =
      tabItem.getAttribute('aria-label')?.trim() ||
      tabItem.querySelector('.tab-label, .tab-title')?.textContent?.trim() ||
      tabItem.textContent?.trim() ||
      '';
    if (!mainTabName) continue;

    if (!tabItem.classList.contains('active')) {
      tabItem.click();
      await new Promise((r) => setTimeout(r, 300));
      await dismissAbandonChanges();
    }

    const tabContent = outerCtrl.querySelector(
      '.w-tab-control-content'
    ) as HTMLElement | null;
    if (!tabContent) continue;

    // Check for a nested sub-tab strip inside this content
    const subTabStrip = tabContent.querySelector(
      '.w-tab-control-tabs'
    ) as HTMLElement | null;

    if (subTabStrip) {
      const subOuterCtrl = subTabStrip.parentElement!.parentElement!;
      const subTabItems = Array.from(
        subTabStrip.querySelectorAll('.tab-item')
      ) as HTMLElement[];
      const originalActiveSub = subTabItems.find((t) =>
        t.classList.contains('active')
      );

      // Collect sub-tab labels upfront (label text is stable even if DOM nodes are replaced)
      const subTabLabels = subTabItems.map(
        (t) =>
          t.getAttribute('aria-label')?.trim() ||
          t.querySelector('.tab-label, .tab-title')?.textContent?.trim() ||
          t.textContent?.trim() ||
          ''
      );

      for (let _si = 0; _si < subTabLabels.length; _si++) {
        const subTabName = subTabLabels[_si];
        if (!subTabName) continue;

        // Re-query the strip from the live DOM on every iteration — React may have
        // remounted the entire .w-tab-control between clicks, making all cached
        // element refs stale.
        const liveSubStrip = tabContent.querySelector(
          '.w-tab-control-tabs'
        ) as HTMLElement | null;
        const liveSubItems = liveSubStrip
          ? (Array.from(
              liveSubStrip.querySelectorAll('.tab-item')
            ) as HTMLElement[])
          : [];
        const freshTabItem =
          liveSubItems.find(
            (t) =>
              (t.getAttribute('aria-label')?.trim() ||
                t
                  .querySelector('.tab-label, .tab-title')
                  ?.textContent?.trim() ||
                t.textContent?.trim()) === subTabName
          ) ?? null;
        if (!freshTabItem) {
          console.warn(
            `[Raintree] scanObjExam: sub-tab "${subTabName}" not found in live DOM; skipping`
          );
          continue;
        }

        if (!freshTabItem.classList.contains('active')) {
          freshTabItem.click();
          await new Promise((r) => setTimeout(r, 400));
          await dismissAbandonChanges();
        }

        // Re-query the outer ctrl from the live strip (may also have been remounted)
        const freshSubStrip = tabContent.querySelector(
          '.w-tab-control-tabs'
        ) as HTMLElement | null;
        const freshSubOuter =
          (freshSubStrip?.parentElement?.parentElement as HTMLElement | null) ??
          subOuterCtrl;
        const subTabContent = freshSubOuter.querySelector(
          '.w-tab-control-content'
        ) as HTMLElement | null;
        if (!subTabContent) continue;

        const subPanels = Array.from(
          subTabContent.querySelectorAll('.w-panel')
        );
        if (subPanels.length > 0) {
          for (const panel of subPanels) {
            scanObjExamPanelFields(panel, mainTabName, subTabName, result);
          }
        } else {
          // No .w-panel elements — treat the whole sub-tab content as one panel
          // (e.g. Gross UE ROM which uses a flat grid layout without panel wrappers)
          scanObjExamPanelFields(
            subTabContent,
            mainTabName,
            subTabName,
            result,
            subTabName
          );
        }
        // Also scan for row-list listboxes (e.g. Joint Mobility) in this sub-tab content
        await scanObjExamTabListboxes(
          subTabContent,
          mainTabName,
          subTabName,
          result
        );
      }

      // Restore original active sub-tab using a fresh live reference
      if (originalActiveSub) {
        const origLabel =
          originalActiveSub.getAttribute('aria-label')?.trim() ||
          originalActiveSub
            .querySelector('.tab-label, .tab-title')
            ?.textContent?.trim() ||
          originalActiveSub.textContent?.trim() ||
          '';
        const liveRestoreStrip = tabContent.querySelector(
          '.w-tab-control-tabs'
        ) as HTMLElement | null;
        const liveRestoreItem = liveRestoreStrip
          ? (
              Array.from(
                liveRestoreStrip.querySelectorAll('.tab-item')
              ) as HTMLElement[]
            ).find(
              (t) =>
                (t.getAttribute('aria-label')?.trim() ||
                  t
                    .querySelector('.tab-label, .tab-title')
                    ?.textContent?.trim() ||
                  t.textContent?.trim()) === origLabel
            )
          : null;
        const restoreItem = liveRestoreItem ?? originalActiveSub;
        if (!restoreItem.classList.contains('active')) {
          restoreItem.click();
          await new Promise((r) => setTimeout(r, 150));
        }
      }

      // Also scan direct panels in the main tab content that live OUTSIDE the sub-tab control
      // (e.g. Tenderness, Effusion, Girth, Crepitus, Comments on the Palpation tab).
      const directPanels = Array.from(
        tabContent.querySelectorAll('.w-panel')
      ).filter((p) => !subOuterCtrl.contains(p));
      for (const panel of directPanels) {
        scanObjExamPanelFields(panel, mainTabName, null, result);
      }
      // Flat widgets outside panels and outside the sub-tab control (e.g. Gait/Posture combos,
      // memos, checkboxes that use absolute positioning rather than panel containers).
      scanObjExamFlatFields(tabContent, mainTabName, subOuterCtrl, result);
    } else {
      const mainPanels = Array.from(tabContent.querySelectorAll('.w-panel'));
      if (mainPanels.length > 0) {
        for (const panel of mainPanels) {
          scanObjExamPanelFields(panel, mainTabName, null, result);
        }
      } else {
        scanObjExamPanelFields(
          tabContent,
          mainTabName,
          null,
          result,
          mainTabName
        );
      }
      // Also scan for row-list listboxes in this main-tab content
      await scanObjExamTabListboxes(tabContent, mainTabName, null, result);
      // Flat widgets that bypass the panel structure (absolute-positioned fields)
      scanObjExamFlatFields(tabContent, mainTabName, null, result);
    }
  }

  if (originalActiveMain && !originalActiveMain.classList.contains('active')) {
    originalActiveMain.click();
    await new Promise((r) => setTimeout(r, 200));
  }

  // Scan panels in the exam screen that live OUTSIDE the tab control (e.g. "Observations"
  // and "Objective Findings Comments" panels positioned below the tab strip). These fields
  // are always in the DOM regardless of active tab, so '_screen' is used as a synthetic
  // mainTabName — navigateObjExamTab will silently skip navigation when no tab matches.
  const examScreen = maintabEl.closest('.screen') as HTMLElement | null;
  if (examScreen) {
    const screenPanels = Array.from(
      examScreen.querySelectorAll('.w-panel')
    ).filter((p) => !maintabEl.contains(p));
    for (const panel of screenPanels) {
      scanObjExamPanelFields(panel, '_screen', null, result);
    }
  }

  await readObjExamListboxChoices(maintabEl, result);

  return result;
}

// Navigate a_maintab to the given main tab (and optional sub-tab).
async function navigateObjExamTab(
  maintabEl: Element,
  mainTabName: string,
  subTabName: string | null
) {
  // a_maintab IS the exam container — first .w-tab-control-tabs is the outer exam tab strip
  const mainTabStrip = maintabEl.querySelector(
    '.w-tab-control-tabs'
  ) as HTMLElement | null;
  if (!mainTabStrip) return;

  const outerCtrl = mainTabStrip.parentElement!.parentElement!;
  const mainTabItem = mainTabStrip.querySelector(
    `.tab-item[aria-label="${mainTabName}"]`
  ) as HTMLElement | null;

  if (mainTabItem && !mainTabItem.classList.contains('active')) {
    mainTabItem.click();
    await new Promise((r) => setTimeout(r, 300));
  }

  if (!subTabName) return;

  const tabContent = outerCtrl.querySelector(
    '.w-tab-control-content'
  ) as HTMLElement | null;
  if (!tabContent) return;

  const subTabStrip = tabContent.querySelector(
    '.w-tab-control-tabs'
  ) as HTMLElement | null;
  if (!subTabStrip) return;

  const subTabItem = subTabStrip.querySelector(
    `.tab-item[aria-label="${subTabName}"]`
  ) as HTMLElement | null;

  if (subTabItem && !subTabItem.classList.contains('active')) {
    subTabItem.click();
    // Sub-tab content (especially dense grids like Palpation) needs time to render.
    await new Promise((r) => setTimeout(r, 400));
  }
}

// Fill one exam row: open it via right-click > Edit, navigate to each field, fill, save.
async function fillObjExamRow(
  listboxId: string,
  rowName: string,
  rowObj: Record<string, string>,
  listboxFills: Array<{
    spec: ObjExamFieldSpec;
    rowData: Record<string, string>;
  }> = [],
  skipOpenAndClose = false
) {
  const fieldSpecMap = _objExamFieldSpecs.get(rowName);
  if (!fieldSpecMap) {
    console.warn('[Raintree] fillObjExamRow: no field spec for', rowName);
    return;
  }

  let maintabEl: Element | null = null;

  if (skipOpenAndClose) {
    // Exam form is already open — use the currently active screen
    const activeScreen = document.querySelector('.screen.screen-active');
    maintabEl = activeScreen?.querySelector('[data-widgetname="a_maintab"]') ?? null;
    if (!maintabEl) {
      console.warn('[Raintree] fillObjExamRow: no open exam screen found for fill');
      return;
    }
  } else {
    const listbox = document.getElementById(listboxId) as HTMLElement | null;
    if (!listbox) {
      console.warn('[Raintree] fillObjExamRow: listbox not found', listboxId);
      return;
    }

    // rowName may be a data-index value (unique per row); try that first, then text search.
    const targetRow =
      (listbox.querySelector(`tr[data-index="${rowName}"]`) as Element | null) ??
      findRowInListbox(listbox, rowName);
    if (!targetRow) {
      console.warn('[Raintree] fillObjExamRow: row not found in listbox:', rowName);
      return;
    }

    // Record screen before opening so we can detect the exam screen transition
    const beforeScreenFill = document.querySelector('.screen.screen-active');

    const firstDataCell = Array.from(targetRow.querySelectorAll('td')).find(
      (td) =>
        !td.classList.contains('w-listbox-extended-checkbox') &&
        !td.classList.contains('w-listbox-toolbar-cell')
    ) as HTMLElement | null;
    const clickTarget = firstDataCell || (targetRow as HTMLElement);

    clickTarget.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true, button: 2 }));

    let contextMenu: Element | null = null;
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 100));
      contextMenu = document.querySelector('.popup-overlay') ?? null;
      if (contextMenu) break;
    }
    if (!contextMenu) {
      console.warn('[Raintree] fillObjExamRow: context menu did not open');
      return;
    }

    const editItem = contextMenu.querySelector('.popup-item[aria-label="Edit"]') as HTMLElement | null;
    if (!editItem) {
      console.warn('[Raintree] fillObjExamRow: Edit item not found in context menu');
      return;
    }
    editItem.click();

    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 200));
      const active = document.querySelector('.screen.screen-active');
      if (active && active !== beforeScreenFill) {
        maintabEl = active.querySelector('[data-widgetname="a_maintab"]');
        break;
      }
    }
    if (!maintabEl) {
      console.warn('[Raintree] fillObjExamRow: exam screen did not load for', rowName);
      return;
    }
  }

  // Group fills by (mainTab, subTab) to minimise tab switches
  const groups = new Map<
    string,
    Array<{ fieldLabel: string; value: string; spec: ObjExamFieldSpec }>
  >();
  for (const [rawLabel, rawValue] of Object.entries(rowObj)) {
    if (isEmptyOrNull(String(rawValue))) continue;
    const value = String(rawValue).trim();
    if (/^not\s+discussed$/i.test(value)) continue;
    // Exact match first, then fuzzy
    let spec = fieldSpecMap.get(rawLabel);
    if (!spec) {
      const normLabel = normalizeKey(rawLabel);
      for (const [k, s] of Array.from(fieldSpecMap.entries())) {
        if (normalizeKey(k) === normLabel) {
          spec = s;
          break;
        }
      }
    }
    if (!spec) {
      console.warn('[Raintree] fillObjExamRow: no spec for field', rawLabel);
      continue;
    }
    const navKey = `${spec.mainTabName}::${spec.subTabName ?? ''}`;
    if (!groups.has(navKey)) groups.set(navKey, []);
    groups.get(navKey)!.push({ fieldLabel: rawLabel, value, spec });
  }

  console.log(
    `[Raintree] fillObjExamRow "${rowName}": ${Object.keys(rowObj).length} flat fields, ${groups.size} tab groups`
  );

  // Navigate to each tab group and fill
  for (const entries of Array.from(groups.values())) {
    const { mainTabName, subTabName } = entries[0].spec;
    console.log(
      `[Raintree] fillObjExamRow: tab "${mainTabName}"/"${subTabName ?? ''}" — ${entries.length} fields`
    );
    await navigateObjExamTab(maintabEl, mainTabName, subTabName);

    // Fill movement-level values BEFORE panel-level "noright" dropdowns. When noright =
    // "Unremarkable" is set first, Raintree hides/disables individual ROM inputs so they
    // can't be filled. Also skip filling noright = "Unremarkable" if the same panel has
    // movement values — contradictory data, movement values take precedence.
    const panelsWithMovements = new Set<string>();
    for (const { spec, value: v } of entries) {
      if (spec.movement !== null && v)
        panelsWithMovements.add(spec.panelCaption);
    }
    const orderedEntries = [
      ...entries.filter((e) => e.spec.column !== 'noright'),
      ...entries.filter(
        (e) =>
          e.spec.column === 'noright' &&
          !(
            e.value === 'Unremarkable' &&
            panelsWithMovements.has(e.spec.panelCaption)
          )
      )
    ];

    for (const { fieldLabel, value, spec } of orderedEntries) {
      const el =
        (document.querySelector(
          `[name="${spec.nameAttr}"]`
        ) as HTMLElement | null) ?? findRaintreeElementById(spec.nameAttr);
      if (!el) {
        console.warn(
          '[Raintree] fillObjExamRow: element not found for',
          fieldLabel,
          'nameAttr=',
          spec.nameAttr
        );
        continue;
      }

      if (spec.type === 'input') {
        const inp = el as HTMLInputElement;
        inp.focus();
        nativeInputFill(inp, value);
        inp.dispatchEvent(new Event('focusout', { bubbles: true }));
        inp.blur();
      } else if (spec.type === 'select') {
        const sel = el as HTMLSelectElement;
        const opt = Array.from(sel.options).find(
          (o) =>
            matchesChoice(value, o.textContent?.trim() || '') ||
            matchesChoice(value, o.value)
        );
        if (opt) {
          sel.focus();
          const nativeSetter = Object.getOwnPropertyDescriptor(
            window.HTMLSelectElement.prototype,
            'value'
          )?.set;
          if (nativeSetter) nativeSetter.call(sel, opt.value);
          else sel.value = opt.value;
          sel.dispatchEvent(new Event('input', { bubbles: true }));
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          sel.dispatchEvent(new Event('blur', { bubbles: true }));
        } else {
          console.warn(
            '[Raintree] fillObjExamRow: no option match for',
            fieldLabel,
            '=',
            value,
            '— available:',
            Array.from(sel.options).map((o) => o.textContent?.trim())
          );
        }
      } else if (spec.type === 'memo') {
        await eventBasedFill(el as HTMLElement, value);
      } else if (spec.type === 'checkbox') {
        const cb = (
          el instanceof HTMLInputElement
            ? el
            : el.querySelector('input[type="checkbox"]')
        ) as HTMLInputElement | null;
        if (cb) {
          const shouldCheck = /^(true|yes|1|checked|x)$/i.test(value.trim());
          if (cb.checked !== shouldCheck) cb.click();
        }
      }
    }
    // Give React time to commit input field values before navigating to the next tab.
    // Without this, tab navigation unmounts the panel before React processes the events,
    // causing filled input values to be lost (race condition confirmed via console testing).
    const hasInputs = orderedEntries.some((e) => e.spec.type === 'input');
    if (hasInputs) await new Promise((r) => setTimeout(r, 150));
  }

  // Fill any row-list listboxes (e.g. Joint Mobility) within the open exam form.
  // For each row in the AI response: right-click the sub-listbox → Add → 1st picker option
  // (which opens the row editor) → fill → save. Repeated once per array item.
  const specGroups = new Map<
    string,
    { spec: ObjExamFieldSpec; rows: Record<string, string>[] }
  >();
  for (const { spec, rowData } of listboxFills) {
    const key = spec.nameAttr;
    if (!specGroups.has(key)) specGroups.set(key, { spec, rows: [] });
    specGroups.get(key)!.rows.push(rowData);
  }
  for (const { spec, rows } of Array.from(specGroups.values())) {
    // Re-query maintabEl each iteration — the form remounts after row editors close,
    // making the original reference stale and silently breaking tab navigation.
    const freshActive = document.querySelector('.screen.screen-active');
    const freshMaintab = (freshActive?.querySelector(
      '[data-widgetname="a_maintab"]'
    ) ?? maintabEl) as Element | null;
    await navigateObjExamTab(
      freshMaintab ?? maintabEl!,
      spec.mainTabName,
      spec.subTabName
    );
    // Extra render wait for tab content after navigation
    await new Promise((r) => setTimeout(r, 150));
    for (const rowData of rows) {
      // Re-query the listbox — the form can remount after an editor closes.
      // Scope lookup to the current active screen first to avoid matching
      // identically-named listboxes in other forms.
      const activeScreen = document.querySelector('.screen.screen-active');
      const baseName = spec.nameAttr.split('.').pop() || '';
      const lbEl =
        (activeScreen?.querySelector(
          `[id="${spec.nameAttr}"]`
        ) as HTMLElement | null) ??
        (baseName
          ? (activeScreen?.querySelector(
              `[id$=".${baseName}"]`
            ) as HTMLElement | null)
          : null) ??
        findRaintreeElementById(spec.nameAttr);
      if (!lbEl) {
        console.warn(
          '[Raintree] fillObjExamRow: sub-listbox not found',
          spec.nameAttr
        );
        continue;
      }
      await addAndFillObjExamSubListboxRow(
        lbEl,
        rowData,
        spec.listboxHeaders || []
      );
    }
  }

  // Close the exam form.  Scope the Save/Cancel lookup to the screen that owns the
  // exam form (has a_maintab) so a stacked sub-screen can't steal the click.
  // Dismiss any stray screens sitting on top of the exam form (e.g. the Special Tests
  // full-screen picker that Raintree opens instead of a .w-picker overlay). Walk active
  // screens that lack a_maintab and press their back/cancel button.
  const dismissStrayScreens = async () => {
    for (let attempt = 0; attempt < 5; attempt++) {
      const stray = Array.from(
        document.querySelectorAll('.screen.screen-active')
      ).find(
        (s) => !s.querySelector('[data-widgetname="a_maintab"]')
      ) as HTMLElement | null;
      if (!stray) break;
      const closeBtn = (stray.querySelector('[id^="backbutton-"]') ??
        stray.querySelector('.backbtn') ??
        stray.querySelector('#ToolbarButton\\.Cancel') ??
        stray.querySelector(
          '.toolbar-button[aria-label="Cancel"]'
        )) as HTMLElement | null;
      if (closeBtn) {
        console.log(
          '[Raintree] fillObjExamRow: closing stray screen',
          stray.className,
          '—',
          closeBtn.id || closeBtn.className
        );
        closeBtn.click();
        await dismissAbandonChanges(800);
      } else {
        document.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Escape',
            keyCode: 27,
            bubbles: true
          })
        );
      }
      for (let i = 0; i < 15; i++) {
        await new Promise((r) => setTimeout(r, 100));
        if (!stray.classList.contains('screen-active')) break;
      }
    }
  };

  await dismissStrayScreens();

  if (skipOpenAndClose) {
    // User opened the form manually — leave it open for them to review and save
    return;
  }

  const examFormScreen = Array.from(
    document.querySelectorAll('.screen.screen-active')
  ).find((s) =>
    s.querySelector('[data-widgetname="a_maintab"]')
  ) as HTMLElement | null;

  const examClosed = () => {
    const live = document.querySelector('.screen.screen-active');
    return !live || !live.querySelector('[data-widgetname="a_maintab"]');
  };

  const saveBtn = examFormScreen
    ? ((examFormScreen.querySelector('#ToolbarButton\\.Save') ??
        examFormScreen.querySelector(
          '.toolbar-button[aria-label="Save"]'
        )) as HTMLElement | null)
    : null;

  console.log(
    '[Raintree] fillObjExamRow: closing form — examFormScreen=',
    examFormScreen ? examFormScreen.className : 'NOT FOUND',
    '— saveBtn=',
    saveBtn ? saveBtn.id : 'NOT FOUND'
  );

  if (saveBtn) {
    saveBtn.click();
    for (let i = 0; i < 25; i++) {
      await new Promise((r) => setTimeout(r, 100));
      if (examClosed()) break;
    }
  }

  // If form is still open (Save didn't work or wasn't found), try Cancel + dismiss Abandon Changes.
  if (!examClosed()) {
    console.warn(
      '[Raintree] fillObjExamRow: Save did not close form — trying Cancel for',
      rowName
    );
    const cancelBtn = examFormScreen
      ? ((examFormScreen.querySelector('#ToolbarButton\\.Cancel') ??
          examFormScreen.querySelector(
            '.toolbar-button[aria-label="Cancel"]'
          )) as HTMLElement | null)
      : (document.querySelector(
          '#ToolbarButton\\.Cancel'
        ) as HTMLElement | null);

    if (cancelBtn) {
      cancelBtn.click();
      await dismissAbandonChanges(1500);
    } else {
      document.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Escape',
          keyCode: 27,
          bubbles: true,
          cancelable: true
        })
      );
      await dismissAbandonChanges(1500);
    }

    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 100));
      if (examClosed()) break;
    }
  }

  if (!examClosed()) {
    console.warn(
      '[Raintree] fillObjExamRow: form still open after Save+Cancel for',
      rowName
    );
  }
  await new Promise((r) => setTimeout(r, 300));
}

// Returns the nested key path for a spec: [mainTab, subTab?, panelCaption?, movement?, column]
function objExamNestPath(spec: ObjExamFieldSpec): string[] {
  // Listbox: path is just [mainTab, panelCaption] — value at that path is an array of row objects
  if (spec.type === 'listbox') {
    const path: string[] = [spec.mainTabName];
    if (spec.subTabName) path.push(spec.subTabName);
    path.push(spec.panelCaption);
    return path;
  }
  const path: string[] = [spec.mainTabName];
  if (spec.subTabName) {
    path.push(spec.subTabName);
    if (spec.panelCaption !== spec.subTabName) path.push(spec.panelCaption);
  } else if (!spec.skipPanelInPath) {
    path.push(spec.panelCaption);
  }
  if (spec.movement) path.push(expandRomMotion(spec.movement));
  path.push(spec.column);
  return path;
}

/** Expand ROM/MMT motion abbreviations to full clinical names. */
function expandRomMotion(raw: string): string {
  const map: Record<string, string> = {
    // Generic ROM terms
    'flex': 'Flexion', 'ext': 'Extension', 'abd': 'Abduction',
    'add': 'Adduction', 'er': 'External Rotation', 'ir': 'Internal Rotation',
    'ha': 'Horizontal Adduction', 'habd': 'Horizontal Abduction',
    // Gross UE MMT compound codes
    'shshrug': 'Shoulder Shrug', 'shflex': 'Shoulder Flexion',
    'shabd': 'Shoulder Abduction', 'shir': 'Shoulder Internal Rotation',
    'sher': 'Shoulder External Rotation', 'shhorabd': 'Shoulder Horizontal Abduction',
    'shhoradd': 'Shoulder Horizontal Adduction',
    'elflex': 'Elbow Flexion', 'elext': 'Elbow Extension',
    'wrflex': 'Wrist Flexion', 'wrext': 'Wrist Extension',
    'fasup': 'Forearm Supination', 'fapro': 'Forearm Pronation',
    'rdev': 'Radial Deviation', 'udev': 'Ulnar Deviation',
    'lum': 'Lumbrical Interossei', 'thumb': 'Thumb Extension',
  };
  return map[raw.toLowerCase()] ?? raw;
}

// Deep-sets a value in a nested object using a key path.
function setNestedValue(
  obj: Record<string, any>,
  path: string[],
  value: any
): void {
  let cur = obj;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i];
    if (typeof cur[k] !== 'object' || cur[k] === null || Array.isArray(cur[k]))
      cur[k] = {};
    cur = cur[k];
  }
  cur[path[path.length - 1]] = value;
}

// Fuzzy key lookup: exact match first, then prefix match in either direction (case-insensitive).
// Handles scan-label vs AI-token divergence, e.g. scan produces "Flexion" but AI sends "Flex".
function fuzzyObjKey(
  obj: Record<string, any>,
  key: string
): string | undefined {
  if (key in obj) return key;
  const kL = key.toLowerCase();
  for (const k of Object.keys(obj)) {
    const oL = k.toLowerCase();
    if (oL.startsWith(kL) || kL.startsWith(oL)) return k;
  }
  return undefined;
}

// Deep-gets a value from a nested object using a key path. Returns undefined if not found.
// Uses fuzzy key matching at each level so abbreviated AI keys match expanded scan labels.
function getNestedValue(obj: any, path: string[]): any {
  let cur = obj;
  for (const k of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    const actual = fuzzyObjKey(cur as Record<string, any>, k) ?? k;
    cur = cur[actual];
  }
  return cur;
}

// Open a single objview exam row via right-click → Edit and wait for the exam screen to load.
// Clicking Edit opens a NEW .screen.screen-active (e.g. "Shoulder Evaluation") — we wait
// for that screen transition and return { examScreen, maintabEl } so the caller can close it.
async function openObjExamRow(rowEl: HTMLElement): Promise<{
  examScreen: Element;
  maintabEl: Element;
  examTitle: string;
} | null> {
  const dataCell = Array.from(rowEl.querySelectorAll('td')).find(
    (td) =>
      !td.classList.contains('w-listbox-extended-checkbox') &&
      !td.classList.contains('w-listbox-toolbar-cell')
  ) as HTMLElement | null;

  // Record the active screen before opening
  const beforeScreen = document.querySelector('.screen.screen-active');

  (dataCell || rowEl).dispatchEvent(
    new MouseEvent('contextmenu', {
      bubbles: true,
      cancelable: true,
      button: 2
    })
  );

  let contextMenu: Element | null = null;
  for (let i = 0; i < 20; i++) {
    await new Promise((r) => setTimeout(r, 100));
    contextMenu = document.querySelector('.popup-overlay') ?? null;
    if (contextMenu) break;
  }
  if (!contextMenu) return null;

  const editItem = contextMenu.querySelector(
    '.popup-item[aria-label="Edit"]'
  ) as HTMLElement | null;
  if (!editItem) return null;
  editItem.click();

  // Wait for a NEW active screen to appear (the exam screen)
  let examScreen: Element | null = null;
  for (let i = 0; i < 40; i++) {
    await new Promise((r) => setTimeout(r, 200));
    const active = document.querySelector('.screen.screen-active');
    if (active && active !== beforeScreen) {
      examScreen = active;
      break;
    }
  }
  if (!examScreen) return null;

  const maintabEl = examScreen.querySelector('[data-widgetname="a_maintab"]');
  if (!maintabEl) return null;

  // Try to get a human-readable exam title from the screen header
  const examTitle =
    (
      examScreen.querySelector('.screen-title') as HTMLElement
    )?.innerText?.trim() ||
    (
      examScreen.querySelector(
        '.screen-header h1, .screen-header h2, .screen-header .title'
      ) as HTMLElement
    )?.innerText?.trim() ||
    (
      examScreen.querySelector(
        '[data-widgetname="a_title"], [data-widgetname="a_header"]'
      ) as HTMLElement
    )?.innerText?.trim() ||
    (examScreen as HTMLElement).getAttribute('aria-label')?.trim() ||
    '';

  return { examScreen, maintabEl, examTitle };
}

// Close the exam form by clicking Save — avoids "Abandon Changes?" dialog entirely.
async function closeObjExamForm(examScreen: Element): Promise<void> {
  const saveBtn = (examScreen.querySelector('#ToolbarButton\\.Save') ??
    document.querySelector('#ToolbarButton\\.Save') ??
    document.querySelector(
      '.toolbar-button[aria-label="Save"]'
    )) as HTMLElement | null;

  if (saveBtn) {
    saveBtn.click();
  } else {
    document.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'F10',
        bubbles: true,
        cancelable: true
      })
    );
  }

  // Wait for the exam screen to lose .screen-active
  for (let i = 0; i < 40; i++) {
    await new Promise((r) => setTimeout(r, 100));
    if (!examScreen.classList.contains('screen-active')) break;
  }
  await new Promise((r) => setTimeout(r, 300));
}

// Add a new row to a sub-listbox inside the open exam form (e.g. Joint Mobility):
// 1. Click toolbar "+" → picker appears → select matching option → back button → picker closes
// 2. Click the newly added row to select it
// 3. Click toolbar Edit (pen) button → Raintree opens a new .screen.screen-active editor
// 4. Fill each header field by matching label text → back/OK to close
async function addAndFillObjExamSubListboxRow(
  lbEl: HTMLElement,
  rowData: Record<string, string>,
  headers: string[]
): Promise<void> {
  const addBtn = lbEl.querySelector(
    '.w-listbox-toolbar-button.mdi-plus-box'
  ) as HTMLElement | null;
  if (!addBtn) {
    console.warn(
      '[Raintree] addAndFillObjExamSubListboxRow: Add toolbar button not found'
    );
    return;
  }

  // Skip rows where the AI returned "Not Discussed" or left the key column
  // blank — "Not Discussed" is not a valid picker option in Raintree.
  const firstColValue = headers[0] ? rowData[headers[0]]?.trim() : '';
  if (!firstColValue || /^not\s+discussed$/i.test(firstColValue)) {
    return;
  }

  // ── Step 1: snapshot existing row data-indexes before add ────────────────
  const existingIndexes = new Set(
    Array.from(lbEl.querySelectorAll('tbody tr'))
      .map((r) => r.getAttribute('data-index'))
      .filter(Boolean)
  );

  // ── Step 2: open picker and select the matching item ──────────────────────
  addBtn.click();

  let picker: Element | null = null;
  for (let i = 0; i < 30; i++) {
    await new Promise((r) => setTimeout(r, 50));
    picker = document.querySelector('.w-picker');
    if (picker) break;
  }

  const closePickerFn = async () => {
    const pickerEl = document.querySelector('.w-picker');
    if (!pickerEl) return;
    // Scope back-button search to INSIDE the picker first so we never accidentally
    // click the exam form's own back button (which would close the whole form).
    const save = pickerEl.querySelector(
      '.w-picker-toolbar .mdi-content-save:not(.disabled)'
    ) as HTMLElement | null;
    if (save) {
      save.click();
    } else {
      const back = (pickerEl.querySelector('.backdown') ??
        pickerEl.querySelector('.backbtn') ??
        pickerEl.querySelector('[title*="Back"]') ??
        pickerEl.querySelector('[aria-label*="Back"]') ??
        pickerEl.querySelector('[role="button"]:first-child') ??
        pickerEl.querySelector('button:first-child') ??
        document.querySelector(
          '.w-picker-toolbar .backbtn'
        )) as HTMLElement | null;
      if (back) {
        back.click();
      } else {
        document.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Escape',
            keyCode: 27,
            bubbles: true
          })
        );
      }
    }
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 50));
      if (!document.querySelector('.w-picker')) break;
    }
  };

  if (picker) {
    const targetValue = headers[0] ? rowData[headers[0]]?.trim() : '';
    const allItems = Array.from(picker.querySelectorAll('.w-picker-item'));
    const matchedItem = targetValue
      ? (allItems.find((el) =>
          matchesChoice(
            targetValue,
            (el as HTMLElement).textContent?.trim() || ''
          )
        ) as HTMLElement | null)
      : null;

    if (matchedItem) {
      const cb = matchedItem.querySelector(
        'input[type="checkbox"]'
      ) as HTMLInputElement | null;
      if (cb && !cb.checked) {
        cb.click();
        await new Promise((r) => setTimeout(r, 80));
      } else if (!cb) {
        matchedItem.click();
        await new Promise((r) => setTimeout(r, 80));
      }
    } else {
      console.warn(
        '[Raintree] addAndFillObjExamSubListboxRow: no picker match for',
        targetValue,
        '— available:',
        allItems
          .slice(0, 10)
          .map((el) => (el as HTMLElement).textContent?.trim())
      );
      await closePickerFn();
      return;
    }

    // Commit selection with the picker toolbar save (floppy disk).
    // Back button cancels without adding; save is required to add the row.
    const pickerSave = document.querySelector(
      '.w-picker-toolbar .mdi-content-save:not(.disabled)'
    ) as HTMLElement | null;
    if (pickerSave) {
      pickerSave.click();
    } else {
      await closePickerFn();
    }
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 50));
      if (!document.querySelector('.w-picker')) break;
    }
  } else {
    console.warn(
      '[Raintree] addAndFillObjExamSubListboxRow: picker never appeared'
    );
    return;
  }

  await new Promise((r) => setTimeout(r, 150));

  // ── Step 3: find the newly added row by data-index diff ───────────────────
  const freshLb = (lbEl.id ? findRaintreeElementById(lbEl.id) : null) ?? lbEl;
  const newRow = Array.from(freshLb.querySelectorAll('tbody tr')).find(
    (r) => !existingIndexes.has(r.getAttribute('data-index'))
  ) as HTMLElement | null;
  if (!newRow) {
    console.warn(
      '[Raintree] addAndFillObjExamSubListboxRow: new row not found in listbox'
    );
    return;
  }

  // Click the first data cell to send ListFocusItem (selecting <tr> alone doesn't work)
  const selectCell = Array.from(newRow.querySelectorAll('td')).find(
    (td) =>
      !td.classList.contains('w-listbox-extended-checkbox') &&
      !td.classList.contains('w-listbox-toolbar-cell')
  ) as HTMLElement | null;
  (selectCell ?? newRow).click();
  await new Promise((r) => setTimeout(r, 100));

  // ── Step 4: click toolbar Edit (pen) button to open row editor ────────────
  const editBtn = freshLb.querySelector(
    '.w-listbox-toolbar-button.mdi-pen'
  ) as HTMLElement | null;
  if (!editBtn) {
    console.warn(
      '[Raintree] addAndFillObjExamSubListboxRow: Edit toolbar button not found'
    );
    return;
  }

  const screenBefore = document.querySelector('.screen.screen-active');
  const popupsBefore = document.querySelectorAll('.popup-overlay').length;
  editBtn.click();

  // The row editor is injected into the active screen's DOM (not a new .screen element).
  // Wait for the btnok widget to appear, which signals the editor is ready.
  let editorScreen: Element | null = null;
  for (let i = 0; i < 40; i++) {
    await new Promise((r) => setTimeout(r, 50));
    // Primary: btnok appeared in the document (editor embedded in main screen)
    const btnOKEl = document.querySelector('[data-widgetname="btnok"]');
    if (btnOKEl) {
      editorScreen =
        btnOKEl.closest('.screen') ??
        document.querySelector('.screen.screen-active');
      break;
    }
    // Fallback: new .screen.screen-active pushed on top
    const active = document.querySelector('.screen.screen-active');
    if (active && active !== screenBefore) {
      editorScreen = active;
      break;
    }
    // Fallback: popup/dialog appeared
    const popups = document.querySelectorAll('.popup-overlay');
    if (popups.length > popupsBefore) {
      editorScreen = popups[popups.length - 1];
      break;
    }
  }

  if (!editorScreen) {
    console.warn(
      '[Raintree] addAndFillObjExamSubListboxRow: row editor did not open — no btnok after 2s. Headers:',
      headers,
      'rowData:',
      rowData
    );
    return;
  }
  console.log(
    '[Raintree] addAndFillObjExamSubListboxRow: editor opened, editorScreen=',
    editorScreen.className
  );

  // ── Step 5: fill each header field by matching its label ──────────────────
  // Build a label→field map from the editor screen. Raintree puts the column label
  // in the nearest .w-panel-caption-text ancestor, OR in a standard label[for=id].

  // Diagnostic: log all fillable fields and their resolved labels so label-mismatch
  // issues (e.g. editor labels differ from listbox column headers) are visible in DevTools.
  const allEditorFields = Array.from(
    editorScreen.querySelectorAll(
      'input:not([readonly]):not([disabled]), select:not([disabled]), textarea:not([readonly])'
    )
  ) as HTMLElement[];
  console.log(
    '[Raintree] addAndFillObjExamSubListboxRow: editor has',
    allEditorFields.length,
    'fillable fields:',
    allEditorFields.map((el) => ({
      tag: el.tagName,
      id: el.id,
      label: (() => {
        if (el.id) {
          const lb = editorScreen!.querySelector(`label[for="${el.id}"]`);
          if (lb) return (lb as HTMLElement).innerText?.trim();
        }
        const panel = el.closest('.w-panel');
        if (panel) {
          const cap = panel.querySelector('.w-panel-caption-text');
          if (cap) return cap.textContent?.trim();
        }
        const titled = el.closest('[title]');
        if (titled) return (titled as HTMLElement).title?.trim();
        const prev = el.previousElementSibling;
        if (prev?.tagName === 'LABEL')
          return (prev as HTMLElement).innerText?.trim();
        return '(no label)';
      })()
    }))
  );

  function resolveEditorLabel(el: HTMLElement): string {
    // 1. Standard label[for=id]
    if (el.id) {
      const lb = editorScreen!.querySelector(`label[for="${el.id}"]`);
      if (lb) return (lb as HTMLElement).innerText?.trim() ?? '';
    }
    // 2. .w-panel-caption-text wrapping this widget (Raintree panel header)
    const panel = el.closest('.w-panel');
    if (panel) {
      const cap = panel.querySelector('.w-panel-caption-text');
      if (cap) return cap.textContent?.trim() ?? '';
    }
    // 3. Closest enclosing element with a title attribute
    const titled = el.closest('[title]');
    if (titled) return (titled as HTMLElement).title?.trim() ?? '';
    // 4. Previous sibling label
    const prev = el.previousElementSibling;
    if (prev?.tagName === 'LABEL')
      return (prev as HTMLElement).innerText?.trim() ?? '';
    return '';
  }

  for (const header of headers) {
    // Skip the first column — it was already set by the picker
    if (header === headers[0]) continue;
    const val = rowData[header]?.trim();
    if (!val) continue;

    let field: HTMLElement | null = null;
    for (const el of Array.from(
      editorScreen.querySelectorAll(
        'input:not([readonly]):not([disabled]), select:not([disabled]), textarea:not([readonly])'
      )
    )) {
      const htmlEl = el as HTMLElement;
      const labelText = resolveEditorLabel(htmlEl);
      if (normalizeKey(labelText) === normalizeKey(header)) {
        field = htmlEl;
        break;
      }
    }

    if (!field) {
      console.warn(
        '[Raintree] addAndFillObjExamSubListboxRow: no field for header',
        header,
        '— val:',
        val,
        '— headers:',
        headers,
        '— rowData:',
        rowData
      );
      continue;
    }
    console.log(
      '[Raintree] addAndFillObjExamSubListboxRow: filling',
      header,
      '=',
      val,
      'in',
      field.tagName,
      field.id
    );

    if (field instanceof HTMLSelectElement) {
      const opt = Array.from(field.options).find(
        (o) =>
          matchesChoice(val, o.textContent?.trim() || '') ||
          matchesChoice(val, o.value)
      );
      if (opt) {
        field.focus();
        field.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
        const nativeSetter = Object.getOwnPropertyDescriptor(
          window.HTMLSelectElement.prototype,
          'value'
        )?.set;
        if (nativeSetter) nativeSetter.call(field, opt.value);
        else field.value = opt.value;
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
        field.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
        field.blur();
      }
    } else if (
      field instanceof HTMLInputElement ||
      field instanceof HTMLTextAreaElement
    ) {
      // Match fillRowListRow's proven focusin→fill→focusout→blur cycle so Raintree
      // sends FieldSetContent. Using a generic Event('blur') is insufficient —
      // Raintree checks for real FocusEvent instances.
      field.focus();
      field.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
      nativeInputFill(field as HTMLInputElement, val);
      field.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
      field.blur();
    }
    // Brief pause so Raintree can process each FieldSetContent before we move on.
    await new Promise((r) => setTimeout(r, 80));
  }

  // ── Step 6: save and close the row editor ────────────────────────────────
  await new Promise((r) => setTimeout(r, 100));

  const okBtnWrapper = editorScreen.querySelector(
    '[data-widgetname="btnok"]'
  ) as HTMLElement | null;
  const okBtnInner =
    (okBtnWrapper?.querySelector('.w-image-button') as HTMLElement | null) ??
    (editorScreen.querySelector('[aria-label="OK"]') as HTMLElement | null) ??
    (Array.from(editorScreen.querySelectorAll('button')).find(
      (b) => b.textContent?.trim() === 'OK'
    ) as HTMLElement | null);

  // editorClosed: when editorScreen is the MAIN exam screen (btnok embedded), done when btnok
  // is removed from DOM.  When editorScreen is a real sub-screen, done when it loses screen-active.
  // Do NOT use "no btnok anywhere" as a fallback — that fires immediately when the sub-screen
  // never had one and causes the caller to proceed while the sub-screen is still open.
  const editorClosed = () => {
    if (okBtnWrapper) {
      return !document.contains(okBtnWrapper);
    }
    // Sub-screen with toolbar Save (no btnok): closed when it loses screen-active
    return !editorScreen.classList.contains('screen-active');
  };

  if (okBtnInner) {
    okBtnInner.click();
  } else {
    // Row editor is a sub-screen using a toolbar Save button instead of btnok.
    // Try the toolbar Save, then fall back to F10, then Enter.
    const editorSaveBtn = (editorScreen.querySelector(
      '#ToolbarButton\\.Save'
    ) ??
      editorScreen.querySelector(
        '.toolbar-button[aria-label="Save"]'
      )) as HTMLElement | null;
    if (editorSaveBtn) {
      editorSaveBtn.click();
    } else {
      editorScreen.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'F10',
          keyCode: 121,
          bubbles: true,
          cancelable: true
        })
      );
    }
  }

  for (let i = 0; i < 60; i++) {
    await new Promise((r) => setTimeout(r, 50));
    if (editorClosed()) break;
  }

  // Final fallback: if still open, try Enter
  if (!editorClosed()) {
    editorScreen.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Enter',
        keyCode: 13,
        bubbles: true,
        cancelable: true
      })
    );
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 50));
      if (editorClosed()) break;
    }
  }

  await new Promise((r) => setTimeout(r, 150));
}

/** Build the AI template row from a scanned fieldSpecMap (no existing values — stripped by stripForFlexPP). */
function buildObjExamTemplateRow(fieldSpecMap: Map<string, ObjExamFieldSpec>): Record<string, any> {
  const templateRow: Record<string, any> = {};
  for (const [_flatLabel, spec] of Array.from(fieldSpecMap.entries())) {
    const nestPath = objExamNestPath(spec);
    if (spec.type === 'listbox') {
      const LB_PRE_EXPAND = 5;
      const lbHeaders = spec.listboxHeaders || [];
      const protoRow: Record<string, any> = {};
      lbHeaders.forEach((h, i) => {
        if (i === 0) {
          protoRow[h] = {
            _existingValue: null,
            ...(spec.choices && spec.choices.length > 0 ? { _choices: spec.choices } : {}),
            _description: 'Only include rows for items that were actually discussed or examined in this session. Leave unused slots empty.'
          };
        } else {
          protoRow[h] = '';
        }
      });
      const expandedLb: Record<string, any> = {};
      for (let idx = 0; idx < LB_PRE_EXPAND; idx++) {
        const slot = JSON.parse(JSON.stringify(protoRow));
        if (idx > 0) {
          lbHeaders.forEach((h, i) => {
            if (i === 0 && slot[h] && typeof slot[h] === 'object') delete slot[h]['_choices'];
          });
        }
        expandedLb[String(idx)] = slot;
      }
      setNestedValue(templateRow, nestPath, expandedLb);
      continue;
    }
    // Goal / Last never reach here — skipObjExamColumn() filters them out at scan time — so
    // the only degree column present is whichever one this note type writes.
    const isRomDegree = spec.type === 'input' && /\b(AROM|PROM|FPROM)\b/.test(spec.panelCaption) &&
      (spec.column === 'Initial' || spec.column === 'Current');
    const isRomComments = spec.type === 'input' && /\b(AROM|PROM|FPROM)\b/.test(spec.panelCaption) && spec.column === 'Comments';
    let leafDescription: string | undefined;
    if (isRomDegree) {
      // Three observed failure modes drove the wording below, in order:
      //  1. PROM mirrored AROM's degrees verbatim (identical structure invites filling both).
      //  2. With copying banned, one measurement PAIR got SPLIT across AROM and PROM.
      //  3. The split was anchored on the phrase "passive range of motion" appearing in the
      //     TREATMENT list ("soft tissue massage, patella mobs, passive range of motion, …") —
      //     an intervention, not a measurement. Keyword presence is therefore the wrong signal;
      //     what matters is who moved the limb when the number was obtained.
      leafDescription =
        `Degrees only, e.g. "100" — never in Comments. ` +
        `Blank unless a degree number was stated for this exact motion/side/type; blank is normal. ` +
        `AROM = patient moved it, or unstated (default). PROM only if the clinician moved the limb ` +
        `— "passive range of motion" in a treatment list is not a measurement. ` +
        `Motions measured together on one side share ONE type: never mirror or split values ` +
        `between AROM and PROM. Never estimate or carry forward.`;
    } else if (isRomComments) {
      leafDescription =
        'Brief clinical finding for this exact motion/side only (e.g. pain at end range, ' +
        'stiffness, guarding). No degrees, narration, or how it was measured. ' +
        'Never invent filler to justify a value — "assessed to tolerance", "within normal ' +
        'limits", "no pain reported" only if actually said. Absence of a finding is not a ' +
        'finding. Otherwise return None.';
    }
    const leaf: Record<string, any> = {
      _existingValue: null,
      ...(spec.choices && !isRomDegree ? { _choices: spec.choices } : {}),
      ...(leafDescription ? { _description: leafDescription } : {})
    };
    setNestedValue(templateRow, nestPath, leaf);
  }
  return templateRow;
}

// Objective Exam forms are now scanned only when already open (user-initiated).
// buildObjViewField is kept for the call-site signature but does nothing.
async function buildObjViewField(
  listbox: Element,
  _container: Element,
  _label: string,
  _fields: Record<string, any>
): Promise<boolean> {
  return false;
}

// ─── Utilities ────────────────────────────────────────────────────────────────

/**
 * Find the label for an element: first check label[for=id] within container,
 * then fall back to the provided fallback string.
 */
function resolveLabel(
  el: Element,
  container: Element,
  fallback: string
): string {
  // Returns true if the text looks like a date (e.g. "06-30-26", "6/30/2026")
  function isDateLike(t: string): boolean {
    return /^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/.test(t);
  }

  // Returns true if the label element is decorative/hidden (zero-width, hidden attr, or display:none)
  function isHiddenLabel(lbl: Element): boolean {
    if ((lbl as HTMLElement).hidden) return true;
    const style = (lbl as HTMLElement).style;
    if (style && (style.width === '0px' || style.width === '0' || style.display === 'none')) return true;
    return false;
  }

  // Normalize raw label text: fix Raintree-specific patterns and expand PT abbreviations.
  function normalizeLabelText(t: string): string {
    const dx = t.match(/^Dxcode(\d+)/i);
    if (dx) return `Diagnosis Code ${dx[1]}`;
    const ptAbbrLower: Record<string, string> = {
      'flex': 'Flexion', 'ext': 'Extension', 'abd': 'Abduction',
      'add': 'Adduction', 'er': 'External Rotation', 'ir': 'Internal Rotation',
      'ha': 'Horizontal Adduction', 'habd': 'Horizontal Abduction',
    };
    const abbrMatch = t.match(/^([A-Za-z]+)(\s*\(.*\))?$/);
    if (abbrMatch) {
      const expanded = ptAbbrLower[abbrMatch[1].toLowerCase()];
      if (expanded) return expanded + (abbrMatch[2] || '');
    }
    return t;
  }

  // 1. label[for=id] within container — skip hidden or date-value labels
  const id = el.getAttribute('id');
  if (id) {
    const labels = Array.from(container.querySelectorAll(`label[for="${id}"]`));
    const visibleLabel = labels.find(lbl => {
      if (isHiddenLabel(lbl)) return false;
      const text = lbl.textContent?.trim();
      return text && !isDateLike(text);
    });
    const text = visibleLabel?.textContent?.trim();
    if (text) return normalizeLabelText(text);
  }
  // 2. aria-label on the element itself — skip if it looks like a date
  const aria = el.getAttribute('aria-label')?.trim();
  if (aria && !isDateLike(aria)) return normalizeLabelText(aria);
  // 3. name attribute → humanize (e.g. "diag1" → "Diagnosis 1", "caseName" → "Case Name")
  const name = el.getAttribute('name')?.trim();
  if (name) {
    const diagMatch = name.match(/^diag(\d+)$/i);
    if (diagMatch) return `Diagnosis ${diagMatch[1]}`;
    if (/^moo$/i.test(name)) return 'Subjective';
    // ROM/MMT field pattern [PREFIX]_[column]_[motion] → map column to readable label
    const romColMatch = name.match(/^[A-Z]{2,8}_([A-Za-z]+)_[A-Za-z]+$/);
    if (romColMatch) {
      const colMap: Record<string, string> = { 'init': 'Initial', 'goal': 'Goal', 'comm': 'Comments', 'last': 'Last', 'current': 'Current' };
      const colLabel = colMap[romColMatch[1].toLowerCase()];
      if (colLabel) return colLabel;
    }
    // camelCase → Title Case with spaces
    const humanized = name
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
    if (humanized !== fallback) return humanized;
  }
  // 4. For listboxes: use the first non-checkbox table column header (e.g. "Exercises", "Modalities")
  const firstTh = el.querySelector(
    'thead th:not(.w-listbox-extended-checkbox)'
  );
  const thText = firstTh?.textContent?.trim();
  if (thText) return thText;
  return fallback;
}

/** Find a value in a Record by exact key, then by normalized key fallback */
function fuzzyLookup<T>(
  record: Record<string, T>,
  key: string
): [string, T] | null {
  if (record[key] !== undefined) return [key, record[key]];
  const normalizedKey = normalizeKey(key);
  for (const [k, v] of Object.entries(record)) {
    if (normalizeKey(k) === normalizedKey) return [k, v];
  }
  return null;
}

function isEmptyOrNull(value: any): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase();
    return (
      lower === 'none' ||
      lower === 'null' ||
      lower === 'n/a' ||
      lower === 'not discussed'
    );
  }
  return false;
}

function bubbleFocus(element: Element) {
  element.dispatchEvent(
    new FocusEvent('focusin', { bubbles: true, cancelable: true })
  );
}

async function eventBasedFill(element: HTMLElement, value: string) {
  const id = element.id;
  if (id) {
    // If TinyMCE hasn't initialized yet on this element (no user has clicked it),
    // do the same focus-init polling that fillRowListRow uses before trying setContent.
    let targetEl: HTMLElement | null = element;
    if (targetEl.contentEditable !== 'true') {
      bubbleFocus(targetEl);
      targetEl.focus();
      let waited = 0;
      while (targetEl && targetEl.contentEditable !== 'true' && waited < 3000) {
        await new Promise((r) => setTimeout(r, 200));
        waited += 200;
        targetEl = document.getElementById(id) as HTMLElement | null;
        if (targetEl && waited % 1000 === 0) {
          bubbleFocus(targetEl);
          targetEl.focus();
        }
      }
    }

    // Use TinyMCE's API via main world to update its internal model (innerText bypasses it)
    const reqId = `${id}_${Date.now()}`;
    const result = await new Promise<string>((resolve) => {
      const handler = (e: MessageEvent) => {
        if (
          e.source === window &&
          e.data.action === 'setTinyMCEContent_response' &&
          e.data.reqId === reqId
        ) {
          window.removeEventListener('message', handler);
          resolve(e.data.result);
        }
      };
      window.addEventListener('message', handler);
      window.postMessage(
        { action: 'setTinyMCEContent', id, value, reqId },
        '*'
      );
      setTimeout(() => {
        window.removeEventListener('message', handler);
        resolve('timeout');
      }, 2000);
    });
    if (result === 'success') {
      // Fire blur so Raintree treats this as a committed edit and sends FieldSetContent
      // to the server before any tab switch. Without blur the server never saves the
      // programmatic fill, so tab-remount rehydrates TinyMCE from the old server value.
      await blurTinyMCEEditor(id);
      return;
    }
  }

  // Fallback: direct DOM manipulation
  element.focus();
  element.click();
  element.innerText = value;
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
  element.blur();
  element.dispatchEvent(new Event('blur', { bubbles: true }));
}

// Raintree stores memo field content in Widget.HTML as an XHTML document.
// The OK handler reads Widget.HTML directly from the prop, so mutating it
// is sufficient — no events needed.
function buildRaintreeXHTML(text: string): string {
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const spanStyle =
    " font-size: 10pt; font-family: 'Arial', 'Helvetica', sans-serif;" +
    ' font-style: normal; font-weight: normal; color: #000000;' +
    ' background-color: transparent; text-decoration: none;';
  const pStyle =
    'text-align: left; text-indent: 0px; padding: 0px 0px 0px 0px; margin: 0px 0px 0px 0px;';
  const lines = text.split('\n');
  const spans = lines
    .map((l) => `<span style="${spanStyle}">${esc(l)}</span>`)
    .join('<br />');
  return (
    '<?xml version="1.0" encoding="UTF-8"?>\r\n' +
    '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN"' +
    ' "http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">\r\n' +
    '<html xmlns="http://www.w3.org/1999/xhtml">\r\n' +
    '<head><title>RTF</title>\r\n' +
    '<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />\r\n' +
    '<meta http-equiv="Content-Style-Type" content="text/css" />\r\n' +
    '</head>\r\n' +
    '<body style="margin: 0px 0px 0px 0px; background-color: #ffffff;">\r\n' +
    `<p style="${pStyle}">${spans}</p>\r\n` +
    '</body></html>\r\n'
  );
}

// After programmatically setting a Raintree memo's content, two operations
// that real user typing performs must be replicated for the edit to survive
// the OK click:
//   1. `inst.memo.doSyncNow()` — flushes the periodic sync timer so the diff
//      reaches the server before OK closes the modal.
//   2. `inst.memo.changed = true` + `inst.setMemoChanged()` — marks the modal
//      row dirty so OK's listbox commit picks it up. Without this, OK closes
//      the modal and the listbox cell reverts even though the diff was synced.
// Walks the React fiber tree directly — DOM expandos like __reactFiber are
// readable from content scripts in Chrome (isolated/main worlds share DOM).
function commitRaintreeMemoEdit(element: HTMLElement): boolean {
  // Walk up the DOM to find the nearest ancestor with a React fiber key.
  // The Activities edit modal mounts w-memo-content without a fiber directly
  // on the element; the fiber lives on a parent wrapper instead.
  let fiberEl: HTMLElement | null = element;
  let fiberKey: string | undefined;
  while (fiberEl && fiberEl !== document.body) {
    fiberKey = Object.keys(fiberEl).find(
      (k) =>
        k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance')
    );
    if (fiberKey) break;
    fiberEl = fiberEl.parentElement;
  }
  if (!fiberKey || !fiberEl) {
    // Log the ancestor chain to diagnose why no fiber was found
    const chain: string[] = [];
    let dbgEl: HTMLElement | null = element;
    for (let i = 0; i < 12 && dbgEl && dbgEl !== document.body; i++) {
      chain.push(
        `${dbgEl.tagName}${dbgEl.id ? '#' + dbgEl.id : ''}[${dbgEl.className.slice(0, 40)}]`
      );
      dbgEl = dbgEl.parentElement;
    }
    console.warn(
      '[Raintree] commitRaintreeMemoEdit: no fiber key on element; ancestors:',
      chain.join(' > ')
    );
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let fiber: any = (fiberEl as any)[fiberKey];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let inst: any = null;
  while (fiber) {
    const t = fiber.type;
    const name = (typeof t === 'function' && (t.displayName || t.name)) || null;
    if (name === 'Editor') {
      inst = fiber.stateNode;
      break;
    }
    fiber = fiber.return;
  }
  if (!inst) {
    console.warn('[Raintree] commitRaintreeMemoEdit: no Editor fiber');
    return false;
  }

  let didSync = false;
  let didChanged = false;
  try {
    if (inst.memo && typeof inst.memo.doSyncNow === 'function') {
      inst.memo.doSyncNow();
      didSync = true;
    }
  } catch (e) {
    console.warn('[Raintree] commitRaintreeMemoEdit: doSyncNow threw', e);
  }
  try {
    if (inst.memo) inst.memo.changed = true;
    if (typeof inst.setMemoChanged === 'function') {
      inst.setMemoChanged();
      didChanged = true;
    }
  } catch (e) {
    console.warn('[Raintree] commitRaintreeMemoEdit: setMemoChanged threw', e);
  }
  return didSync && didChanged;
}

function setRaintreeWidgetHTML(element: HTMLElement, html: string): boolean {
  const fiberKey = Object.keys(element).find(
    (k) =>
      k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance')
  );
  if (!fiberKey) return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let fiber = (element as any)[fiberKey];
  while (fiber) {
    const mp = fiber.memoizedProps;
    if (mp?.Widget && 'HTML' in mp.Widget) {
      try {
        mp.Widget.HTML = html;
        return true;
      } catch {
        console.warn(
          '[Raintree] setRaintreeWidgetHTML: Widget.HTML not writable'
        );
        return false;
      }
    }
    fiber = fiber.return;
  }
  console.warn(
    '[Raintree] setRaintreeWidgetHTML: Widget with HTML not found on element'
  );
  return false;
}

// Walk the React fiber tree upward from `element`, looking for useState/
// useReducer state that holds an HTML string matching a memo value. When found,
// dispatch an update so Raintree's OK handler reads the new content from state.
function tryUpdateReactFormState(element: HTMLElement, xhtml: string): string {
  const fiberKey = Object.keys(element).find(
    (k) =>
      k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance')
  );
  if (!fiberKey) return 'no_fiber';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let fiber = (element as any)[fiberKey];
  let depth = 0;
  while (fiber && depth < 40) {
    // Walk the memoizedState hook chain for this fiber
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let ms: any = fiber.memoizedState;
    let hookIdx = 0;
    while (ms) {
      const val = ms.memoizedState;
      // Log any hook state that contains an HTML/xhtml string (for diagnosis)
      if (
        typeof val === 'string' &&
        (val.includes('<?xml') || val.includes('<html') || val.includes('<p>'))
      ) {
        // Try to update this state via the queue dispatcher
        if (ms.queue?.dispatch) {
          try {
            ms.queue.dispatch(xhtml);
            return `dispatched_depth${depth}_hook${hookIdx}`;
          } catch (e) {
            console.warn(
              '[Raintree] tryUpdateReactFormState: dispatch failed',
              e
            );
          }
        }
      }
      // Also check object state with HTML-like fields
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        const keys = Object.keys(val);
        const htmlKey = keys.find(
          (k) =>
            typeof val[k] === 'string' &&
            (val[k].includes('<') ||
              k.toLowerCase().includes('html') ||
              k.toLowerCase().includes('content'))
        );
      }
      ms = ms.next;
      hookIdx++;
    }
    // Class component
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inst = fiber.stateNode as any;
    if (inst && typeof inst.setState === 'function' && inst.state) {
      const stateKeys = Object.keys(inst.state);
      const htmlKey = stateKeys.find(
        (k) =>
          typeof inst.state[k] === 'string' && inst.state[k].includes('<?xml')
      );
      if (htmlKey) {
        inst.setState({ [htmlKey]: xhtml });
        return `class_setState_depth${depth}`;
      }
    }
    fiber = fiber.return;
    depth++;
  }
  return 'not_found';
}

async function blurTinyMCEEditor(id: string): Promise<void> {
  await new Promise<void>((resolve) => {
    const reqId = `blur_${id}_${Date.now()}`;
    let timer: ReturnType<typeof setTimeout>;
    const handler = (e: MessageEvent) => {
      if (
        e.source === window &&
        e.data.action === 'blurTinyMCE_response' &&
        e.data.reqId === reqId
      ) {
        clearTimeout(timer);
        window.removeEventListener('message', handler);
        resolve();
      }
    };
    window.addEventListener('message', handler);
    window.postMessage({ action: 'blurTinyMCE', id, reqId }, '*');
    timer = setTimeout(() => {
      console.warn('[Raintree] blurTinyMCEEditor: timeout for', id);
      window.removeEventListener('message', handler);
      resolve();
    }, 500);
  });
}

function nativeInputFill(element: HTMLInputElement, value: string) {
  // Use native value setter to bypass React's cached value — then fire a real input event.
  const nativeSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value'
  )?.set;
  if (nativeSetter) {
    nativeSetter.call(element, value);
  } else {
    element.value = value;
  }
  element.dispatchEvent(new InputEvent('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

// ─── Async Mutex ──────────────────────────────────────────────────────────────

class AsyncMutex {
  private chain: Promise<any>;
  constructor() {
    this.chain = Promise.resolve();
  }
  async runExclusive(task: () => Promise<any>) {
    let release: () => void;
    const willLock = new Promise<void>((resolve) => (release = resolve));
    const previous = this.chain;
    this.chain = previous.then(() => willLock);
    await previous;
    try {
      return await task();
    } finally {
      // @ts-ignore release is always assigned above
      release();
    }
  }
}

const questionMutex = new AsyncMutex();

// ─── Answer Functions ─────────────────────────────────────────────────────────

export async function answerQuestions(answers: any[]) {
  answeringInProgress = true;
  // Raintree can raise an advisory "Notice" popup part-way through a table fill; it blocks
  // further input until acknowledged, so watch for it and click OK for the whole pass.
  startNoticeWatcher();
  const errors = [];
  try {
    for (const answer of answers) {
      const error = await answerQuestion(
        answer['Question'],
        answer['Answer'],
        answer['Reply Type']
      );
      if (error) {
        errors.push({
          Question: answer['Question'],
          Answer: answer['Answer'],
          Error: error
        });
      }
    }
  } finally {
    answeringInProgress = false;
    stopNoticeWatcher();
    // One last sweep: a notice raised by the final write may land after the loop exits.
    await dismissNoticeDialog(500);
  }
  console.error('Errors:', errors);
  return errors;
}

export async function answerQuestion(
  question: string,
  answer: any,
  replyType = 'overwrite'
) {
  if (!questionInteractions[question]) {
    // The panel rewrites a never-seen section's Question with the instruction
    // it just generated before pushing the answer (ConversationDetailsCX's
    // elementsToProcess loop), so the lookup key no longer matches the scan.
    // Recover by section key, then case-insensitively; with an exam form open
    // the scan holds exactly one section, so there is only one place to go.
    const keys = Object.keys(questionInteractions);
    const wanted = String(question).trim().toLowerCase();
    const resolved =
      keys.find((k) => questionInteractions[k]?.Key === question) ??
      keys.find((k) => k.trim().toLowerCase() === wanted) ??
      (keys.length === 1 ? keys[0] : undefined);
    if (resolved) {
      console.warn(`[Raintree] answerQuestion: "${String(question).slice(0, 60)}" is not a scanned section — using "${resolved}"`);
      question = resolved;
    }
  }
  if (!questionInteractions[question]) {
    console.error('Question not found:', question);
    return 'Question not found';
  }

  // Block getQuestions() from switching tabs while any answer is in flight.
  // Use a ref-count so concurrent PUSH_ANSWER calls keep the flag true until all finish.
  answeringInProgress = true;
  __answeringRefCount++;
  // Raintree can raise an advisory "Notice" popup part-way through a fill; it blocks further
  // input until acknowledged. Watch from here (not just answerQuestions) because the sidebar's
  // per-section refresh buttons call answerQuestion directly — that path had no watcher at all.
  startNoticeWatcher();

  beginOverlay('Answering questions…', { title: 'ComprehendPT' });
  pushQuestionToQueue(String(question));

  if (questionInteractions[question].Type === 'Flex') {
    try {
      const flexErrors = await answerFlexQuestion(question, answer, replyType);
      applyAnswerToQuestionCache(question, answer, replyType, flexErrors);
      return true;
    } finally {
      removeQuestionFromQueue(String(question));
      endOverlay();
      __answeringRefCount--;
      if (__answeringRefCount <= 0) {
        __answeringRefCount = 0;
        answeringInProgress = false;
        stopNoticeWatcher();
      }
    }
  }

  // Flat Input fallback (should rarely be reached in the flex architecture)
  if (questionInteractions[question].Input) {
    try {
      await questionInteractions[question].Input(answer, replyType);
      return true;
    } finally {
      removeQuestionFromQueue(String(question));
      endOverlay();
      __answeringRefCount--;
      if (__answeringRefCount <= 0) {
        __answeringRefCount = 0;
        answeringInProgress = false;
        stopNoticeWatcher();
      }
    }
  }

  __answeringRefCount--;
  if (__answeringRefCount <= 0) {
    __answeringRefCount = 0;
    answeringInProgress = false;
    stopNoticeWatcher();
  }
  removeQuestionFromQueue(String(question));
  endOverlay();
  return 'Unhandled question type';
}

function applyAnswerToQuestionCache(
  question: string,
  answer: any,
  replyType: string,
  flexErrors: any
) {
  try {
    // See note above — Objective's existing values are stripped on purpose.
    if (question === 'Objective') return;
    if (!answer || typeof answer !== 'object') return;

    const entry = (cachedQuestions as any[] | null)?.find(
      (q: any) => q?.Key === question
    );
    const structure = entry?.Structure;
    if (!structure || typeof structure !== 'object') return;

    // answerFlexQuestion reports per-field failures; don't record those as written.
    const failed = new Set(
      (Array.isArray(flexErrors) ? flexErrors : [])
        .map((e: any) => e?.Field)
        .filter(Boolean)
    );

    // A plain flex++ leaf only ever carries _existingValue / _choices / _description.
    // Anything else is a pass-through Structure we must not touch.
    const isFlexLeaf = (leaf: any) =>
      leaf &&
      typeof leaf === 'object' &&
      !Array.isArray(leaf) &&
      Object.keys(leaf).every((k) => k.startsWith('_'));

    for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
      if (failed.has(fieldKey)) continue;
      const subsectionMatch = fuzzyLookup(
        structure as Record<string, any>,
        fieldKey
      );
      if (!subsectionMatch) continue;
      const subsectionKey = subsectionMatch[0];
      const subsection = subsectionMatch[1] as Record<string, any>;
      if (!subsection || typeof subsection !== 'object') continue;
      if (Array.isArray(subsection)) continue;

      // Mirror the fill path's re-wrap of a flattened single-field subsection.
      const isDirectAnswer =
        Array.isArray(fieldDetails) ||
        typeof fieldDetails !== 'object' ||
        fieldDetails === null;
      const subFields: Record<string, unknown> = isDirectAnswer
        ? { [subsectionKey]: fieldDetails }
        : (fieldDetails as Record<string, unknown>);

      for (const [subFieldKey, value] of Object.entries(subFields)) {
        if (failed.has(`${fieldKey}.${subFieldKey}`)) continue;
        if (typeof value !== 'string' || isEmptyOrNull(value)) continue;
        const leaf =
          isFlexLeaf(subsection) &&
          normalizeKey(subFieldKey) === normalizeKey(subsectionKey)
            ? subsection
            : (fuzzyLookup(subsection, subFieldKey)?.[1] as any);
        if (!isFlexLeaf(leaf)) continue;
        if (
          typeof leaf._description === 'string' &&
          leaf._description.includes("'$'")
        ) {
          continue;
        }
        const previous = leaf._existingValue;
        leaf._existingValue =
          replyType === 'append' && previous
            ? `${previous}\n${value.trim()}`
            : value.trim();
      }
    }
  } catch (error) {
    // Never let cache bookkeeping break an answer that already landed in the note.
    console.error('Error updating question cache after answer:', error);
  }
}

async function answerFlexQuestion(
  question: string,
  answer: any,
  replyType = 'overwrite'
) {
  return questionMutex.runExclusive(async () => {
    _memoAppendBase.clear();
    _memoAppendedVals.clear();
    try {
      const errors = [];
      // Wait for any in-progress getQuestions scan — it wipes questionInteractions mid-flight
      while (getQuestionsInProgress) {
        await new Promise((r) => setTimeout(r, 100));
      }
      const tabName = questionInteractions[question]?.tabName;

      // Switch to the correct tab before filling so elements are interactive
      const serviceTicketTab = document.querySelector(
        '[data-widgetname="servicetickettab"]'
      );
      if (tabName && serviceTicketTab) {
        const tabEl = serviceTicketTab.querySelector(
          `[data-name="${tabName}"]`
        ) as HTMLElement | null;
        if (tabEl && !tabEl.classList.contains('active')) {
          tabEl.click();
          await new Promise((r) => setTimeout(r, 600));
          await dismissAbandonChanges();
        }
      }

      // Raintree re-renders tab DOM on every tab switch — rebuild structure with fresh element refs.
      // Re-register qi each iteration in case getQuestions() wiped questionInteractions mid-flight.
      // Poll until panels appear (up to 2s) to handle lazy-loading tabs.
      //
      // IMPORTANT: do NOT use serviceTicketTab.querySelector('#tabcontent') here.
      // Some tabs (e.g. Assessment) contain a nested widget that has its own inner
      // #tabcontent. The simple querySelector finds the inner one and builds structure
      // from the wrong sub-widget. Instead walk up from the active tab element to the
      // enclosing .w-tab-control and take its direct .w-tab-control-content child —
      // exactly the same approach used by getQuestions (see scan code ~line 370).
      const _fillTabEl = tabName
        ? (serviceTicketTab?.querySelector(
            `.tab-item[data-name="${tabName}"]`
          ) as HTMLElement | null)
        : null;
      const _fillSoapCtrl = _fillTabEl?.closest('.w-tab-control');
      const tabContent = _fillSoapCtrl
        ? (Array.from(_fillSoapCtrl.children).find((el) =>
            el.classList.contains('w-tab-control-content')
          ) as Element | null)
        : null;
      let freshStructure: Record<string, any> = {};
      if (tabContent) {
        let attempts = 0;
        while (attempts < 8) {
          // Re-click our tab — getQuestions() may have switched away during our await
          if (tabName) {
            const tabEl = serviceTicketTab?.querySelector(
              `[data-name="${tabName}"]`
            ) as HTMLElement | null;
            if (tabEl && !tabEl.classList.contains('active')) {
              tabEl.click();
              await new Promise((r) => setTimeout(r, 400));
              await dismissAbandonChanges();
            }
          }

          freshStructure = {};
          questionInteractions[question] = {
            Type: 'Flex',
            Key: question,
            Question: question,
            tabName,
            Structure: freshStructure
          };
          // If this section has Phase 2 sub-tab content registered, we must run
          // Phase 2 during the rebuild — skipping it leaves the structure empty for
          // those subsections and the field-by-field re-add in the loop below fails.
          // Sections with only Phase 1 content (Subjective, Plan, etc.) still skip
          // Phase 2 for speed.
          const _needsPhase2 = Array.from(_subTabRegistry.keys()).some((k) =>
            k.startsWith(`${question}::`)
          );
          await buildAllPanels(tabContent, question, !_needsPhase2);

          // Verify we got the right tab's content before accepting the structure
          const activeTabName = (
            serviceTicketTab?.querySelector(
              '.tab-item.active'
            ) as HTMLElement | null
          )?.dataset.name;
          if (
            activeTabName === tabName &&
            Object.keys(freshStructure).length > 0
          )
            break;

          await new Promise((r) => setTimeout(r, 300));
          attempts++;
        }
        // Wait for TinyMCE to initialize after bubbleFocus was dispatched during buildPanelStructure
        await new Promise((r) => setTimeout(r, 2000));
      }

      const structure = freshStructure;
      const rebuiltSubTabs = new Set<string>();

      for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
        // If this subsection is from a nested sub-tab (Phase 2), skipPhase2=true means it wasn't
        // rebuilt above. Navigate to the sub-tab now and rebuild just that part with fresh refs.
        const registryKey =
          Array.from(_subTabRegistry.keys()).find((k) => {
            const [rQuestion, rSection] = k.split('::');
            return (
              rQuestion === question &&
              normalizeKey(rSection) === normalizeKey(fieldKey)
            );
          }) ?? null;
        if (registryKey) {
          const entry = _subTabRegistry.get(registryKey)!;
          const dedupKey = `${entry.widgetName}:${entry.tabName}`;
          if (!rebuiltSubTabs.has(dedupKey) && tabContent) {
            rebuiltSubTabs.add(dedupKey);
            const widgetEl = tabContent.querySelector(
              `[data-widgetname="${entry.widgetName}"]`
            );
            if (widgetEl) {
              const ctrl = widgetEl.querySelector('.w-tab-control');
              const tabItem = (
                Array.from(
                  ctrl?.querySelectorAll('.w-tab-control-tabs .tab-item') ?? []
                ) as HTMLElement[]
              ).find(
                (t) =>
                  (t.getAttribute('aria-label')?.trim() ||
                    t.querySelector('span')?.textContent?.trim()) ===
                  entry.tabName
              );
              if (tabItem && !tabItem.classList.contains('active')) {
                tabItem.click();
                await new Promise((r) => setTimeout(r, 400));
              }
              // Re-query after tab click
              const freshCtrl = widgetEl.querySelector('.w-tab-control');
              const freshTabContent = freshCtrl?.querySelector(
                '.w-tab-control-content'
              ) as HTMLElement | null;
              const panelsInTab = freshTabContent
                ? Array.from(freshTabContent.querySelectorAll('.w-panel'))
                : [];
              if (freshTabContent) {
                for (const panel of panelsInTab) {
                  const pName = panelCaptionName(panel);
                  if (!pName) continue;
                  await buildPanelStructure(panel, question);
                }
                await buildPanelStructure(
                  freshTabContent,
                  question,
                  entry.tabName
                );
              }
            }
          }
        }

        const subsectionMatch = fuzzyLookup(
          structure as Record<string, any>,
          fieldKey
        );
        if (!subsectionMatch) {
          console.warn(
            `[Raintree] Subsection not found: "${fieldKey}". Available:`,
            Object.keys(structure)
          );
          errors.push({
            Field: fieldKey,
            Error: 'Subsection not found in Structure'
          });
          continue;
        }
        const subsectionKey = subsectionMatch[0];
        const subsection = subsectionMatch[1] as Record<string, any>;

        // Flattened single-field subsection: the AI answered with a direct value (array or
        // primitive) instead of { subFieldKey: value }. Re-wrap it so the field lookup below
        // finds the right Input function — the internal structure still has the full nesting.
        // Indexed dict {"0": {...}, "1": {...}} → array: row-list fields sent as nested lists
        // are converted to indexed dict by from_comprehend_to_openai; convert back here.
        let normalizedFieldDetails: unknown = fieldDetails;
        if (
          !Array.isArray(fieldDetails) &&
          typeof fieldDetails === 'object' &&
          fieldDetails !== null &&
          Object.keys(fieldDetails as object).length > 0 &&
          Object.keys(fieldDetails as object).every((k) => /^\d+$/.test(k))
        ) {
          normalizedFieldDetails = Object.keys(
            fieldDetails as Record<string, unknown>
          )
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map((k) => (fieldDetails as Record<string, unknown>)[k]);
        }

        const isDirectAnswer =
          Array.isArray(normalizedFieldDetails) ||
          typeof normalizedFieldDetails !== 'object' ||
          normalizedFieldDetails === null;
        const subFields: Record<string, unknown> = isDirectAnswer
          ? { [subsectionKey]: normalizedFieldDetails }
          : (normalizedFieldDetails as Record<string, unknown>);

        for (const [subFieldKey, value] of Object.entries(subFields)) {
          const fieldMatch = fuzzyLookup(subsection, subFieldKey);
          const field = fieldMatch?.[1] as any;
          if (!field?.Input) {
            console.warn(
              `[Raintree] Input not found: "${fieldKey}.${subFieldKey}". Available:`,
              Object.keys(subsection)
            );
            errors.push({
              Field: `${fieldKey}.${subFieldKey}`,
              Error: 'Input function not found'
            });
            continue;
          }

          // Row-list fields (Goals, Chief Complaints): AI responds with an array of row objects
          // OR { rows: [...] }. Queue each row individually for the batch flush.
          const rowArray: any[] | null = Array.isArray(value)
            ? (value as any[])
            : Array.isArray((value as any)?.rows)
              ? (value as any).rows
              : null;
          if (rowArray !== null) {
            for (const rowObj of rowArray) {
              try {
                await field.Input(rowObj, replyType);
              } catch (err) {
                console.error(
                  `[Raintree] Error filling row "${fieldKey}.${subFieldKey}":`,
                  err
                );
                errors.push({
                  Field: `${fieldKey}.${subFieldKey}`,
                  Error: `Error applying row: ${String(err)}`
                });
              }
            }
            continue;
          }

          // Only split on '$' for multi-select fields (pickers, checkbox listboxes).
          // Memo/text fields use the whole string — splitting would overwrite on each call.
          const isMultiSelect =
            typeof field.Description === 'string' &&
            field.Description.includes("'$'");
          const values =
            isMultiSelect && typeof value === 'string'
              ? value
                  .split('$')
                  .map((v: string) => v.trim())
                  .filter(Boolean)
              : [value];

          for (const val of values) {
            try {
              await field.Input(val, replyType);
            } catch (err) {
              console.error(
                `[Raintree] Error filling "${fieldKey}.${subFieldKey}":`,
                err
              );
              errors.push({
                Field: `${fieldKey}.${subFieldKey}`,
                Error: `Error applying value: ${String(err)}`
              });
            }
          }
        }
      }

      // Flush any batched picker selections (accumulated across all Input calls above)
      for (const [key, flush] of Array.from(_pickerBatchFlushers.entries())) {
        const pending = _pickerBatch.get(key)?.length ?? 0;
        if (pending > 0) {
          await flush();
        }
      }

      if (errors.length > 0) console.error('[Raintree] Fill errors:', errors);
      return errors.length > 0 ? errors : null;
    } catch (err) {
      console.error('Unexpected error in answerFlexQuestion:', err);
      return { Question: question, Error: `Unexpected error: ${String(err)}` };
    }
  });
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent }
    });
    return { htmlContent };
  } catch (error) {
    console.error('Error in getLogFromInjector:', error);
    getLogFromInjector();
  }
}

// ─── Blur Overlay ─────────────────────────────────────────────────────────────

const OVERLAY_ID = 'BlurOverlay';
const ACTIVE_CLASS = 'blur-overlay-active';
const CSS_ID = 'BlurOverlayStyles';

let overlayEl: HTMLElement | null = null;
let msgEl: HTMLElement | null = null;
let closeBtn: HTMLElement | null = null;
let lastFocused: HTMLElement | null = null;
let _overlayAutoCloseTimer: ReturnType<typeof setTimeout> | null = null;
const OVERLAY_AUTO_CLOSE_MS = 360_000; // 6-minute safety net

function injectStyles() {
  if (document.getElementById(CSS_ID)) return;
  const style = document.createElement('style');
  style.id = CSS_ID;
  style.textContent = `
       html.${ACTIVE_CLASS} { overflow: hidden; }
       body.${ACTIVE_CLASS} { overflow: hidden; }
  
       html.${ACTIVE_CLASS} body > *:not(#${OVERLAY_ID}) {
         filter: blur(var(--blur-overlay-amount, 8px)) saturate(0.9);
         pointer-events: none;
         user-select: none;
       }
  
       #${OVERLAY_ID} {
         position: fixed;
         inset: 0;
         --brand-color: rgb(107, 216, 136);
         z-index: 2147483647;
         display: none;
         align-items: center;
         justify-content: center;
         padding: 24px;
       }
  
       #${OVERLAY_ID}[data-open="true"] { display: flex; }
  
       #${OVERLAY_ID}::before {
         content: "";
         position: absolute;
         inset: 0;
         background: rgba(0,0,0,0.25);
         backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
         -webkit-backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
       }
  
       #${OVERLAY_ID} .blur-msg {
         position: relative;
         max-width: min(720px, 90vw);
         width: max(320px, 40vw);
         background: rgba(255,255,255,0.9);
         border-radius: 16px;
         box-shadow: 0 10px 30px rgba(0,0,0,0.2);
         padding: 20px 24px;
         font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
         color: #111;
         backdrop-filter: blur(2px);
       }
  
       #${OVERLAY_ID} .blur-msg h2 {
         margin: 0 0 10px 0;
         font-size: 18px;
         font-weight: 600;
         color: var(--brand-color);
       }
  
       #${OVERLAY_ID} .blur-msg .blur-text {
         line-height: 1.5;
         font-size: 15px;
         white-space: pre-wrap;
       }
  
       #${OVERLAY_ID} .blur-close {
         position: fixed;
         top: 12px;
         right: 12px;
         appearance: none;
         border: none;
         background: var(--brand-color);
         color: #fff;
         width: 36px;
         height: 36px;
         border-radius: 10px;
         font-size: 20px;
         line-height: 36px;
         text-align: center;
         cursor: pointer;
         box-shadow: 0 4px 12px rgba(0,0,0,0.25);
       }
       #${OVERLAY_ID} .blur-close:hover { background: #00937f; }
       #${OVERLAY_ID} .blur-close:focus { outline: 2px solid #fff; outline-offset: 2px; }
     `;
  document.head.appendChild(style);
}

function createOverlay() {
  if (document.getElementById(OVERLAY_ID)) {
    overlayEl = document.getElementById(OVERLAY_ID);
    msgEl = overlayEl.querySelector('.blur-text');
    closeBtn = overlayEl.querySelector('.blur-close');
    return;
  }
  overlayEl = document.createElement('div');
  overlayEl.id = OVERLAY_ID;
  overlayEl.setAttribute('aria-hidden', 'true');

  const card = document.createElement('div');
  card.className = 'blur-msg';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-live', 'polite');

  const title = document.createElement('h2');
  title.textContent = 'Notice';

  msgEl = document.createElement('div');
  msgEl.className = 'blur-text';
  msgEl.textContent = '';

  closeBtn = document.createElement('button');
  closeBtn.className = 'blur-close';
  closeBtn.setAttribute('type', 'button');
  closeBtn.setAttribute('aria-label', 'Close overlay');
  closeBtn.textContent = '×';

  card.appendChild(title);
  card.appendChild(msgEl);
  overlayEl.appendChild(card);
  overlayEl.appendChild(closeBtn);
  document.body.appendChild(overlayEl);

  closeBtn.addEventListener('click', hide);
  document.addEventListener('keydown', (e) => {
    if (overlayEl?.dataset.open === 'true' && e.key === 'Escape') hide();
  });
}

function show(message = '', opts: any = {}) {
  injectStyles();
  createOverlay();

  const {
    title = 'Notice',
    blurAmount = 8,
    textColor,
    background,
    width
  } = opts;

  overlayEl.style.setProperty('--blur-overlay-amount', String(blurAmount));
  const card = overlayEl.querySelector('.blur-msg') as HTMLElement;
  if (textColor) card.style.color = textColor;
  if (background) card.style.background = background;
  if (width) card.style.width = width;

  (overlayEl.querySelector('h2') as HTMLElement).textContent = title;
  msgEl.textContent = message ?? '';

  document.documentElement.classList.add(ACTIVE_CLASS);
  document.body.classList.add(ACTIVE_CLASS);

  closeBtn.style.display = '';
  overlayEl.dataset.open = 'true';
  overlayEl.setAttribute('aria-hidden', 'false');

  lastFocused =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  (closeBtn as HTMLElement).focus();

  // Safety net: force-close if the overlay is somehow still open after 4 minutes.
  if (_overlayAutoCloseTimer) clearTimeout(_overlayAutoCloseTimer);
  _overlayAutoCloseTimer = setTimeout(() => {
    _overlayAutoCloseTimer = null;
    if (isVisible()) {
      __overlayRefCount = 0;
      hide();
    }
  }, OVERLAY_AUTO_CLOSE_MS);
}

function update(message = '') {
  if (!overlayEl) createOverlay();
  msgEl.textContent = message ?? '';
}

function hide() {
  if (!overlayEl) return;
  overlayEl.dataset.open = 'false';
  overlayEl.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove(ACTIVE_CLASS);
  document.body.classList.remove(ACTIVE_CLASS);
  if (_overlayAutoCloseTimer) {
    clearTimeout(_overlayAutoCloseTimer);
    _overlayAutoCloseTimer = null;
  }
  if (lastFocused && document.contains(lastFocused)) {
    try {
      lastFocused.focus();
    } catch {}
  }
  lastFocused = null;
}

function isVisible() {
  return !!(overlayEl && overlayEl.dataset.open === 'true');
}

// ─── Overlay ref-count helpers ────────────────────────────────────────────────

let __overlayRefCount = 0;

function beginOverlay(message: string, opts?: any) {
  __overlayRefCount++;
  if (!isVisible()) {
    show(message, opts);
  } else {
    update(message);
  }
}

function endOverlay() {
  __overlayRefCount = Math.max(0, __overlayRefCount - 1);
  if (__overlayRefCount === 0 && isVisible()) {
    hide();
  }
}

// ─── Queue tracking ───────────────────────────────────────────────────────────

let __overlayAnswerQueue: string[] = [];

function formatAnswerQueueMessage() {
  const header = 'Finishing your note… This should only take a few seconds.';
  if (__overlayAnswerQueue.length === 0) return header;
  const current = __overlayAnswerQueue[0];
  const nextItems = __overlayAnswerQueue.slice(1);
  const nextPreview = nextItems.slice(0, 3);
  const remaining = Math.max(0, nextItems.length - nextPreview.length);
  let message = header + `\nCurrently working on: ${current}`;
  if (nextPreview.length > 0) {
    message +=
      `\nUp next: ${nextPreview.join(', ')}` +
      (remaining > 0 ? `, and ${remaining} more` : '');
  }
  return message;
}

function pushQuestionToQueue(questionLabel: string) {
  if (!questionLabel) return;
  if (!__overlayAnswerQueue.includes(questionLabel)) {
    __overlayAnswerQueue.push(questionLabel);
  }
  if (isVisible()) update(formatAnswerQueueMessage());
}

function removeQuestionFromQueue(questionLabel: string) {
  const idx = __overlayAnswerQueue.indexOf(questionLabel);
  if (idx !== -1) __overlayAnswerQueue.splice(idx, 1);
  if (isVisible()) update(formatAnswerQueueMessage());
  // Refresh the getQuestions cache TTL as each answer finishes, so a scan triggered right after
  // answering serves the cache instead of re-walking every tab (which could clobber fresh fills).
  lastGetQuestionsAt = Date.now();
}
